// A Web Audio player with a sampled jazz guitar and the original plucked
// synth option: SIX independent monophonic strings, plus a
// dedicated pizzicato double-bass voice (slot 6) for the walking bass line,
// plus a struck-piano voice (per-pitch polyphonic, see buildPianoVoice).
//
// The original guitar voice is an additive pluck (fundamental + two quieter, faster-
// decaying partials) shaped by a low-pass filter that closes over the note and
// a fast-attack / exponential-release envelope, summed through a master limiter.
//
// Per-string monophony: each string holds at most one ringing voice. Plucking a
// string damps whatever that same string was playing (a fast release) but leaves
// the other strings ringing — so a new strum/arpeggio interrupts each string only
// as it reaches it. A "sequence" (Play / Play progression) is a single
// cancellable scheduler, so pressing it again restarts rather than layering.

import { activeStringsForFingering, frettedMidi, type StringSet } from '@/music/tuning';
import { buildReverbImpulse, reverbSendGain } from './reverb';
import { masterCompressionSettings, normalizeCompressionAmount } from './compression';
import { loopingPassPosition, loopStartPassIndex } from './playbackPasses';
import {
  levelGain,
  normalizeLevels,
  type ChannelLevels,
  type MixChannel,
  UNITY_LEVELS,
} from './levels';
import { arrangementSpanBeats } from '@/music/timing';
import { bandCueAtBeat, type BandCueInterval } from '@/music/score/bandCues';
import type { CompHit, CompStyle } from '@/music/comping';
import { HOLD_STRETCH } from '@/audio/holds';
import { ritardandoStretch } from '@/audio/endings';
import {
  melodyDownbeatDelay,
  partShift,
  timingDrift,
  velocityDrift,
  type FeelPlacement,
  type FeelTiming,
} from '@/audio/feel';
import {
  DEFAULT_SWING_FEEL,
  KIT_FEEL,
  kitGrid,
  kitSwingRatio,
  straightPairsOf,
  swingGrid,
  swingPosition,
  type SwingFeel,
  type SwingGrid,
  type SwingOffsets,
  type SwingPart,
} from '@/music/swingFeel';
import {
  measureLoopSwing,
  measuredLoopSwing,
  monoSamples,
  rememberLoopSwing,
} from '@/audio/loopSwing';
import {
  clickFor,
  DEFAULT_METRONOME,
  gapClickSounds,
  normalizeMetronome,
  subdivisionOffsets,
  type ClickKind,
  type MetronomeSettings,
} from '@/audio/metronome';
import { DrumStretchRenderer } from '@/audio/drumStretchRenderer';
import { LayeredSampler, type LayeredSample } from '@/audio/layeredSampler';
import type { Fingering } from '@/music/voicing';
import {
  INSTRUMENT_SAMPLE_DEFINITIONS,
  getInstrumentSamples,
  loadInstrumentSamples,
  selectInstrumentSample,
  type BassSound,
  type GuitarSound,
  type LeadSound,
  type SampledInstrumentSound,
} from './instrumentSamples';

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** The same shape a fret up or down (chromatic approach stab); no-op if it
 * would fall off the nut. */
function shiftFrets(fingering: Fingering, shift: number): Fingering {
  if (fingering.frets.some((f) => f !== null && f !== undefined && f + shift < 0)) return fingering;
  return {
    ...fingering,
    frets: fingering.frets.map((f) => (f === null || f === undefined ? f : f + shift)),
  };
}

const PARTIALS: { ratio: number; gain: number; type: OscillatorType }[] = [
  { ratio: 1, gain: 1.0, type: 'triangle' },
  { ratio: 2, gain: 0.32, type: 'sine' },
  { ratio: 3, gain: 0.12, type: 'sine' },
];

// Pizzicato double bass: a strong round fundamental with a quick-dying octave
// and a faint upper edge that vanishes almost immediately (so the tone darkens
// to a warm hum). `decay` scales each partial's ring relative to the note; the
// upper partials die fastest, leaving mostly fundamental — the upright's woody
// sustain. Paired with an attack noise-thump (finger + fingerboard) in
// buildBassVoice, this reads as plucked acoustic bass rather than a synth.
const BASS_PARTIALS: { ratio: number; gain: number; type: OscillatorType; decay: number }[] = [
  { ratio: 1, gain: 1.0, type: 'sine', decay: 1.0 },
  { ratio: 2, gain: 0.5, type: 'sine', decay: 0.42 },
  { ratio: 3, gain: 0.16, type: 'triangle', decay: 0.26 },
  { ratio: 4, gain: 0.07, type: 'sine', decay: 0.18 },
];

// Default note ring-out (seconds). Real guitar strings sustain for several
// seconds; this is the decay length of a plucked string.
const DEFAULT_SUSTAIN = 3.0;

// Struck piano tone. Two things separate a piano from every plucked voice in
// this file, and both are audible rather than cosmetic:
//
//   INHARMONICITY — piano strings are stiff, so partial n sounds sharp of
//   n·f0 by a factor √(1 + B·n²). That stretch is THE piano tell; perfectly
//   harmonic partials read as an organ or a plucked string. B here is a
//   per-register constant (stiffer-sounding low strings get more).
//
//   THE DOUBLE DECAY — a struck note's "prompt sound" falls fast (the hammer
//   energy), then an "aftersound" rings on much longer at lower level. One
//   exponential sounds like a synth pad wearing a piano attack.
//
// Plus the unison detune (a real note is 2-3 strings tuned within a couple
// of cents, whose slow beat animates the sustain — the fundamental gets one
// detuned twin) and a brief hammer-felt thump of bandpassed noise.
const PIANO_INHARMONICITY = 0.00035;

// Timed playback is filled ahead on the AudioContext clock. The timer merely
// wakes the producer; once a node has been given an absolute audio time,
// main-thread jitter cannot move its onset. A genuinely late producer is
// re-anchored instead of replaying missed beats/chords in a burst. Arrangement
// and Progressions playback deliberately share these timing guarantees.
const AUDIO_LOOKAHEAD_SECONDS = 0.25;
// A HIDDEN page's timers are throttled — to ~1s on iOS under a live audio
// session and in a background desktop tab — so a 250ms fill starves the moment
// the screen locks. Each wake fills several seconds instead, and the page also
// refills the instant it hides (see the visibility listener) rather than
// waiting out the first throttled tick. Pause/stop already rewind and damp a
// deep lookahead (pauseTransport), so nothing else changes shape.
const AUDIO_HIDDEN_LOOKAHEAD_SECONDS = 4;
const AUDIO_SCHEDULER_TICK_MS = 25;
const AUDIO_START_LEAD_SECONDS = 0.1;
// Keep a full 60ms margin before accepting an unscheduled beat.
const AUDIO_MIN_LEAD_SECONDS = 0.06;

// 50ms of 8-bit silence. Looping this through an <audio> element promotes the
// page from iOS's "ambient" audio session (muted by the ringer switch) to a
// "playback" session, so Web Audio actually sounds on a silenced iPhone.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRrQBAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA';

interface Voice {
  channel: Outlet;
  startTime: number;
  /** A written cutoff is final; later damping must never extend it. */
  cutAt?: number;
  amp: GainNode;
  oscillators: OscillatorNode[];
  /** Non-oscillator scheduled sources, such as a bass attack-noise buffer. */
  sources: AudioScheduledSourceNode[];
  nodes: AudioNode[];
  teardown: ReturnType<typeof setTimeout>;
  /** String 0-5 this voice belongs to, or -1 for an untracked (stringless) note. */
  stringIndex: number;
}

export interface SequenceChordInput {
  fingering: Fingering;
  stringSet: StringSet;
}

/**
 * One chord as the player strikes it: a guitar grip, or — when the comp
 * channel is a keyboard — the piano pitches to sound instead (#236). The grip
 * is still carried when piano is playing, because the sheet, the comping
 * generator and the chord-change detection all keep working off it; only what
 * SOUNDS changes.
 */
interface ChordStrike {
  fingering: Fingering;
  stringSet: StringSet;
  pianoMidis?: number[];
  /** A one-hand strike: re-strike only `pianoMidis`, releasing nothing —
   * the hand that is not playing has not moved off its keys. */
  pianoPartial?: boolean;
  /** Explicit per-note roll in seconds, UNCAPPED — the comp engine's
   * expressive touch (tight stabs, gentle pads, the slow upward sweep on a
   * long pad). Absent, the pianist's default 12ms cap applies. */
  pianoRollSec?: number;
}

export interface ArrangementEvent {
  fingering: Fingering;
  stringSet: StringSet;
  /** When the comp channel is a keyboard (#236): the pitches to strike on the
   * piano voice instead of plucking the grip. The grip is still required — the
   * sheet draws it and the comping generator lays its figures on these same
   * events — so this changes what SOUNDS, nothing else. */
  pianoMidis?: number[];
  /** How the piano pitches split between the hands, when the voicing family
   * splits them (two-hand, upper-structure, quartal, guide-tones + root). A
   * CompHit tagged `hand: 'right'` strikes just that hand's pitches as a
   * PARTIAL strike — the other hand's ringing tones are left alone. */
  pianoHands?: { left: number[]; right: number[] };
  /** The chord's LIFT voicing — the nearest inversion above the walk's pick.
   * A CompHit tagged `lift` strikes these pitches whole; the full-strike
   * path keeps the common tones (typically the LH root) ringing. */
  pianoLift?: number[];
  /** Absolute beat onset from the start of the chart (0-based). */
  startBeat: number;
  /** Length in beats (controls bass note length). */
  durationBeats: number;
  /** Low MIDI pitch of the chord root — legacy single-root bass fallback. */
  bassMidi?: number;
}

export interface NoteExpressionOptions {
  velocity?: number;
  articulationGate?: number;
  /** Gain relative to the attack dynamic. Ramps interpolate; other points step. */
  expression?: { offsetSeconds: number; gain: number; ramp?: boolean }[];
}

/** One note of a precomputed part, keyed by absolute in-form beat. */
export interface BassTrackNote {
  midi: number;
  /** Absolute in-form beat onset; fractional for off-beat embellishments. */
  beat: number;
  durationBeats: number;
  /** Relative gain (ghost notes < 1, written accents may exceed 1). Default 1. */
  velocity?: number;
  /** Written articulation: sound this fraction of the duration, with no generated tail. */
  articulationGate?: number;
  /** Written dynamics across a held note, relative to its attack dynamic. */
  expression?: { beatOffset: number; gain: number; ramp?: boolean }[];
  /** false = play where written whatever the band swings (a latin tumbao). */
  swing?: false;
}

export interface AuthoredCompTrackNote extends BassTrackNote {
  /** Omitted keeps legacy authored comp on the guitar. */
  instrument?: 'piano' | 'guitar';
}

/** A written score span which replaces generated comping, including silence. */
export interface AuthoredCompSpan {
  startBeat: number;
  durationBeats: number;
}

/** A recorded drum loop stretched to the arrangement tempo without changing pitch. */
export interface DrumLoopRef {
  url: string;
  bpm: number;
  beats: number;
  seconds: number;
  /**
   * Where the loop's skip note sits, a fraction of the beat (audio/loopSwing.ts):
   * a number from the manifest, null when measured and unclear, absent when
   * never measured (an uploaded loop), in which case the player measures it as
   * it decodes the loop.
   */
  swing?: number | null;
}

/**
 * Does the beat at `beat` swing?
 *
 * One question, asked by every track at the same onset. That is the whole
 * point: melody, bass and comp landing on the same beat must agree about
 * where that beat's "and" is, and a feel boundary is exactly where
 * independently-derived answers drift apart.
 */
/** Identical count and identical per-index timing: the same chords in the same
 *  places, so nothing addressed by index (a CompHit's chordIndex) has moved. */
function sameArrangementTiming(a: ArrangementEvent[], b: ArrangementEvent[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (event, index) =>
      Math.abs(event.startBeat - b[index].startBeat) <= 1e-6 &&
      Math.abs(event.durationBeats - b[index].durationBeats) <= 1e-6,
  );
}

function swingsAt(opts: ArrangementOptions, beat: number): boolean {
  return opts.swingAt ? opts.swingAt(beat) : !!opts.swing;
}

/** The swing feel in force at `beat`: per onset under cues, else the band's. */
function swingFeelAt(opts: ArrangementOptions, beat: number): SwingFeel | undefined {
  return opts.swingFeelAt ? opts.swingFeelAt(beat) : opts.swingFeel;
}

/** Whether the triplet eighth follows the kit at `beat` (a section may differ). */
function swingsWithKitAt(opts: ArrangementOptions, beat: number): boolean {
  return opts.swingWithKitAt ? opts.swingWithKitAt(beat) : !!opts.swingWithKit;
}

/** Where the kit in force at `beat` puts its skip note, when it has a clear one. */
function kitSwingAt(opts: ArrangementOptions, beat: number): number | undefined {
  const loop = drumsAtBeat(opts, beat);
  if (!loop) return undefined;
  const swing = loop.swing !== undefined ? loop.swing : measuredLoopSwing(loop.url);
  return typeof swing === 'number' ? swing : undefined;
}

/** How far past the kit the band and each part swing at `beat`. */
function swingOffsetsAt(opts: ArrangementOptions, beat: number): SwingOffsets | undefined {
  return opts.swingOffsetsAt ? opts.swingOffsetsAt(beat) : opts.swingOffsets;
}

/**
 * The swing grid at `beat`: the feel in force, its "and" moved onto the kit's
 * skip note where the triplet eighth swings with the kit (docs/swing-plan.md
 * §4.1). The kit is the clock: every part asking here lands its "and" where
 * the drummer plays it, moved by the band's offset and then the part's own
 * (the sliders under Kit and on each part). With no `part` the answer is the
 * drums', the recording's own "and". A kit with no clear skip note, or no kit,
 * swings the triplet; any other feel keeps its own ratio.
 */
function gridAt(opts: ArrangementOptions, beat: number, part?: SwingPart): SwingGrid {
  const feel = swingFeelAt(opts, beat) ?? DEFAULT_SWING_FEEL;
  if (feel === KIT_FEEL && swingsWithKitAt(opts, beat)) {
    const kit = kitSwingAt(opts, beat);
    if (!part) return kit !== undefined ? kitGrid(kit) : swingGrid(feel);
    const base = kit ?? swingGrid(feel).ratio;
    return kitGrid(kitSwingRatio(base, swingOffsetsAt(opts, beat), part));
  }
  return swingGrid(feel);
}

/** Where each part sits at `beat`; absent = every part on the grid. */
function placementAt(opts: ArrangementOptions, beat: number): FeelPlacement | undefined {
  return opts.placementAt ? opts.placementAt(beat) : opts.placement;
}

/**
 * Whether a melody note on `position` is a SWUNG DOWNBEAT: on the start of a
 * pair at the swing's level, in a pair the line plays at that level, where the
 * band swings. Only those sit late (feel.ts): the offbeats stay on the kit.
 */
function isSwungDownbeat(
  opts: ArrangementOptions,
  position: number,
  origin: number,
  track?: readonly { beat: number }[],
  shift = 0,
): boolean {
  const grid = gridAt(opts, Math.floor(position - origin + 1e-9) + origin, 'melody');
  const written = position - shift - origin;
  const k = Math.round(written / grid.pair);
  if (Math.abs(written - k * grid.pair) >= 0.02) return false;
  if (track && straightPairsOf(track, grid, origin).has(k)) return false;
  return swingsAt(opts, Math.floor(k * grid.pair + 1e-9) + origin + shift);
}

/**
 * Seconds a fresh melody onset moves: a swung downbeat late by its placement's
 * delay at this tempo (never more than 40% of the note), and the looseness
 * drift on every note.
 */
function melodyOnsetShift(
  opts: ArrangementOptions,
  position: number,
  origin: number,
  secondsPerBeat: number,
  lengthBeats: number,
  track?: readonly { beat: number }[],
  shift = 0,
): number {
  const placement = placementAt(opts, position)?.melody;
  const delay =
    placement && placement !== 'on-top' && isSwungDownbeat(opts, position, origin, track, shift)
      ? Math.min(
          melodyDownbeatDelay(placement, 60 / secondsPerBeat),
          0.4 * lengthBeats * secondsPerBeat,
        )
      : 0;
  return delay + timingDrift(opts.feel, 'melody', position);
}

/**
 * Where a written position SOUNDS. The second note of a pair at the feel's
 * level (an "and" under an eighth feel) moves to the feel's ratio when the
 * pair swings; every other position stays put (swingFeel.ts). `track` is the
 * part the note belongs to (`part` names it, for its own swing under Kit;
 * none is the drums): a pair it plays finer than the level (sixteenths
 * under an eighth feel) stays straight, or its "and" lands on the sixteenth
 * after it and the two blur into one (user-found on Libertango). `origin` is
 * where the beat grid starts (a fractional pickup moves every beat), and
 * `shift` re-bases a note played away from where it is written (the pickup's
 * upbeat over the last bar).
 */
function swungPosition(
  opts: ArrangementOptions,
  position: number,
  origin: number,
  part?: SwingPart,
  track?: readonly { beat: number }[],
  shift = 0,
): number {
  const grid = gridAt(opts, Math.floor(position - origin + 1e-9) + origin, part);
  const straight = track ? straightPairsOf(track, grid, origin) : undefined;
  return (
    shift +
    origin +
    swingPosition(
      position - shift - origin,
      grid,
      (beat) => swingsAt(opts, beat + origin + shift),
      straight,
    )
  );
}

/**
 * A note's length as it sounds: from where it starts sounding to where its
 * written end sounds, so a swung pair comes out long-short. Keeping the
 * straight length behind a delayed "and" turned the line inside out: the
 * downbeat note stopped short of its partner and the "and" ran on into the
 * next beat (user-found by ear on Moment's Notice: "it's stiff sounding").
 * A note the swing squeezes below half its written length keeps its written
 * length.
 */
function swungLength(
  opts: ArrangementOptions,
  writtenStart: number,
  writtenLength: number,
  soundingStart: number,
  origin: number,
  part?: SwingPart,
  track?: readonly { beat: number }[],
  shift = 0,
): number {
  const end = swungPosition(opts, writtenStart + writtenLength, origin, part, track, shift);
  const length = end - soundingStart;
  return length >= writtenLength / 2 ? length : writtenLength;
}

/** The drum loop in force at `beat`; the global loop when no segments. */
function drumsAtBeat(opts: ArrangementOptions, beat: number): DrumLoopRef | null {
  const segments = opts.drumSegments;
  if (!segments?.length) return opts.drums ?? null;
  let current: DrumLoopRef | null = opts.drums ?? null;
  for (const segment of segments) {
    if (segment.fromBeat > beat + 1e-6) break;
    current = segment.loop;
  }
  return current;
}

/**
 * Lock-screen Next/Previous. The player knows nothing about set lists: the
 * set strip hands it the two moves while a set is open, and only the sides
 * the set actually has — so the OS hides Next at the last tune instead of
 * offering a dead button — and null when the set ends.
 */
export interface MediaNavigation {
  next?: () => void;
  previous?: () => void;
}

export interface ArrangementOptions {
  bpm: number;
  /**
   * What the lock screen / media controls show while the band plays (the
   * MediaSession "Now Playing" card): the tune's title and its composer.
   */
  nowPlaying?: { title: string; artist?: string };
  /**
   * Sounding length of the expanded form. Harmony usually implies this, but
   * written parts can continue through trailing (or entirely) N.C. measures.
   */
  formBeats?: number;
  /**
   * Resolve the tempo for a 1-based logical pass. Logical passes include every
   * entry in `passStarts`, then continue across whole-form repeats. The initial
   * count-in and an inter-pass count-in use the tempo of the pass they lead
   * into. This is synchronous so a speed trainer cannot lag one beat behind a
   * boundary while UI state catches up.
   */
  bpmForPass?: (pass: number) => number;
  beatsPerBar?: number;
  metronome?: boolean;
  /** Explicit in-form click positions, including pickup-aware downbeats. */
  metronomeBeats?: { beat: number; accent: boolean; inBar?: number }[];
  bassline?: boolean;
  /** When true (with bassline), play only the bass — mute the chord strums. */
  soloBass?: boolean;
  /**
   * Precomputed walking bass line for the whole form (one note per beat / two
   * beats). When present it drives the bass instead of the per-event root, so a
   * live style change just swaps this array.
   */
  bassNotes?: BassTrackNote[];
  /**
   * Per-pass bass variants: one full-form line each. When present and looping,
   * pass N of the form plays `bassVariants[N % length]` so a repeated form isn't
   * note-for-note identical. `bassNotes` is the fallback when this is absent.
   */
  bassVariants?: BassTrackNote[][];
  /** Swing the eighth-note offbeats of the bass line (delays each "and"). */
  swing?: boolean;
  /**
   * Per-onset feel lookup (#174). A tune whose feel changes during the form
   * cannot be described by one global switch: a Latin A and a swing bridge
   * need the answer at each onset, and every track — melody, bass, comp —
   * must get the SAME answer at the same beat or a boundary is where they
   * drift apart. Absent = the global `swing` applies throughout.
   */
  swingAt?: (beat: number) => boolean;
  /**
   * How the band swings (swingFeel.ts): the level that swings, eighths or
   * sixteenths, and the ratio, triplet, dotted or Scottish. Absent = the
   * triplet eighth. Only consulted where `swing`/`swingAt` say the band swings.
   */
  swingFeel?: SwingFeel;
  /** Per-onset swing feel, beside `swingAt`, for a band split by section or cue. */
  swingFeelAt?: (beat: number) => SwingFeel | undefined;
  /**
   * The triplet eighth swings WITH THE KIT (docs/swing-plan.md §4.1): every
   * "and" lands on the skip note the loop in force measured (DrumLoopRef.swing),
   * so the bass and the melody lock with the ride. A kit with no clear skip
   * note, or no kit, swings the triplet. Absent = the triplet everywhere.
   */
  swingWithKit?: boolean;
  /** Per-onset `swingWithKit`, for a band split by section or cue. */
  swingWithKitAt?: (beat: number) => boolean;
  /**
   * How far past the kit the band swings, and each part past the band, where
   * the triplet eighth follows the kit (swingFeel.ts `kitSwingRatio`): the
   * slider under Kit and each part's Swing slider. The drums are the loop's
   * recording and keep its own swing. Absent = every part on the kit.
   */
  swingOffsets?: SwingOffsets;
  /** Per-onset `swingOffsets`, for a band split by section or cue. */
  swingOffsetsAt?: (beat: number) => SwingOffsets | undefined;
  /**
   * Where each part sits against the kit (feel.ts): the bass and the comp by a
   * constant few milliseconds, the melody's swung downbeats late. Absent =
   * every part on the grid.
   */
  placement?: FeelPlacement;
  /** Per-onset `placement`, for a band split by section or cue. */
  placementAt?: (beat: number) => FeelPlacement | undefined;
  /**
   * How loose the band is (#423): the reach of a slow, correlated drift on
   * every part's timing and level. Absent = tight, exact placement.
   */
  feel?: FeelTiming;
  /** Sound the melody line (when melodyNotes are present). */
  melody?: boolean;
  /** Preserve the authored instrument when a legacy part carries the melody. */
  melodyInstrument?: 'lead' | 'piano' | 'bass' | 'guitar';
  /**
   * Precomputed melody track for the whole (flattened) form, keyed by
   * absolute in-form beat like `bassNotes` — so repeats, voltas and codas
   * carry the tune through every pass. Tied notes arrive pre-merged.
   */
  melodyNotes?: BassTrackNote[];
  /**
   * Precomputed comping rhythm (Charleston, pushes, approach stabs…). When
   * present it replaces the default one-strum-per-chord; hits reference the
   * events array by index, so both must come from the same optimised form.
   */
  compHits?: CompHit[];
  /** Comping style controls articulation as well as the precomputed hit pattern. */
  compStyle?: CompStyle;
  /** Polyphonic MIDI heads extracted from written Guitar/Piano score parts. */
  authoredCompNotes?: AuthoredCompTrackNote[];
  /**
   * Whole written bars which replace generated comp. A span with no notes is
   * intentional authored silence; an empty score bar is omitted and falls
   * back to the generator.
   */
  authoredCompSpans?: AuthoredCompSpan[];
  /** Written bass bars, including rests, which own forced-hit pitch selection. */
  authoredBassSpans?: AuthoredCompSpan[];
  /** Written bass bars that sound a note: not a band break even under N.C. */
  writtenBassPlays?: AuthoredCompSpan[];
  /** Explicit accompaniment instructions on the expanded performance timeline. */
  bandCues?: BandCueInterval[];
  /**
   * A recorded drum loop: `bpm` is its native tempo and `beats` its exact
   * length. The loop follows the arrangement tempo and stays anchored to the
   * beat grid on every pass.
   */
  drums?: DrumLoopRef | null;
  /** Keep drum pitch when changing tempo (default true). False uses playback speed. */
  drumTimeStretch?: boolean;
  /**
   * Beat-ranged drum segments, the drum half of `swingAt` (#174). Each entry
   * takes effect at `fromBeat` and holds until the next; `null` is silence.
   *
   * A LIST rather than a lookup function on purpose: the player must preload
   * every loop the performance can reach before it starts, and it cannot
   * enumerate what a function might return. A loop that starts fetching at
   * its own boundary plays silence until it arrives.
   *
   * Switching re-anchors through the existing url-changed path, so phase
   * comes from the beat: the incoming loop's "one" lands on the tune's bar,
   * not wherever the outgoing loop happened to be.
   */
  drumSegments?: { fromBeat: number; loop: DrumLoopRef | null }[];
  /** When true, loop the whole arrangement until stopped. */
  loop?: boolean;
  /**
   * The standalone metronome's gap click (#413): `on` bars click, then `off`
   * bars are silent, counted from the first downbeat. Absent = every bar.
   */
  clickGap?: { on: number; off: number };
  /** Play the whole form this many times before stopping (ignored when loop). */
  loopCount?: number;
  /**
   * Logical pass starts inside one pre-unrolled arrangement. Used by detached-
   * coda forms whose non-final and final choruses are not the same length.
   */
  passStarts?: number[];
  /** Click one bar of metronome before the form starts (even with metronome off). */
  countIn?: boolean;
  /** Length of the count-in, in bars (default 1). Read at play start. */
  countInBars?: number;
  /**
   * Insert a count-in (same length) between form passes when looping /
   * repeating — practice-room style. Read at play start: the pass grid is
   * anchored when playback begins, so this doesn't shift mid-form.
   */
  countInBetweenLoops?: boolean;
  /**
   * Length of a leading anacrusis (pickup) bar, in beats. Read at play start.
   * The pickup is melody-only — comp, bass and drums wait for the first full
   * bar's downbeat, and the drum loop's bar grid anchors THERE, not at beat 0.
   * With `countIn`, the count-in bar OVERLAYS the pickup: the clicks count a
   * full bar ending at that downbeat, so a one-crotchet pickup sounds on the
   * count's final beat (click only, no backing).
   */
  pickupBeats?: number;
  /**
   * Where a repeated form RE-ENTERS, in beats — past the ENTRY material: the
   * pickup plus any written intro/verse, which a band plays once, on the way
   * in. Defaults to `pickupBeats` (never less). Unlike the pickup, an intro
   * is full-band material — it merely doesn't repeat — so everything keyed to
   * the anacrusis (band silence, count-in overlay, bar-grid anchor) stays on
   * `pickupBeats`; this only moves the loop seam. Read at play start.
   */
  loopStartBeat?: number;
  /**
   * Fermata holds, as absolute in-form beat windows. A beat inside a window
   * plays stretched (HOLD_STRETCH x slower) with metronome and drums silent —
   * whatever struck at the window's start rings through it. The note tracks
   * are pre-thinned by the caller (see src/audio/holds.ts).
   */
  holds?: { start: number; end: number }[];
  /**
   * The ritardando window (absolute in-form beats over the FINAL bars): every
   * beat inside it plays a little longer than the one before, to
   * 1 + RITARDANDO_DEPTH on the last (src/audio/endings.ts). The kit lays out
   * through it — a loop cannot follow a pull-back — like a hold. The band hook
   * writes a looping form out before asking for one, so the window is the
   * tune's ending and not every pass's.
   */
  ritardando?: { start: number; end: number };
  /**
   * In-form bar start beats. A chorus jump waits for a barline; with mixed
   * meters the bars are not every `beatsPerBar`, so the caller names them.
   */
  barStarts?: number[];
  /**
   * Play from here: an in-form beat (a bar's start) the band begins at instead
   * of the top. The count-in, when on, counts whole bars into it; the pickup
   * and everything before the beat are skipped. Read at play start.
   */
  startBeat?: number;
}

export type TransportState = 'idle' | 'playing' | 'paused';

/** Where the running arrangement is, by chorus (a global pass). */
export type ChorusPosition = {
  /** 1-based chorus at the scheduling cursor. */
  pass: number;
  /** Choruses in the performance; Infinity when looping until Stop. */
  total: number;
  /** Form beats since the chorus's first downbeat. */
  intoPass: number;
  /** Length of the chorus's opening bar, in beats. */
  firstBar: number;
  /** A jump waiting for the next barline, or null. */
  pending: number | null;
};

/** Where a voice lands: a mixer channel's bus, or the master directly (a
 * tapped grip, edit feedback — sounds the faders must never silence). */
type Outlet = MixChannel | 'direct';

/** The mixer's Master channel — see ChordPlayer.setMasterEffects. */
export type MasterEffects = { reverb: number; compression: number };

/** The kit's place in the mix at a unity fader. */
const DRUM_BUS_GAIN = 0.9;

/**
 * The comp pianist's DAMPER PEDAL (user, Sep 2026: "you can tell there's no
 * soft pedal so the stab chords are very staccato"). A stab is a short
 * TOUCH, not a short SOUND: the hand lifts on the figure but the pedal is
 * riding, so the strings ring on and fade — `stabRelease` is that fade
 * (cutting in 60ms, the guitar's damp, read as a dead-stop). At a chord
 * change the pedal comes up just AFTER the new chord sounds (legato /
 * syncopated pedalling, the way every rhythm-section pianist changes
 * pedal), so the outgoing chord overlaps the new one by `changeOverlap` and
 * fades over `changeRelease` instead of being cut on the barline.
 */
const PIANO_PEDAL = { stabRelease: 0.32, changeOverlap: 0.05, changeRelease: 0.16 };

/** Level of a recorded piano note at full gain (the sampled set is levelled to
 * the loudness the mix was balanced against — see build-piano-samples.mjs). */
const PIANO_SAMPLE_LEVEL = 0.5;
/** Level of a recorded archtop note at full gain: its files sit at −20 dBFS
 * RMS over their first 100 ms, so this gives the 0.06 per-note RMS the guitar
 * comp was balanced at, with room for four-note voicings under the horn. */
const GUITAR_SAMPLE_LEVEL = 0.6;
/** Level of a recorded upright note at full gain. Measured in a real browser
 * on the same walking line (Blues For Alice at 132, the master bus, BS.1770
 * loudness): at 2 the upright is as loud as the electric it replaced (−22.6
 * LUFS each, alone and in the band, the limiter idle), with its pluck peaking
 * 7–8 dB higher — the punch a real pizzicato has and a held electric note
 * lacks (docs/bass-feel.md §5). */
const BASS_SAMPLE_LEVEL = 2;

function clamp01(n: number): number {
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
}

export class ChordPlayer {
  private ctx: AudioContext | null = null;
  private contextStateListener: (() => void) | null = null;
  private visibilityListener: (() => void) | null = null;
  private autoPausedForInterruption = false;
  private contextClockStalled = false;
  private master: GainNode | null = null;
  private voices = new Set<Voice>();
  // Currently-ringing voice per guitar string (index 0-5), like a real fretboard.
  // Index 6 is a virtual monophonic bass "string" used by the bass line.
  // Slots 0-5 = guitar strings, 6 = the double bass, 7 = the melody lead.
  private stringVoices: (Voice | null)[] = [null, null, null, null, null, null, null, null];
  // Signature of the chord currently ringing, so a chord *change* can mute all
  // strings while a same-chord re-trigger keeps the per-string ring-out.
  private lastChordSig: string | null = null;
  private seqToken = 0;
  private seqTimer: ReturnType<typeof setTimeout> | null = null;
  // The live scheduler's pump, so hiding the page can refill the lookahead at
  // once instead of waiting out the first throttled tick. Only ever invoked
  // while `seqTimer` is pending — a pending timer is the one invariant that
  // says the chain is live and not paused/finished/cancelled.
  private activePump: (() => void) | null = null;
  private mediaSessionBound = false;
  private mediaNavigation: MediaNavigation | null = null;
  private seqPlaying = false;
  private seqListeners = new Set<(playing: boolean) => void>();
  // Live playback options for the running arrangement; the scheduler reads these
  // each beat so tempo / metronome / bass / solo / loop update mid-playback.
  private liveOptions: ArrangementOptions | null = null;
  // Current in-form beat during arrangement playback (-1 when idle), for a playhead.
  private beatListeners = new Set<(beat: number) => void>();
  private transport: TransportState = 'idle';
  private transportListeners = new Set<(state: TransportState) => void>();
  private completionListeners = new Set<() => void>();
  // Handles into the running arrangement's closure so pause/resume can freeze
  // the scheduler and restart its clock without losing its place.
  private arrControl: {
    schedule: (abs: number) => void;
    resetClock: () => void;
    freeze: () => void;
    nextAbs: number;
    position: () => ChorusPosition;
    jump: (pass: number | null) => void;
    /** Move to an in-form beat (a bar) in the chorus playing, on the next beat. */
    seek: (formBeat: number) => boolean;
    /** Queue a new form (events + options) to land on the next beat. */
    reform: (events: ArrangementEvent[] | null, patch: Partial<ArrangementOptions>) => boolean;
    hasPendingForm: () => boolean;
  } | null = null;
  private pendingChorusListeners = new Set<(pass: number | null) => void>();
  private lastPendingChorus: number | null = null;
  // The running arrangement's events, read by the scheduler per beat window so
  // a same-timing voicing swap (setArrangementEvents) lands live. Null when
  // nothing is playing.
  private liveEvents: ArrangementEvent[] | null = null;
  // One-shot: after a resume, the next beat re-strikes chords still sounding at
  // that beat (not just ones that start on it), so playback picks up audibly.
  private resumeRestrike = false;

  private leadSound: LeadSound = 'tenor-sax';
  private bassSound: BassSound = 'upright';
  private guitarSound: GuitarSound = 'jazz-guitar';

  setLeadSound(sound: LeadSound): void {
    this.leadSound = sound;
    if (!this.ctx) return;
    if (sound === 'piano') void this.preloadPianoSamples();
    else if (sound !== 'guitar') void loadInstrumentSamples(this.ctx, sound);
  }

  setBassSound(sound: BassSound): void {
    this.bassSound = sound;
    if (!this.ctx) return;
    if (sound === 'upright') void this.preloadBassSamples();
    else void loadInstrumentSamples(this.ctx, sound);
  }

  setGuitarSound(sound: GuitarSound): void {
    this.guitarSound = sound;
    if (this.ctx) void this.preloadGuitarSamples();
  }

  /** The archtop recordings (public/instruments/archtop/, Karoryfer's
   * Shinyguitar, CC0): two velocity layers × two round robins every minor
   * third, played up to a fifth from the nearest recording so a guitar lead
   * keeps its voice up to G6. */
  private guitarSampler = new LayeredSampler('instruments/archtop', 7);

  /** The upright bass (public/instruments/upright/, Karoryfer's Meatbass,
   * CC0): three velocity layers × three round robins every minor third from
   * C1 to C4, played up to a fifth from the nearest recording so a bass head
   * keeps its voice to G4. */
  private bassSampler = new LayeredSampler('instruments/upright', 7);

  private async preloadBassSamples(complete = false): Promise<void> {
    if (this.bassSound !== 'upright') return;
    try {
      // Play waits for the hard pluck only, as the piano waits for its
      // loudest layer: decoding all 117 files first added 2.2 s to a first
      // Play on a phone-speed CPU. The other two follow in a second or two,
      // the hard pluck answering for them until they land. An audition that
      // must hear the real layers asks for the `complete` set.
      await this.bassSampler.load(this.ensureContext());
      if (complete) await this.bassSampler.whenSettled();
    } catch {
      // The synthesised bass remains available without the recordings.
    }
  }

  private async preloadGuitarSamples(): Promise<void> {
    if (this.guitarSound === 'original') return;
    try {
      // The whole set is 1.3 MB, so this waits for EVERY layer, not just the
      // loudest: a comp never changes timbre partway through the form, and an
      // audition that requires the complete set finds it complete.
      void this.guitarSampler.load(this.ensureContext());
      await this.guitarSampler.whenSettled();
    } catch {
      // The original pluck remains available without the recordings.
    }
  }

  /** Warm selected voices before anchoring a transport's first beat.
   * `complete` waits for every layer of the layered bass as well, for an
   * audition that must not hear one layer answering for another. */
  async preloadInstrumentSamples({ complete = false }: { complete?: boolean } = {}): Promise<void> {
    try {
      const ctx = this.ensureContext();
      await Promise.all([
        this.leadSound === 'guitar' || this.leadSound === 'piano'
          ? null
          : loadInstrumentSamples(ctx, this.leadSound),
        this.bassSound === 'upright'
          ? this.preloadBassSamples(complete)
          : loadInstrumentSamples(ctx, this.bassSound),
        this.preloadGuitarSamples(),
      ]);
    } catch {
      // The existing synthesized voices keep notes audible if audio or a
      // recording is unavailable.
    }
  }

  /**
   * Read-only readiness check for auditions that require the recorded voice.
   * Ordinary playback still permits the existing partial/synthesized fallback.
   * Do not create a context or initiate network work from a status check.
   */
  hasCompleteInstrumentSamples(sound: SampledInstrumentSound | 'jazz-guitar' | 'upright'): boolean {
    if (!this.ctx) return false;
    if (sound === 'jazz-guitar') return this.guitarSampler.isComplete();
    if (sound === 'upright') return this.bassSampler.isComplete();
    const loaded = getInstrumentSamples(this.ctx, sound);
    if (!loaded) return false;
    const files = new Set(loaded.samples.map((sample) => sample.file));
    return INSTRUMENT_SAMPLE_DEFINITIONS[sound].every((sample) => files.has(sample.file));
  }

  // Which pass through the form is playing (1-based; 0 when idle / counting in).
  private passListeners = new Set<(pass: number) => void>();
  private lastPass = 0;

  /** Subscribe to the current playback beat (-1 when idle). Returns an unsubscribe fn. */
  onBeat(listener: (beat: number) => void): () => void {
    this.beatListeners.add(listener);
    return () => this.beatListeners.delete(listener);
  }

  /**
   * Skip by CHORUS (a global pass). Forward goes to the next chorus; each tap
   * before the jump lands adds one more. Back restarts the chorus being
   * played, or, within its opening bar, goes to the one before; a back tap
   * that undoes a pending forward cancels it. The jump waits for the next
   * barline; paused, it moves at once. Returns false when nothing plays.
   */
  skipChorus(direction: 1 | -1): boolean {
    const control = this.arrControl;
    if (!control || this.transport === 'idle') return false;
    const at = control.position();
    if (direction > 0) {
      const base = at.pending ?? at.pass;
      if (base >= at.total) return false;
      control.jump(base + 1);
      return true;
    }
    if (at.pending !== null) {
      const target = at.pending - 1;
      if (target <= at.pass && at.pending > at.pass) control.jump(null);
      else control.jump(Math.max(1, target));
      return true;
    }
    control.jump(at.intoPass >= at.firstBar - 1e-3 ? at.pass : Math.max(1, at.pass - 1));
    return true;
  }

  /** Jump straight to a chorus (1-based), at the next barline. */
  jumpToChorus(pass: number): boolean {
    const control = this.arrControl;
    if (!control || this.transport === 'idle') return false;
    control.jump(pass);
    return true;
  }

  /**
   * Move the band to an in-form beat (a bar's start) in the chorus being
   * played: tap a bar and the band goes there. Unlike a chorus jump it does
   * not wait for a barline, it lands on the next beat the scheduler reaches,
   * so the tap answers at once. Paused, the cursor moves and Resume plays
   * from there. Returns false when nothing plays or the beat is off the form.
   */
  seekToBeat(formBeat: number): boolean {
    const control = this.arrControl;
    if (!control || this.transport === 'idle') return false;
    return control.seek(formBeat);
  }

  /** The running arrangement's chorus position, or null when idle. */
  chorusPosition(): ChorusPosition | null {
    return this.arrControl && this.transport !== 'idle' ? this.arrControl.position() : null;
  }

  /** Subscribe to the pending chorus jump (null when none). */
  onPendingChorus(listener: (pass: number | null) => void): () => void {
    this.pendingChorusListeners.add(listener);
    return () => this.pendingChorusListeners.delete(listener);
  }

  private emitPendingChorus(pass: number | null): void {
    if (pass === this.lastPendingChorus) return;
    this.lastPendingChorus = pass;
    this.pendingChorusListeners.forEach((l) => {
      try {
        l(pass);
      } catch {
        /* ignore listener errors */
      }
    });
  }

  /** Subscribe to the current repeat/pass number (1-based; 0 when idle). */
  onPass(listener: (pass: number) => void): () => void {
    this.passListeners.add(listener);
    return () => this.passListeners.delete(listener);
  }

  private emitPass(pass: number): void {
    if (pass === this.lastPass) return;
    this.lastPass = pass;
    this.passListeners.forEach((l) => {
      try {
        l(pass);
      } catch {
        /* ignore listener errors */
      }
    });
  }

  private emitBeat(beat: number): void {
    this.beatListeners.forEach((l) => {
      try {
        l(beat);
      } catch {
        /* ignore listener errors */
      }
    });
  }

  /** Subscribe to sequence play/stop changes; returns an unsubscribe fn. */
  onSequenceChange(listener: (playing: boolean) => void): () => void {
    this.seqListeners.add(listener);
    return () => this.seqListeners.delete(listener);
  }

  isSequencePlaying(): boolean {
    return this.seqPlaying;
  }

  /**
   * Monotonic ownership token for clients with their own scheduler (the score
   * transport). A global Stop/Reset or another playback claimant invalidates
   * the token, so that client can stop dispatching without silencing the newer
   * owner in turn.
   */
  getPlaybackGeneration(): number {
    return this.seqToken;
  }

  private setSeqPlaying(playing: boolean): void {
    if (this.seqPlaying === playing) return;
    this.seqPlaying = playing;
    this.seqListeners.forEach((l) => l(playing));
  }

  /**
   * Subscribe to a sequence reaching its END — the scheduler running out, not
   * a Stop.
   *
   * Both paths land on `transport: 'idle'`, so a transport listener cannot
   * tell "the tune finished" from "the player pressed Stop". Anything that
   * should happen only on a real ending (advancing a set) needs the
   * difference, and only the scheduler knows it.
   */
  onSequenceComplete(listener: () => void): () => void {
    this.completionListeners.add(listener);
    return () => this.completionListeners.delete(listener);
  }

  /** Subscribe to transport changes (idle / playing / paused); returns an unsubscribe fn. */
  onTransportChange(listener: (state: TransportState) => void): () => void {
    this.transportListeners.add(listener);
    return () => this.transportListeners.delete(listener);
  }

  getTransportState(): TransportState {
    return this.transport;
  }

  /** Whether the transport is playing a tune's arrangement: the standalone
   *  metronome plays an empty form, practice that belongs to no tune, which
   *  play history must not count against the tune on screen. */
  playingTune(): boolean {
    return this.transport !== 'idle' && !!this.liveEvents && this.liveEvents.length > 0;
  }

  private setTransport(state: TransportState): void {
    if (this.transport === state) return;
    this.transport = state;
    this.transportListeners.forEach((l) => l(state));
    this.publishPlaybackState();
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();

      const master = this.ctx.createGain();
      master.gain.value = 0.5;

      const limiter = this.ctx.createDynamicsCompressor();
      limiter.threshold.value = -10;
      limiter.knee.value = 24;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.25;

      // master → [compressor → makeup]? → dry/reverb send → limiter.
      // The room receives the compressed signal; its tail joins the dry
      // signal at the limiter, before the final Master fader. Both are wired by
      // applyMasterEffects so the mixer's Master channel can change them
      // live; the convolver is built on first use (a 2s impulse is not free
      // on a phone, and most sessions never move the slider).
      const dryBus = this.ctx.createGain();
      master.connect(dryBus);
      dryBus.connect(limiter);
      // The master FADER sits after the limiter: turning the band down never
      // changes how hard the compressor or the limiter work, and it only
      // attenuates, so nothing gets past the safety.
      const output = this.ctx.createGain();
      output.gain.value = levelGain(this.levels.master);
      limiter.connect(output);
      output.connect(this.ctx.destination);
      this.master = master;
      this.dryBus = dryBus;
      this.limiter = limiter;
      this.output = output;
      this.buses = {};
      this.clickBus = null;
      this.applyMasterEffects();

      // Declare the page's audio as MEDIA PLAYBACK (the Audio Session API,
      // Safari 16.4+): it sounds through the ringer switch and is meant to
      // continue when the screen locks, where the default "ambient" session is
      // muted by the switch and parked on lock. Absent elsewhere; harmless.
      const audioSession = (navigator as { audioSession?: { type: string } }).audioSession;
      if (audioSession) {
        try {
          audioSession.type = 'playback';
        } catch {
          /* an older Safari that exposes the object but not the value */
        }
      }
      this.bindMediaSession();

      // iOS exposes calls, route changes and screen/background transitions as
      // suspended/interrupted context states. Freeze the musical transport at
      // that boundary; a later successful resume re-anchors it cleanly.
      if (typeof this.ctx.addEventListener === 'function') {
        const observed = this.ctx;
        this.contextStateListener = () => this.handleContextStateChange(observed);
        observed.addEventListener('statechange', this.contextStateListener);
      }
      if (typeof document !== 'undefined') {
        this.visibilityListener = () => {
          if (document.visibilityState === 'visible' && this.autoPausedForInterruption) {
            void this.resume();
          }
          if (document.visibilityState === 'hidden') this.refillOnHide();
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
      }
    }
    return this.ctx;
  }

  /** How far ahead the scheduler fills: deep while hidden (throttled timers). */
  private lookaheadSeconds(): number {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden'
      ? AUDIO_HIDDEN_LOOKAHEAD_SECONDS
      : AUDIO_LOOKAHEAD_SECONDS;
  }

  /**
   * The page just hid: its next timer may be a second away and the visible
   * fill covers 250ms. Run the pump NOW so the deep fill is in place before
   * the throttling bites. The pending timer is cleared first so the chain
   * stays single; a paused, finished or cancelled chain has no pending timer
   * and is left alone.
   */
  private refillOnHide(): void {
    if (this.seqTimer === null || !this.activePump) return;
    clearTimeout(this.seqTimer);
    this.seqTimer = null;
    this.activePump();
  }

  // ---------- Now Playing (MediaSession) ----------
  // The lock screen and hardware media keys drive the transport, and show
  // the tune — what makes the band a "now playing" app to the OS, which is
  // also what earns it background audio where the platform grants any.

  private mediaSession(): MediaSession | null {
    if (typeof navigator === 'undefined') return null;
    return (navigator as { mediaSession?: MediaSession }).mediaSession ?? null;
  }

  private bindMediaSession(): void {
    const session = this.mediaSession();
    if (!session || this.mediaSessionBound) return;
    this.mediaSessionBound = true;
    const handlers: [MediaSessionAction, () => void][] = [
      ['play', () => this.resumePlayback()],
      ['pause', () => this.pause()],
      ['stop', () => this.stop()],
    ];
    for (const [action, handler] of handlers) {
      try {
        session.setActionHandler(action, handler);
      } catch {
        /* an action this browser does not support */
      }
    }
  }

  private setNowPlaying(nowPlaying: ArrangementOptions['nowPlaying']): void {
    const session = this.mediaSession();
    if (!session || !nowPlaying || typeof MediaMetadata === 'undefined') return;
    try {
      session.metadata = new MediaMetadata({
        title: nowPlaying.title,
        artist: nowPlaying.artist ?? '',
      });
    } catch {
      /* metadata is cosmetic */
    }
  }

  /**
   * Offer (or withdraw) the lock screen's Next/Previous. Independent of the
   * transport binding, which happens at Play: a set can be entered before the
   * band starts, and the buttons must be there the moment the card appears.
   */
  setMediaNavigation(nav: MediaNavigation | null): void {
    this.mediaNavigation = nav;
    const session = this.mediaSession();
    if (!session) return;
    const bind = (action: MediaSessionAction, handler: (() => void) | undefined) => {
      try {
        session.setActionHandler(action, handler ?? null);
      } catch {
        /* an action this browser does not support */
      }
    };
    bind('nexttrack', nav?.next);
    bind('previoustrack', nav?.previous);
  }

  private publishPlaybackState(): void {
    const session = this.mediaSession();
    if (!session) return;
    try {
      session.playbackState =
        this.transport === 'playing' ? 'playing' : this.transport === 'paused' ? 'paused' : 'none';
    } catch {
      /* cosmetic */
    }
  }

  private handleContextStateChange(observed: AudioContext): void {
    if (observed !== this.ctx) return;
    const state = observed.state as string;
    if (state === 'suspended' || state === 'interrupted') {
      if (this.transport === 'playing' && this.arrControl) {
        this.autoPausedForInterruption = true;
        this.pauseTransport(true);
      }
      return;
    }
    if (state === 'running' && this.autoPausedForInterruption) this.resumePlayback();
  }

  // ---------- Drum loop ----------
  // Decoded loop audio, keyed by URL; failures are remembered so a broken
  // fetch/decode doesn't retry on every beat.
  private drumBuffers = new Map<string, AudioBuffer | 'loading' | 'failed'>();
  private drumLoads = new Map<string, Promise<AudioBuffer | null>>();
  private drum: {
    source: AudioBufferSourceNode;
    gain: GainNode;
    url: string;
    bpm: number;
    timeStretch: boolean;
  } | null = null;
  // Reuse rendered tempos (including speed-trainer passes) without retaining
  // unbounded PCM after somebody drags the tempo control through many values.
  private stretchedDrums = new Map<string, AudioBuffer>();
  private drumStretches = new Map<string, Promise<AudioBuffer | null>>();
  private failedDrumStretches = new Set<string>();
  private drumStretchBytes = 0;
  private drumStretchEpoch = 0;
  private drumRenderer: DrumStretchRenderer | null = null;
  private wantedDrumStretches = new Set<string>();
  private drumBeat = 0;
  // Includes sources with a future stop already scheduled. Keeping ownership
  // until they actually end lets an intervening Stop/Pause move that stop to now.
  private drumSources = new Set<{
    source: AudioBufferSourceNode;
    gain: GainNode;
    url: string;
    stopAt?: number;
  }>();
  private drumGain: GainNode | null = null;
  /**
   * The loop's exact musical length. NOT the manifest's `seconds`: that is the
   * measured duration of the encoded m4a, and AAC is not gapless — the file
   * carries encoder padding, so using it as `loopEnd` plays a sliver of
   * silence before every wrap (the audible "gap between loops"). The loops are
   * authored to whole bars at their native tempo, so beats × 60/bpm IS the
   * true period; anything past it in the buffer is padding to cut off.
   */
  private static drumPeriod(drums: NonNullable<ArrangementOptions['drums']>): number {
    return Math.max(0.001, drums.beats * (60 / drums.bpm));
  }

  private loadDrumBuffer(url: string, loop?: DrumLoopRef): Promise<AudioBuffer | null> {
    const existing = this.drumBuffers.get(url);
    if (existing && existing !== 'loading' && existing !== 'failed')
      return Promise.resolve(existing);
    if (existing === 'failed') return Promise.resolve(null);
    const pending = this.drumLoads.get(url);
    if (pending) return pending;
    this.drumBuffers.set(url, 'loading');
    const epoch = this.drumStretchEpoch;
    const load = fetch(url)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) =>
        epoch === this.drumStretchEpoch ? this.ensureContext().decodeAudioData(data) : null,
      )
      .then((buffer) => {
        if (!buffer || epoch !== this.drumStretchEpoch) return null;
        this.drumBuffers.set(url, buffer);
        // A loop with no manifest measurement (an uploaded one) is measured
        // here, once, so a band swinging with the kit can follow it too.
        if (loop && loop.swing === undefined && measuredLoopSwing(url) === undefined) {
          try {
            rememberLoopSwing(
              url,
              measureLoopSwing(monoSamples(buffer), buffer.sampleRate, loop.bpm).swing,
            );
          } catch {
            rememberLoopSwing(url, null);
          }
        }
        return buffer;
      })
      .catch(() => {
        if (epoch === this.drumStretchEpoch) this.drumBuffers.set(url, 'failed');
        return null;
      })
      .finally(() => {
        if (this.drumLoads.get(url) === load) this.drumLoads.delete(url);
      });
    this.drumLoads.set(url, load);
    return load;
  }

  private drumStretchKey(drums: DrumLoopRef, bpm: number): string {
    return `${drums.url}|${drums.beats}|${drums.bpm}|${bpm}`;
  }

  private preparedDrumBuffer(
    drums: DrumLoopRef,
    bpm: number,
    timeStretch = true,
  ): AudioBuffer | null {
    if (!timeStretch || Math.abs(bpm - drums.bpm) < 1e-6) {
      const original = this.drumBuffers.get(drums.url);
      return original && typeof original !== 'string' ? original : null;
    }
    const key = this.drumStretchKey(drums, bpm);
    const ready = this.stretchedDrums.get(key);
    if (!ready) return null;
    this.stretchedDrums.delete(key);
    this.stretchedDrums.set(key, ready);
    return ready;
  }

  private drumPreparationFailed(drums: DrumLoopRef, bpm: number, timeStretch = true): boolean {
    return (
      this.drumBuffers.get(drums.url) === 'failed' ||
      (timeStretch && this.failedDrumStretches.has(this.drumStretchKey(drums, bpm)))
    );
  }

  private prepareDrums(
    drums: DrumLoopRef,
    bpm: number,
    timeStretch = true,
  ): Promise<AudioBuffer | null> {
    if (!timeStretch) return this.loadDrumBuffer(drums.url, drums);
    const key = this.drumStretchKey(drums, bpm);
    if (!this.wantedDrumStretches.has(key)) return Promise.resolve(null);
    if (this.drumPreparationFailed(drums, bpm)) return Promise.resolve(null);
    const ready = this.preparedDrumBuffer(drums, bpm);
    if (ready) return Promise.resolve(ready);
    const pending = this.drumStretches.get(key);
    if (pending) return pending;
    const epoch = this.drumStretchEpoch;
    const preparation = this.loadDrumBuffer(drums.url, drums)
      .then(async (original) => {
        if (
          !original ||
          epoch !== this.drumStretchEpoch ||
          this.drumStretches.get(key) !== preparation ||
          !this.wantedDrumStretches.has(key)
        )
          return null;
        if (Math.abs(bpm - drums.bpm) < 1e-6) return original;
        const channels = Array.from({ length: original.numberOfChannels }, (_, c) =>
          original.getChannelData(c),
        );
        const renderer = (this.drumRenderer ??= new DrumStretchRenderer());
        renderer.retain(this.wantedDrumStretches);
        const rendered = await renderer.render(key, {
          channels,
          sampleRate: original.sampleRate,
          sourceFrames: Math.round(ChordPlayer.drumPeriod(drums) * original.sampleRate),
          rate: bpm / drums.bpm,
        });
        if (
          !rendered ||
          epoch !== this.drumStretchEpoch ||
          this.drumStretches.get(key) !== preparation ||
          !this.wantedDrumStretches.has(key)
        )
          return null;
        const buffer = this.ensureContext().createBuffer(
          rendered.length,
          rendered[0].length,
          original.sampleRate,
        );
        rendered.forEach((channel, c) => buffer.getChannelData(c).set(channel));
        // 96 MiB holds several ordinary loops. Retain at most one unusually
        // large loop, since evicting the just-rendered result would retry forever.
        const bytes = buffer.length * buffer.numberOfChannels * Float32Array.BYTES_PER_ELEMENT;
        while (this.stretchedDrums.size && this.drumStretchBytes + bytes > 96 * 1024 * 1024) {
          const oldest = this.stretchedDrums.entries().next().value!;
          this.stretchedDrums.delete(oldest[0]);
          this.drumStretchBytes -=
            oldest[1].length * oldest[1].numberOfChannels * Float32Array.BYTES_PER_ELEMENT;
        }
        this.stretchedDrums.set(key, buffer);
        this.drumStretchBytes += bytes;
        return buffer;
      })
      .catch(() => {
        // A failed render is terminal for this loop/tempo, like a failed
        // decode. Retrying it every beat can freeze the requested tempo and
        // repeatedly allocate a large buffer. Dispose permits a fresh retry.
        if (
          epoch === this.drumStretchEpoch &&
          this.drumStretches.get(key) === preparation &&
          this.wantedDrumStretches.has(key)
        )
          this.failedDrumStretches.add(key);
        return null;
      })
      .finally(() => {
        if (this.drumStretches.get(key) === preparation) this.drumStretches.delete(key);
      });
    this.drumStretches.set(key, preparation);
    return preparation;
  }

  /** Prepare the opening tempo without unlocking audio or claiming playback. */
  preloadArrangementDrums(
    options: Pick<
      ArrangementOptions,
      'bpm' | 'bpmForPass' | 'drums' | 'drumSegments' | 'drumTimeStretch'
    >,
  ): Promise<unknown[]> {
    let bpm = options.bpm;
    try {
      const resolved = options.bpmForPass?.(1);
      if (resolved !== undefined && Number.isFinite(resolved)) bpm = resolved;
    } catch {
      /* a UI tempo resolver must not prevent preparation or playback */
    }
    return this.warmArrangementDrums(options, Math.max(20, bpm));
  }

  private warmArrangementDrums(
    options: Pick<ArrangementOptions, 'drums' | 'drumSegments' | 'drumTimeStretch'>,
    bpm: number,
    nextBpm?: number,
    activeLoop?: DrumLoopRef | null,
  ): Promise<unknown[]> {
    const loops = new Map<string, DrumLoopRef>();
    // Render the kit sounding now ahead of later feel cues and trainer passes.
    if (activeLoop) loops.set(this.drumStretchKey(activeLoop, bpm), activeLoop);
    // A cue at the opening replaces the global loop for the entire form.
    // Keep that fallback only when it can sound before the first cue.
    if (options.drums && (options.drumSegments?.[0]?.fromBeat ?? Infinity) > 1e-6)
      loops.set(this.drumStretchKey(options.drums, bpm), options.drums);
    for (const segment of options.drumSegments ?? []) {
      if (segment.loop) loops.set(this.drumStretchKey(segment.loop, bpm), segment.loop);
    }
    const timeStretch = options.drumTimeStretch !== false;
    const tempos = timeStretch ? [...new Set([bpm, nextBpm ?? bpm])] : [];
    const requests = tempos.flatMap((tempo) =>
      [...loops.values()].map((loop) => ({
        loop,
        bpm: tempo,
        key: this.drumStretchKey(loop, tempo),
      })),
    );
    // Slider inputs supersede old work. Neither a late decode nor a finished
    // obsolete render may put that work back ahead of the latest tempo.
    this.wantedDrumStretches = new Set(requests.map((request) => request.key));
    for (const key of this.drumStretches.keys()) {
      if (!this.wantedDrumStretches.has(key)) this.drumStretches.delete(key);
    }
    this.drumRenderer?.retain(this.wantedDrumStretches);
    // Speed mode reuses decoded recordings at every tempo. Clearing the wanted
    // set above also stops a render immediately when time stretch is disabled.
    if (!timeStretch)
      return Promise.all([...loops.values()].map((loop) => this.loadDrumBuffer(loop.url, loop)));
    return Promise.all(requests.map((request) => this.prepareDrums(request.loop, request.bpm)));
  }

  /** Stop the loop; a small `fade` ramps it out instead of hard-cutting. */
  private stopDrums(at?: number, fade = 0): void {
    if (this.drumSources.size > 0) {
      const when = at ?? this.ctx?.currentTime ?? 0;
      for (const voice of [...this.drumSources]) {
        const { source, gain } = voice;
        const stopAt = when + fade;
        // Web Audio replaces an earlier scheduled stop with a later one.
        // A lookahead re-entry must not resurrect the loop across a rest.
        if (voice.stopAt !== undefined && voice.stopAt <= stopAt) continue;
        voice.stopAt = stopAt;
        try {
          if (fade > 0) {
            gain.gain.cancelScheduledValues(when);
            gain.gain.setValueAtTime(1, when);
            gain.gain.linearRampToValueAtTime(0, when + fade);
          }
          // Calling stop again before the source ends replaces a future stop,
          // which is exactly what a user Stop during a scheduled gap needs.
          source.stop(stopAt);
        } catch {
          /* not started / already ended */
        }
        // Let the tail play out; sever the graph once it must be silent.
        setTimeout(
          () => {
            this.drumSources.delete(voice);
            try {
              source.disconnect();
              gain.disconnect();
            } catch {
              /* already gone */
            }
          },
          Math.max(0, (when + fade - (this.ctx?.currentTime ?? 0)) * 1000) + 100,
        );
      }
      this.drum = null;
    }
  }

  /**
   * (Re)start the loop so its beat `beat % loop.beats` lands exactly at `at`,
   * using either a stretched buffer or the recording at an adjusted speed.
   * `fade` crossfades tempo, mode and kit changes without a click.
   */
  private startDrums(
    drums: NonNullable<ArrangementOptions['drums']>,
    beat: number,
    at: number,
    bpm: number,
    timeStretch: boolean,
    fade = 0,
  ): void {
    const buffer = this.preparedDrumBuffer(drums, bpm, timeStretch);
    if (!buffer) {
      if (this.drumPreparationFailed(drums, bpm, timeStretch)) {
        // Do not leave the old kit playing after a failed feel/tempo switch.
        // The remaining band can continue at the requested tempo in silence
        // on this channel, just as it does when initial loading fails.
        if (this.drum) this.stopDrums(at, fade);
      } else {
        void this.prepareDrums(drums, bpm, timeStretch);
      }
      return;
    }
    const ctx = this.ensureContext();
    this.stopDrums(at, fade);
    if (!this.drumGain) {
      this.drumGain = ctx.createGain();
      this.drumGain.gain.value = DRUM_BUS_GAIN * levelGain(this.levels.drums);
      this.drumGain.connect(this.master!);
    }
    const bufferBpm = timeStretch ? bpm : drums.bpm;
    const period = (drums.beats * 60) / bufferBpm;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.loopEnd = Math.min(period, buffer.duration);
    source.playbackRate.value = timeStretch ? 1 : bpm / drums.bpm;
    const gain = ctx.createGain();
    if (fade > 0) {
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(1, at + fade);
    }
    source.connect(gain);
    gain.connect(this.drumGain);
    const offset = (((beat % drums.beats) + drums.beats) % drums.beats) * (60 / bufferBpm);
    source.start(at, offset);
    const voice = { source, gain, url: drums.url, bpm, timeStretch };
    this.drumSources.add(voice);
    source.onended = () => {
      this.drumSources.delete(voice);
      if (this.drum === voice) this.drum = null;
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        /* already disconnected */
      }
    };
    this.drum = voice;
  }

  /**
   * One SLICE of the loop through a ritardando (#414): `beats` of its content
   * from loop beat `beat`, played at the kit's own speed from `at` and then
   * silenced. A loop cannot follow a pull-back, so the scheduler re-strikes
   * the recording slice by slice on the stretched grid: every hit lands on the
   * band's slowing beat and the pitch never moves (the way a beat-slicing
   * sampler stretches a drum loop). Nothing plays in the gap a longer beat
   * leaves, since the next hit is exactly what would sound there.
   */
  private drumSliceAt(
    drums: NonNullable<ArrangementOptions['drums']>,
    beat: number,
    at: number,
    bpm: number,
    timeStretch: boolean,
    beats: number,
  ): void {
    const buffer = this.preparedDrumBuffer(drums, bpm, timeStretch);
    this.stopDrums(at);
    if (!buffer || !(beats > 1e-3)) return;
    const ctx = this.ensureContext();
    if (!this.drumGain) {
      this.drumGain = ctx.createGain();
      this.drumGain.gain.value = DRUM_BUS_GAIN * levelGain(this.levels.drums);
      this.drumGain.connect(this.master!);
    }
    const bufferBpm = timeStretch ? bpm : drums.bpm;
    const rate = timeStretch ? 1 : bpm / drums.bpm;
    const period = (drums.beats * 60) / bufferBpm;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.loopEnd = Math.min(period, buffer.duration);
    source.playbackRate.value = rate;
    const length = (beats * 60) / bpm;
    const fade = Math.min(0.006, length / 4);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(1, at);
    gain.gain.setValueAtTime(1, at + length - fade);
    gain.gain.linearRampToValueAtTime(0, at + length);
    source.connect(gain);
    gain.connect(this.drumGain);
    const offset = (((beat % drums.beats) + drums.beats) % drums.beats) * (60 / bufferBpm);
    source.start(at, offset);
    source.stop(at + length);
    const voice = { source, gain, url: drums.url, bpm, timeStretch, stopAt: at + length };
    this.drumSources.add(voice);
    source.onended = () => {
      this.drumSources.delete(voice);
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        /* already disconnected */
      }
    };
  }

  // Silent looping <audio> that holds the iOS "playback" session (see SILENT_WAV).
  // While a transport plays on a touch device it carries the BAND instead —
  // see routeThroughMediaElement.
  private sessionKeeper: HTMLAudioElement | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  /** master's output tap; runs straight to the limiter, or through the compressor. */
  private dryBus: GainNode | null = null;
  private masterCompressor: DynamicsCompressorNode | null = null;
  private masterMakeup: GainNode | null = null;
  private appliedCompression = 0;
  private compressionActive = false;
  private reverbSend: GainNode | null = null;
  private reverbNode: ConvolverNode | null = null;
  private reverbSource: GainNode | null = null;
  private masterEffects: MasterEffects = { reverb: 0, compression: 0 };
  /** limiter → output → destination (or the media sink); the master fader. */
  private output: GainNode | null = null;
  /** One gain per mixer channel between its voices and the master. */
  private buses: Partial<Record<MixChannel, GainNode>> = {};
  private levels: ChannelLevels = { ...UNITY_LEVELS };
  /** The metronome's own fader, routed before or after the master effects (#412). */
  private clickBus: GainNode | null = null;
  private clickRoutedTo: AudioNode | null = null;
  private metronomeSettings: MetronomeSettings = { ...DEFAULT_METRONOME };

  /** The click's voice, pattern, subdivision and routing; remembered before the context exists. */
  setMetronome(settings: Partial<MetronomeSettings>): void {
    this.metronomeSettings = normalizeMetronome({ ...this.metronomeSettings, ...settings });
    if (this.clickBus) this.routeClick();
  }

  getMetronome(): MetronomeSettings {
    return { ...this.metronomeSettings };
  }

  /** `post` joins the master bus (reverb, compression); `pre` goes straight to the output fader. */
  private routeClick(): void {
    const bus = this.clickBus!;
    const target =
      this.metronomeSettings.routing === 'post' ? this.master! : (this.output ?? this.master!);
    if (this.clickRoutedTo === target) return;
    try {
      bus.disconnect();
    } catch {
      /* not connected yet */
    }
    bus.connect(target);
    this.clickRoutedTo = target;
  }

  private clickOut(): AudioNode {
    if (!this.clickBus) {
      this.clickBus = this.ensureContext().createGain();
      this.clickBus.gain.value = levelGain(this.levels.click);
      this.clickRoutedTo = null;
    }
    this.routeClick();
    return this.clickBus;
  }

  /**
   * The mixer's faders: one per channel (melody, comp, bass, drums; up to
   * 1.5, a little boost) and the master (0..1, after the limiter). Remembered
   * before the context exists like the master effects; a live change glides
   * over ~15ms so a fader drag never zips.
   */
  setChannelLevels(levels: Partial<ChannelLevels>): void {
    this.levels = normalizeLevels({ ...this.levels, ...levels });
    if (!this.ctx) return;
    for (const channel of Object.keys(this.buses) as MixChannel[]) {
      this.glide(this.buses[channel]!.gain, levelGain(this.levels[channel]));
    }
    if (this.drumGain) this.glide(this.drumGain.gain, DRUM_BUS_GAIN * levelGain(this.levels.drums));
    if (this.clickBus) this.glide(this.clickBus.gain, levelGain(this.levels.click));
    if (this.output) this.glide(this.output.gain, levelGain(this.levels.master));
  }

  getChannelLevels(): ChannelLevels {
    return { ...this.levels };
  }

  private glide(param: AudioParam, target: number): void {
    const now = this.ctx!.currentTime;
    param.cancelScheduledValues(now);
    param.setTargetAtTime(target, now, 0.015);
  }

  /** The node a voice connects to for its channel; buses are built on first use. */
  private busFor(channel: Outlet): AudioNode {
    const master = this.master!;
    if (channel === 'direct') return master;
    if (channel === 'drums') {
      if (!this.drumGain) {
        this.drumGain = this.ensureContext().createGain();
        this.drumGain.gain.value = DRUM_BUS_GAIN * levelGain(this.levels.drums);
        this.drumGain.connect(master);
      }
      return this.drumGain;
    }
    let bus = this.buses[channel];
    if (!bus) {
      bus = this.ensureContext().createGain();
      bus.gain.value = levelGain(this.levels[channel]);
      bus.connect(master);
      this.buses[channel] = bus;
    }
    return bus;
  }

  /**
   * The mixer's Master channel: a reverb amount (0..1, the send law lives in
   * reverb.ts) and a compression amount (0..1). Remembered before the context exists
   * and applied when it is built, so the first note of a session already
   * wears the saved settings. Active compressor changes glide without
   * rewiring the running graph; zero bypasses it. Legacy switches map to
   * the original 50% setting or zero.
   */
  setMasterEffects(fx: { reverb?: number; compression?: number | boolean }): void {
    const next: MasterEffects = {
      reverb: fx.reverb === undefined ? this.masterEffects.reverb : clamp01(fx.reverb),
      compression:
        fx.compression === undefined
          ? this.masterEffects.compression
          : normalizeCompressionAmount(fx.compression),
    };
    this.masterEffects = next;
    if (this.ctx) this.applyMasterEffects();
  }

  getMasterEffects(): MasterEffects {
    return { ...this.masterEffects };
  }

  private applyMasterEffects(): void {
    const ctx = this.ctx;
    const dryBus = this.dryBus;
    const limiter = this.limiter;
    if (!ctx || !dryBus || !limiter) return;
    const { reverb, compression } = this.masterEffects;

    // Compression precedes both the dry output and the reverb send. Amount
    // changes keep these connections; bypass changes the room's source below.
    if (compression > 0) {
      const settings = masterCompressionSettings(compression);
      if (!this.masterCompressor) {
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = settings.threshold;
        comp.knee.value = settings.knee;
        comp.ratio.value = settings.ratio;
        comp.attack.value = settings.attack;
        comp.release.value = settings.release;
        const makeup = ctx.createGain();
        makeup.gain.value = settings.makeup;
        comp.connect(makeup);
        makeup.connect(limiter);
        this.masterCompressor = comp;
        this.masterMakeup = makeup;
      } else if (compression !== this.appliedCompression) {
        this.glide(this.masterCompressor.threshold, settings.threshold);
        this.glide(this.masterCompressor.ratio, settings.ratio);
        this.glide(this.masterMakeup!.gain, settings.makeup);
      }
      this.appliedCompression = compression;
      if (!this.compressionActive) {
        dryBus.disconnect(limiter);
        dryBus.connect(this.masterCompressor);
        this.compressionActive = true;
      }
    } else if (this.compressionActive) {
      dryBus.disconnect(this.masterCompressor!);
      dryBus.connect(limiter);
      this.compressionActive = false;
    }

    // Reverb receives the output after optional compression and makeup.
    // Its tail returns directly to the limiter, never through compression.
    // Disconnect the send at zero so an idle room costs nothing.
    const send = reverbSendGain(reverb);
    if (send > 0) {
      if (!this.reverbNode) {
        const convolver = ctx.createConvolver();
        convolver.buffer = buildReverbImpulse(ctx);
        const sendGain = ctx.createGain();
        sendGain.gain.value = 0;
        sendGain.connect(convolver);
        convolver.connect(limiter);
        this.reverbNode = convolver;
        this.reverbSend = sendGain;
      }
      const sendGain = this.reverbSend!;
      sendGain.gain.value = send;
      const source = this.compressionActive ? this.masterMakeup! : dryBus;
      if (this.reverbSource !== source) {
        this.reverbSource?.disconnect(sendGain);
        source.connect(sendGain);
        this.reverbSource = source;
      }
    } else if (this.reverbSend) {
      this.reverbSend.gain.value = 0;
      if (this.reverbSource) {
        this.reverbSource.disconnect(this.reverbSend);
        this.reverbSource = null;
      }
    }
  }
  private sinkNode: MediaStreamAudioDestinationNode | null = null;
  private sinkRouted = false;
  /** The ONE element bound to the sink stream — two would render the band twice (comb filter). */
  private sinkElement: HTMLAudioElement | null = null;
  /** Set by a transport Play before its gesture-bound resume(): carry the band through the media element. */
  private wantSinkRoute = false;

  private isTouchDevice(): boolean {
    // An explicit maxTouchPoints is authoritative (every current browser has
    // one: 0 on a desktop, 5 on an iPhone); the touch-handler probe is only
    // the fallback for engines without it — jsdom among them, which is why
    // the desktop test states its 0 explicitly.
    const points = navigator.maxTouchPoints;
    if (typeof points === 'number') return points > 0;
    return 'ontouchstart' in window;
  }

  private createSessionKeeper(): HTMLAudioElement | null {
    try {
      const audio = document.createElement('audio');
      audio.setAttribute('playsinline', '');
      audio.loop = true;
      audio.preload = 'auto';
      audio.src = SILENT_WAV;
      this.sessionKeeper = audio;
      return audio;
    } catch {
      this.sessionKeeper = null;
      return null;
    }
  }

  /**
   * On touch devices, loop a silent <audio> element so the OS treats the page
   * as media playback — otherwise the iPhone ringer switch mutes all Web
   * Audio. Must be called from inside a user gesture; failures are ignored
   * and retried on the next gesture. While a transport is starting
   * (wantSinkRoute) the same element carries the band itself instead.
   */
  private ensurePlaybackSession(): void {
    if (!this.isTouchDevice()) return;
    const audio = this.sessionKeeper ?? this.createSessionKeeper();
    if (!audio) return;
    if (this.wantSinkRoute) {
      this.routeThroughMediaElement(audio);
      return;
    }
    if (this.sinkRouted && !this.transportLive()) {
      // A transport that played to its end leaves the element carrying the
      // band (see finish); the next sound outside a transport, which always
      // starts from a gesture here, takes the direct route back.
      this.routeDirect();
      return;
    }
    if (audio.paused) {
      // No activation yet (or a load aborted by a source switch): the next
      // gesture retries on this SAME element. Dropping the element here once
      // orphaned one still bound to the sink stream — see routeDirect.
      audio.play().catch(() => undefined);
    }
  }

  // ---------- Background playback, stage 2: the media element carries the band ----------
  // iOS keeps a playing <audio> element alive when Safari goes to the
  // background or the screen locks, but SUSPENDS the AudioContext behind it —
  // user-found: the Now Playing card survived the lock, the band it announced
  // did not. So while a transport plays on a touch device the master is
  // routed through a MediaStream sink INTO that element: what continues in
  // the background is then the music itself. Desktop keeps the direct route
  // (its background tabs already keep Web Audio alive, and the direct route
  // has the lower latency), and so does tap-to-hear outside a transport. The
  // element must start inside the Play gesture — this runs from resume() in
  // the click stack, before any await — and an element that refuses to play
  // falls back to the speaker route so the band always sounds.

  private routeThroughMediaElement(audio: HTMLAudioElement): void {
    const ctx = this.ctx;
    const output = this.output;
    if (!ctx || !output) return;
    if (this.sinkRouted) {
      // Already carrying the band (a resume after a pause, or iOS paused the
      // element on hide): just make sure it is playing.
      if (audio.paused) audio.play().catch(() => this.routeDirect());
      return;
    }
    if (typeof ctx.createMediaStreamDestination !== 'function') return;
    try {
      this.sinkNode ??= ctx.createMediaStreamDestination();
      this.detachSinkElement(audio);
      audio.srcObject = this.sinkNode.stream;
      this.sinkElement = audio;
      output.disconnect();
      output.connect(this.sinkNode);
      this.sinkRouted = true;
      audio.play().catch(() => this.routeDirect());
    } catch {
      this.routeDirect();
    }
  }

  /** Only one element may render the sink stream; unbind any other. */
  private detachSinkElement(keep?: HTMLAudioElement): void {
    const bound = this.sinkElement;
    if (!bound || bound === keep) return;
    bound.srcObject = null;
    bound.pause();
    this.sinkElement = null;
  }

  /** Back to the speaker route; the keeper returns to its silent-WAV duty. */
  private routeDirect(): void {
    if (!this.sinkRouted) return;
    this.sinkRouted = false;
    const ctx = this.ctx;
    const output = this.output;
    if (ctx && output) {
      try {
        output.disconnect();
      } catch {
        /* already detached */
      }
      output.connect(ctx.destination);
    }
    const carrier = this.sinkElement;
    this.sinkElement = null;
    if (carrier) {
      carrier.srcObject = null;
      if (carrier === this.sessionKeeper) {
        // Back on silent-WAV duty. A rejected play (no gesture, or a load
        // aborted by the next Play's source switch — user-found as
        // comb-filtered playback after opening a new tune) KEEPS the
        // element: nulling it orphaned a carrier still bound to the stream,
        // and the next Play bound a fresh keeper beside it.
        carrier.src = SILENT_WAV;
        carrier.loop = true;
        carrier.play().catch(() => undefined);
      } else {
        carrier.pause();
      }
    }
  }

  /**
   * Some browsers start the context suspended until a user gesture, and iOS
   * additionally parks it as "interrupted" after a call / lock screen. Never
   * rejects.
   */
  async resume(): Promise<boolean> {
    try {
      if (this.contextClockStalled) {
        // A watchdog-stalled arrangement captures the old context in its
        // scheduler closure and cannot migrate. Stop it before rebuilding. A
        // fresh Play has already stopped the old transport, so its request
        // token remains valid and can continue on the replacement context.
        if (this.arrControl) this.stopAll();
        return await this.rebuildAudioContext();
      }
      const ctx = this.ensureContext();
      this.ensurePlaybackSession();
      if (ctx.state === 'suspended' || (ctx.state as string) === 'interrupted') await ctx.resume();
      if (ctx.state === 'running' && this.autoPausedForInterruption) this.resumePlayback();
      return ctx.state === 'running';
    } catch {
      /* no audio available / no user gesture yet */
      return false;
    }
  }

  /**
   * Tear down a route-corrupted graph and acquire the current output device
   * again. Intended for an explicit user gesture; the active transport stops
   * rather than trying to migrate already-scheduled nodes between contexts.
   */
  private async rebuildAudioContext(): Promise<boolean> {
    const old = this.ctx;
    if (old && this.contextStateListener) {
      old.removeEventListener('statechange', this.contextStateListener);
    }
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    this.contextStateListener = null;
    this.visibilityListener = null;
    this.autoPausedForInterruption = false;
    this.contextClockStalled = false;
    for (const voice of [...this.voices]) this.disposeVoice(voice);
    for (const { source, gain } of this.drumSources) {
      try {
        source.stop(0);
        source.disconnect();
        gain.disconnect();
      } catch {
        /* already ended */
      }
    }
    this.drumSources.clear();
    this.drum = null;
    this.drumGain = null;
    this.bassNoise = null;
    this.master = null;
    this.limiter = null;
    this.output = null;
    this.buses = {};
    this.clickBus = null;
    this.dryBus = null;
    this.masterCompressor = null;
    this.masterMakeup = null;
    this.appliedCompression = 0;
    this.compressionActive = false;
    this.reverbSend = null;
    this.reverbNode = null;
    this.reverbSource = null;
    this.sinkNode = null;
    this.sinkRouted = false;
    this.sinkElement = null;
    this.ctx = null;
    if (this.sessionKeeper) {
      this.sessionKeeper.pause();
      this.sessionKeeper.srcObject = null;
      this.sessionKeeper.src = '';
      this.sessionKeeper = null;
    }
    // Begin closing the corrupt route, but do not await it before acquiring the
    // replacement. Safari's transient user activation may be gone after an
    // await; create/resume the new graph synchronously inside the click stack.
    try {
      const closing = old?.close();
      if (closing) void closing.catch(() => {});
    } catch {
      /* already closed */
    }
    try {
      const ctx = this.ensureContext();
      this.ensurePlaybackSession();
      if (ctx.state === 'suspended' || (ctx.state as string) === 'interrupted') await ctx.resume();
      const current = this.ctx as AudioContext | null;
      return ctx === current && ctx.state === 'running';
    } catch {
      return false;
    }
  }

  async resetAudio(): Promise<boolean> {
    this.stopAll();
    return this.rebuildAudioContext();
  }

  private disposeVoice(voice: Voice): void {
    clearTimeout(voice.teardown);
    try {
      voice.oscillators.forEach((o) => o.disconnect());
      voice.sources.forEach((source) => source.disconnect());
      voice.nodes.forEach((n) => n.disconnect());
      voice.amp.disconnect();
    } catch {
      /* already torn down */
    }
    this.voices.delete(voice);
    for (const [pitch, current] of this.leadVoices) {
      if (current === voice) this.leadVoices.delete(pitch);
    }
    for (const [pitch, current] of this.pianoVoices) {
      if (current === voice) this.pianoVoices.delete(pitch);
    }
    if (voice.stringIndex >= 0 && this.stringVoices[voice.stringIndex] === voice) {
      this.stringVoices[voice.stringIndex] = null;
    }
  }

  /** Quickly damp a ringing voice starting at time `at` (a string being re-plucked). */
  private dampVoice(voice: Voice, at: number, release = 0.025): void {
    const ctx = this.ctx;
    if (!ctx || (voice.cutAt !== undefined && at >= voice.cutAt)) return;
    try {
      const gain = voice.amp.gain;
      // cancelAndHoldAtTime anchors the param at its in-progress value so a damp
      // landing mid-attack fades from the audible level rather than popping.
      if (typeof gain.cancelAndHoldAtTime === 'function') gain.cancelAndHoldAtTime(at);
      else gain.cancelScheduledValues(at);
      gain.setTargetAtTime(0.0001, at, release / 3);
      if (voice.cutAt !== undefined) gain.setValueAtTime(0, voice.cutAt);
      [...voice.oscillators, ...voice.sources].forEach((source) => {
        try {
          source.stop(Math.min(at + release + 0.04, voice.cutAt ?? Infinity));
        } catch {
          /* not started / already stopped */
        }
      });
    } catch {
      /* ignore */
    }
    clearTimeout(voice.teardown);
    voice.teardown = setTimeout(
      () => this.disposeVoice(voice),
      Math.max(0, (at - ctx.currentTime + release + 0.15) * 1000),
    );
  }

  /** A written rest ends the sound at the boundary, including pedal and sample tails. */
  private cutVoiceAt(voice: Voice, at: number): void {
    const ctx = this.ctx;
    if (!ctx || (voice.cutAt !== undefined && voice.cutAt <= at)) return;
    voice.cutAt = at;
    const fadeAt = Math.max(ctx.currentTime, voice.startTime, at - 0.005);
    try {
      const gain = voice.amp.gain;
      if (fadeAt < at) {
        if (typeof gain.cancelAndHoldAtTime === 'function') gain.cancelAndHoldAtTime(fadeAt);
        else gain.cancelScheduledValues(fadeAt);
        gain.linearRampToValueAtTime(0, at);
      } else {
        gain.cancelScheduledValues(at);
        gain.setValueAtTime(0, at);
      }
      for (const source of [...voice.oscillators, ...voice.sources]) source.stop(at);
    } catch {
      /* already ended */
    }
    clearTimeout(voice.teardown);
    voice.teardown = setTimeout(
      () => this.disposeVoice(voice),
      Math.max(0, (at - ctx.currentTime + 0.15) * 1000),
    );
  }

  private cutChannelAt(channel: 'comp' | 'bass' | 'drums', at: number): void {
    for (const voice of this.voices) {
      if (voice.channel === channel) this.cutVoiceAt(voice, at);
    }
  }

  /** All guitar paths share the selected tone and existing string damping. */
  private buildVoice(
    midi: number,
    when: number,
    duration: number,
    stringIndex: number,
    gain = 1,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const ctx = this.ensureContext();
    const sample =
      this.guitarSound === 'jazz-guitar' ? this.guitarSampler.nearest(midi, gain) : null;
    if (sample) {
      return this.buildOneShotVoice(
        midi,
        when,
        duration,
        gain,
        sample,
        { level: GUITAR_SAMPLE_LEVEL, stringIndex },
        channel,
        outlet,
      );
    }

    // Original additive pluck, also the offline/missing-sample fallback.
    const freq = midiToFreq(midi);
    const nyquist = ctx.sampleRate / 2;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.9;
    const openFreq = Math.min(freq * 6 + 1500, nyquist - 1000);
    const closeFreq = Math.min(freq * 2 + 300, nyquist - 1000);
    filter.frequency.setValueAtTime(openFreq, when);
    filter.frequency.exponentialRampToValueAtTime(closeFreq, when + duration * 0.9);

    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0001, 0.28 * gain), when + 0.006);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + duration);

    filter.connect(amp);
    amp.connect(outlet ?? this.busFor(channel));

    const oscillators: OscillatorNode[] = [];
    const partialGains: AudioNode[] = [];
    for (const partial of PARTIALS) {
      const partialFreq = freq * partial.ratio;
      if (partialFreq >= nyquist) continue;
      const osc = ctx.createOscillator();
      osc.type = partial.type;
      osc.frequency.value = partialFreq;
      const pg = ctx.createGain();
      pg.gain.setValueAtTime(partial.gain, when);
      pg.gain.exponentialRampToValueAtTime(
        Math.max(0.0001, partial.gain * 0.04),
        when + duration * (partial.ratio === 1 ? 1 : 0.5),
      );
      osc.connect(pg);
      pg.connect(filter);
      osc.start(when);
      osc.stop(when + duration + 0.05);
      oscillators.push(osc);
      partialGains.push(pg);
    }

    const voice: Voice = {
      channel,
      startTime: when,
      amp,
      oscillators,
      sources: [],
      nodes: [filter, ...partialGains],
      stringIndex,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (when - ctx.currentTime + duration + 0.2) * 1000),
      ),
    };
    this.voices.add(voice);
    return voice;
  }

  /** Pluck a specific string: damps that string's previous note, lets others ring. */
  private pluckString(
    stringIndex: number,
    midi: number,
    when: number,
    duration = DEFAULT_SUSTAIN,
    gain = 1,
    channel: Outlet = 'direct',
  ): void {
    const prev = this.stringVoices[stringIndex];
    if (prev) this.dampVoice(prev, when);
    this.stringVoices[stringIndex] = this.buildVoice(
      midi,
      when,
      duration,
      stringIndex,
      gain,
      channel,
    );
  }

  // A short white-noise burst reused for every bass pluck's attack transient
  // (the finger-on-string + fingerboard "thh"). Built once and cached.
  private bassNoise: AudioBuffer | null = null;
  private getBassNoise(ctx: AudioContext): AudioBuffer {
    if (this.bassNoise) return this.bassNoise;
    const len = Math.max(1, Math.floor(ctx.sampleRate * 0.08));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.bassNoise = buf;
    return buf;
  }

  /**
   * A pizzicato double-bass voice (distinct from the guitar pluck): a round sine
   * fundamental with quick-dying harmonics through a warm, fast-closing lowpass,
   * a subtle downward pitch settle (the string's "give"), and a parallel filtered
   * noise-burst attack — the finger/fingerboard thump that makes pizz read as
   * acoustic bass, not a synth.
   */
  private buildBassVoice(
    midi: number,
    when: number,
    duration: number,
    gain = 1,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const ctx = this.ensureContext();
    const freq = midiToFreq(midi);
    const nyquist = ctx.sampleRate / 2;

    // Woody body lowpass: modest at the attack, closing low and fast.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.7;
    filter.frequency.setValueAtTime(Math.min(freq * 5 + 700, nyquist - 1000), when);
    filter.frequency.exponentialRampToValueAtTime(
      Math.min(freq * 2 + 120, nyquist - 1000),
      when + 0.18,
    );

    // Punchy start settling into a warm sustain tail.
    const amp = ctx.createGain();
    const peak = Math.max(0.0001, 0.34 * gain);
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(peak, when + Math.min(0.008, duration * 0.2));
    amp.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, peak * 0.5),
      when + Math.min(0.16, duration * 0.65),
    );
    amp.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    filter.connect(amp);
    amp.connect(outlet ?? this.busFor(channel));

    const oscillators: OscillatorNode[] = [];
    const nodes: AudioNode[] = [filter];
    for (const p of BASS_PARTIALS) {
      const pf = freq * p.ratio;
      if (pf >= nyquist) continue;
      const osc = ctx.createOscillator();
      osc.type = p.type;
      // ~14 cents high, settling to pitch over 35ms — the pizzicato "give".
      osc.frequency.setValueAtTime(pf * 1.008, when);
      osc.frequency.exponentialRampToValueAtTime(pf, when + 0.035);
      const pg = ctx.createGain();
      pg.gain.setValueAtTime(p.gain, when);
      pg.gain.exponentialRampToValueAtTime(
        Math.max(0.0001, p.gain * 0.03),
        when + duration * p.decay,
      );
      osc.connect(pg);
      pg.connect(filter);
      osc.start(when);
      osc.stop(when + duration + 0.05);
      oscillators.push(osc);
      nodes.push(pg);
    }

    // Attack thump: a ~50ms bandpassed noise burst (finger + fingerboard click).
    const noise = ctx.createBufferSource();
    noise.buffer = this.getBassNoise(ctx);
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.value = Math.min(freq * 3 + 450, 2600);
    nf.Q.value = 0.8;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(Math.max(0.0001, 0.13 * gain), when);
    ng.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
    noise.connect(nf);
    nf.connect(ng);
    ng.connect(outlet ?? this.busFor(channel));
    noise.start(when);
    noise.stop(when + 0.09);
    nodes.push(noise, nf, ng);

    const voice: Voice = {
      channel,
      startTime: when,
      amp,
      oscillators,
      sources: [noise],
      nodes,
      stringIndex: 6,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (when - ctx.currentTime + duration + 0.2) * 1000),
      ),
    };
    this.voices.add(voice);
    return voice;
  }

  /**
   * Pluck the lead voice — POLYPHONIC PER PITCH (Aug 2026, whole-part
   * playback): the played part may carry several written voices at once (a
   * grand-staff import's dyads over its bassline), so simultaneous pitches
   * coexist instead of cutting one another off. Re-striking a pitch that is
   * still ringing damps its previous instance — that is what a re-attack on
   * one string is — and durations bound every voice, so nothing rings past
   * its written value. Kept clear of comp strums and damping, like the old
   * single slot 7 was.
   */
  private leadVoices = new Map<number, Voice>();
  private buildLeadVoice(
    midi: number,
    when: number,
    duration: number,
    gain = 1,
    stringIndex = 7,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    if (this.leadSound === 'piano') {
      // The head on the piano — the trio's right hand. Built directly rather
      // than through strikePiano so the melody never joins the comp's
      // chord-release bookkeeping (pianoSounding is the left hand's).
      const grand = this.nearestPianoSample(midi, gain);
      return grand
        ? this.buildOneShotVoice(
            midi,
            when,
            duration,
            gain,
            grand,
            { level: PIANO_SAMPLE_LEVEL },
            channel,
            outlet,
          )
        : this.buildPianoVoice(midi, when, duration, gain, channel, outlet);
    }
    const set =
      this.leadSound === 'guitar'
        ? null
        : getInstrumentSamples(this.ensureContext(), this.leadSound);
    const sample = set ? selectInstrumentSample(set, midi) : null;
    return sample
      ? this.buildSampledInstrumentVoice(
          sample,
          when,
          duration,
          gain,
          stringIndex,
          this.leadSound === 'tenor-sax',
          channel,
          outlet,
        )
      : this.buildVoice(midi, when, duration, stringIndex, gain, channel, outlet);
  }

  private pluckLead(
    midi: number,
    when: number,
    duration: number,
    gain = 1,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const prev = this.leadVoices.get(midi);
    if (prev) this.dampVoice(prev, when);
    const voice = this.buildLeadVoice(midi, when, duration, gain, 7, channel, outlet);
    this.leadVoices.set(midi, voice);
    return voice;
  }

  /** Keep dynamic automation separate from the instrument's attack/decay. */
  private scheduleExpressiveVoice(
    when: number,
    duration: number,
    channel: Outlet,
    options: NoteExpressionOptions | undefined,
    build: (length: number, gain: number, outlet?: AudioNode) => Voice,
  ): Voice {
    const gate = options?.articulationGate;
    const length = duration * (gate !== undefined && Number.isFinite(gate) ? Math.max(0, gate) : 1);
    const points = options?.expression
      ?.filter((point) => Number.isFinite(point.offsetSeconds) && Number.isFinite(point.gain))
      .map((point) => ({ ...point, gain: Math.max(0, point.gain) }))
      .sort((a, b) => a.offsetSeconds - b.offsetSeconds);
    let expression: GainNode | undefined;
    if (points?.length) {
      expression = this.ensureContext().createGain();
      expression.connect(this.busFor(channel));
      const valueAt = (offset: number) => {
        let previous = { offsetSeconds: 0, gain: 1 };
        for (const point of points) {
          if (point.offsetSeconds > offset) {
            if (point.ramp && point.offsetSeconds > previous.offsetSeconds) {
              const ratio =
                (offset - previous.offsetSeconds) / (point.offsetSeconds - previous.offsetSeconds);
              return previous.gain + (point.gain - previous.gain) * Math.max(0, ratio);
            }
            return previous.gain;
          }
          previous = point;
        }
        return previous.gain;
      };
      expression.gain.setValueAtTime(valueAt(0), when);
      for (const point of points) {
        if (point.offsetSeconds <= 0) continue;
        const offset = Math.min(length, point.offsetSeconds);
        const gain = point.offsetSeconds > length ? valueAt(length) : point.gain;
        if (point.ramp) expression.gain.linearRampToValueAtTime(gain, when + offset);
        else if (point.offsetSeconds <= length) expression.gain.setValueAtTime(gain, when + offset);
        if (point.offsetSeconds >= length) break;
      }
    }
    const voice = build(Math.max(0.001, length), options?.velocity ?? 1, expression);
    if (expression) voice.nodes.push(expression);
    // A written note-off bounds every sound, including piano ring-out, reed
    // release and the bass attack noise; other pitches keep their own gates.
    if (options) this.cutVoiceAt(voice, when + Math.max(0, length));
    return voice;
  }

  private playMelodyAt(
    note: BassTrackNote,
    when: number,
    secondsPerBeat: number,
    instrument: ArrangementOptions['melodyInstrument'],
    elapsedBeats = 0,
    /** The note's length as it sounds, when swing has moved either end. */
    lengthBeats = note.durationBeats,
    /**
     * Seconds the onset sits late or early (melodyOnsetShift: a swung
     * downbeat laid back, the drift). The note still ends where it would
     * have, so a late downbeat is a shorter note, never one running into the
     * offbeat after it.
     */
    placeSeconds = 0,
  ): void {
    const gate = note.articulationGate;
    const soundingBeats =
      lengthBeats * (gate !== undefined && Number.isFinite(gate) ? Math.max(0, gate) : 0.98);
    if (elapsedBeats >= soundingBeats - 1e-6) return;
    // A restrike mid-note is already late by definition and stays where it is.
    const place = elapsedBeats <= 0 ? placeSeconds : 0;
    when += place;
    const prev = this.leadVoices.get(note.midi);
    if (prev) this.dampVoice(prev, when);
    const level =
      elapsedBeats <= 0
        ? (note.velocity ?? 1) * velocityDrift(this.liveOptions?.feel, 'melody', note.beat)
        : note.velocity;
    const voice = this.scheduleExpressiveVoice(
      when,
      Math.max(0.02 * secondsPerBeat, (soundingBeats - elapsedBeats) * secondsPerBeat - place),
      'melody',
      {
        velocity: level,
        expression: note.expression?.map((point) => ({
          offsetSeconds: (point.beatOffset - elapsedBeats) * secondsPerBeat,
          gain: point.gain,
          ramp: point.ramp,
        })),
      },
      (length, gain, outlet) => {
        // The line owns its voices independently of comp/bass, even when both
        // parts use the same instrument or sound the same pitch.
        if (instrument === 'piano') {
          const sample = this.nearestPianoSample(note.midi, gain);
          return sample
            ? this.buildOneShotVoice(
                note.midi,
                when,
                length,
                gain,
                sample,
                { level: PIANO_SAMPLE_LEVEL },
                'melody',
                outlet,
              )
            : this.buildPianoVoice(note.midi, when, length, gain, 'melody', outlet);
        }
        if (instrument === 'bass')
          return this.buildBassInstrumentVoice(note.midi, when, length, gain, 'melody', outlet);
        if (instrument === 'guitar')
          return this.buildVoice(note.midi, when, length, -1, gain, 'melody', outlet);
        return this.buildLeadVoice(note.midi, when, length, gain, 7, 'melody', outlet);
      },
    );
    this.leadVoices.set(note.midi, voice);
  }

  /** Recordings retain their attack, sustain loop, tuning and envelope. The
   * release fits inside the written duration, including a long tied note. */
  private buildSampledInstrumentVoice(
    selection: NonNullable<ReturnType<typeof selectInstrumentSample>>,
    when: number,
    duration: number,
    gain: number,
    stringIndex: number,
    reed = false,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const ctx = this.ensureContext();
    const { sample, playbackRate } = selection;
    // The recording speaks a little after it starts (a tenor's breath about
    // 25 ms, an upright's lowest zone 20): start it that much early so the
    // note is HEARD on its beat (docs/swing-plan.md §4.3), never in the past.
    const lead = Math.min(0.06, Math.max(0, (sample.onsetSeconds ?? 0) / playbackRate));
    const start = Math.max(ctx.currentTime, when - lead);
    const skipInto = (start - (when - lead)) * playbackRate;
    const length = Math.max(0.01, duration + (when - start));
    when = start;
    const source = ctx.createBufferSource();
    source.buffer = sample.buffer;
    source.playbackRate.value = playbackRate;
    if (sample.loopEnd > sample.loopStart) {
      source.loop = true;
      source.loopStart = sample.loopStart;
      source.loopEnd = sample.loopEnd;
    }

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.7;
    filter.frequency.value = Math.min(sample.filterHz, ctx.sampleRate * 0.45);
    const amp = ctx.createGain();
    const peak = Math.max(0.0001, gain * sample.gain);
    const attack = Math.min(sample.attackSeconds, length * 0.2);
    const release = Math.min(sample.releaseSeconds, length * 0.3);
    const releaseAt = when + length - release;
    const decay = Math.max(0.001, sample.decaySeconds);
    const decayAt = Math.min(when + attack + decay, releaseAt);
    // A short note should not rush through the entire decay of a long bass
    // pluck. Evaluate the same envelope at its earlier note-off instead.
    const decayFraction = Math.min(1, Math.max(0, (decayAt - when - attack) / decay));
    const sustain = peak * Math.pow(Math.max(0.0001, sample.sustainLevel), decayFraction);
    amp.gain.setValueAtTime(0, when);
    amp.gain.linearRampToValueAtTime(peak, when + attack);
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0001, sustain), decayAt);
    amp.gain.setValueAtTime(Math.max(0.0001, sustain), releaseAt);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + length);
    source.connect(filter);
    filter.connect(amp);
    amp.connect(outlet ?? this.busFor(channel));

    const oscillators: OscillatorNode[] = [];
    const nodes: AudioNode[] = [filter];
    if (reed && length > 0.5 && source.detune) {
      const vibrato = ctx.createOscillator();
      vibrato.type = 'sine';
      vibrato.frequency.value = 5;
      const depth = ctx.createGain();
      depth.gain.setValueAtTime(0, when);
      depth.gain.setValueAtTime(0, when + 0.28);
      depth.gain.linearRampToValueAtTime(9, when + Math.min(0.65, length * 0.8));
      vibrato.connect(depth);
      depth.connect(source.detune);
      vibrato.start(when);
      vibrato.stop(when + length + 0.02);
      oscillators.push(vibrato);
      nodes.push(depth);
    }
    if (skipInto > 1e-6) source.start(when, skipInto);
    else source.start(when);
    source.stop(when + length + 0.02);
    const voice: Voice = {
      channel,
      startTime: when,
      amp,
      oscillators,
      sources: [source],
      nodes,
      stringIndex,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (when - ctx.currentTime + length + 0.1) * 1000),
      ),
    };
    this.voices.add(voice);
    return voice;
  }

  /**
   * A struck-piano voice, distinct from both plucked timbres: inharmonic
   * partials (stretched by √(1+Bn²) — the stiff-string tell), a two-stage
   * prompt-sound/aftersound decay, a detuned unison twin on the fundamental,
   * a register-following lowpass and a short hammer-felt noise tick. Low
   * notes ring longer and carry more partials, exactly as the instrument's
   * strings do.
   */
  private buildPianoVoice(
    midi: number,
    when: number,
    duration: number,
    gain = 1,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const ctx = this.ensureContext();
    const f0 = midiToFreq(midi);
    const nyquist = ctx.sampleRate / 2;
    // Register-scaled ring: bass strings sustain far longer than treble ones.
    const ring = duration * (midi < 48 ? 1.5 : midi < 60 ? 1.2 : midi > 84 ? 0.6 : 1);

    // Soundboard brightness follows the register; the hammer's brilliance
    // fades as the note rings.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 0.5;
    filter.frequency.setValueAtTime(Math.min(f0 * 9 + 2200, nyquist - 1000), when);
    filter.frequency.exponentialRampToValueAtTime(
      Math.min(f0 * 2.5 + 400, nyquist - 1000),
      when + Math.min(ring * 0.7, 1.4),
    );

    // Hammer-fast attack, then the double decay: the prompt sound falls to
    // ~55% inside 110ms, the aftersound rides a long shallow slope and a
    // short final release closes the note. The amp OWNS the note's fade —
    // partial gains below only reshape the timbre — because a decay in both
    // places multiplies into a tail that dies 40dB early (measured on the
    // first offline render).
    const amp = ctx.createGain();
    const peak = Math.max(0.0001, 0.3 * gain);
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.exponentialRampToValueAtTime(peak, when + 0.004);
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak * 0.55), when + 0.11);
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak * 0.06), when + ring * 0.9);
    amp.gain.exponentialRampToValueAtTime(0.0001, when + ring);
    filter.connect(amp);
    amp.connect(outlet ?? this.busFor(channel));

    const oscillators: OscillatorNode[] = [];
    const nodes: AudioNode[] = [filter];
    const B = PIANO_INHARMONICITY + Math.max(0, 48 - midi) * 0.00002;
    const count = midi < 48 ? 8 : midi < 65 ? 6 : midi < 80 ? 5 : 4;
    const spawn = (freq: number, gainValue: number, decaySeconds: number, floor: number) => {
      if (freq >= nyquist) return;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const pg = ctx.createGain();
      pg.gain.setValueAtTime(gainValue, when);
      pg.gain.exponentialRampToValueAtTime(
        Math.max(0.0001, gainValue * floor),
        when + Math.max(0.05, decaySeconds),
      );
      osc.connect(pg);
      pg.connect(filter);
      osc.start(when);
      osc.stop(when + ring + 0.05);
      oscillators.push(osc);
      nodes.push(pg);
    };
    for (let n = 1; n <= count; n += 1) {
      const stretched = f0 * n * Math.sqrt(1 + B * n * n);
      // A slightly favoured 2nd partial keeps the mid register from hollowing.
      const partialGain = n === 1 ? 1 : Math.pow(n, -1.6) * (n === 2 ? 1.25 : 1);
      // The fundamental sustains (the amp fades the note); overtones darken
      // the tone as the note rings by dying off on their own, faster with n.
      spawn(
        stretched,
        partialGain,
        n === 1 ? ring : ring * Math.pow(n, -0.7),
        n === 1 ? 0.4 : 0.02,
      );
    }
    // The unison strings' slow beat: a twin fundamental a couple of cents up.
    spawn(f0 * 1.0015, 0.55, ring, 0.4);

    // Hammer felt on string: a very short mid-band noise tick.
    const noise = ctx.createBufferSource();
    noise.buffer = this.getBassNoise(ctx);
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.value = Math.min(f0 * 6 + 800, 5000);
    nf.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(Math.max(0.0001, 0.06 * gain), when);
    ng.gain.exponentialRampToValueAtTime(0.0001, when + 0.014);
    noise.connect(nf);
    nf.connect(ng);
    ng.connect(outlet ?? this.busFor(channel));
    noise.start(when);
    noise.stop(when + 0.03);
    nodes.push(noise, nf, ng);

    const voice: Voice = {
      channel,
      startTime: when,
      amp,
      oscillators,
      sources: [noise],
      nodes,
      stringIndex: -1,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (when - ctx.currentTime + ring + 0.2) * 1000),
      ),
    };
    this.voices.add(voice);
    return voice;
  }

  // ---- Sampled piano (#236): Salamander grand behind the same seam --------
  // Three velocity layers of the Salamander V3 recordings, every minor third
  // A0–C8 (public/piano/, CC-BY — see the README there; built by
  // scripts/build-piano-samples.mjs), pitch-shifted at most ±1 semitone to the
  // nearest recording. Every file is levelled to one register curve, so a
  // LAYER is a timbre and the voice's gain is the loudness: a soft comp hit
  // sounds a softer-struck string, not merely a quieter one. The layer that
  // full gain plays (held chords, taps, accents) loads first and the promise
  // resolves on it; the softer layers follow in the background, the nearest
  // loaded layer answering for each until it lands. The additive synth stays
  // as the INSTANT fallback: nothing awaits the samples except
  // playArrangement's preload (the same rule as drum loops — a cold fetch
  // must not change the piano's voice halfway through the form).
  private pianoSampler = new LayeredSampler('piano');

  /** Fetch + decode the piano samples once; safe to call repeatedly. Failure
   * is silent — the synth voice simply continues. */
  preloadPianoSamples(): Promise<void> {
    try {
      return this.pianoSampler.load(this.ensureContext());
    } catch {
      return Promise.resolve();
    }
  }

  /** The recording nearest `midi` in the layer `gain` asks for. */
  private nearestPianoSample(midi: number, gain = 1): LayeredSample | null {
    return this.pianoSampler.nearest(midi, gain);
  }

  /** A recorded one-shot note (the piano, the archtop, the upright bass),
   * pitch-shifted to `midi`. The sample carries its own attack and decay. A
   * finite `duration` is when the key comes up or the string is stopped: the
   * amp holds, then damps into it, so re-strikes and written note-offs behave
   * exactly as they do on the synth voices. An infinite one (a comp chord on
   * the piano) rings until something damps it or the recording runs out.
   * `anchor` starts the recording early by its attack (never in the past), so
   * the note is HEARD at `when` (docs/swing-plan.md §4.3): the bass, whose
   * placement against the kit is measured in milliseconds. */
  private buildOneShotVoice(
    midi: number,
    when: number,
    duration: number,
    gain: number,
    sample: LayeredSample,
    {
      level: fullLevel,
      stringIndex = -1,
      anchor = false,
    }: { level: number; stringIndex?: number; anchor?: boolean },
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const ctx = this.ensureContext();
    const source = ctx.createBufferSource();
    source.buffer = sample.buffer;
    const rate = Math.pow(2, (midi - sample.base) / 12);
    source.playbackRate.value = rate;
    const lead = anchor ? Math.min(0.015, Math.max(0, (sample.attack ?? 0) / rate)) : 0;
    const start = Math.max(ctx.currentTime, when - lead);
    // Buffer seconds already gone when a note due now cannot start early.
    const skipInto = (start - (when - lead)) * rate;
    // What the recording holds after its onset, in real seconds at this rate.
    const recorded = Math.max(0, (sample.buffer.duration - sample.offset - skipInto) / rate);
    const lifetime = Math.min(duration + (when - start), recorded);

    const amp = ctx.createGain();
    const level = Math.max(0.0001, fullLevel * gain);
    amp.gain.setValueAtTime(level, start);
    if (duration + (when - start) < recorded) {
      amp.gain.setValueAtTime(level, when + duration * 0.8);
      amp.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    }
    source.connect(amp);
    amp.connect(outlet ?? this.busFor(channel));
    // Start where the hammer lands, not where the file does.
    source.start(start, sample.offset + skipInto);
    source.stop(start + lifetime + 0.1);

    const voice: Voice = {
      channel,
      startTime: start,
      amp,
      oscillators: [],
      sources: [source],
      nodes: [],
      stringIndex,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (start - ctx.currentTime + lifetime + 0.2) * 1000),
      ),
    };
    this.voices.add(voice);
    return voice;
  }

  /** Strike a piano note — polyphonic per pitch; re-striking a ringing pitch
   * damps its previous instance, like re-attacking one string. Sampled when
   * the Salamander set has loaded, synthesized until then. */
  private pianoVoices = new Map<number, Voice>();
  private strikePiano(
    midi: number,
    when: number,
    duration: number,
    gain = 1,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const prev = this.pianoVoices.get(midi);
    if (prev) this.dampVoice(prev, when);
    const sample = this.nearestPianoSample(midi, gain);
    // The synth's decay is shaped BY its duration, so "ring until damped"
    // means its usual ring-out there.
    const voice = sample
      ? this.buildOneShotVoice(
          midi,
          when,
          duration,
          gain,
          sample,
          { level: PIANO_SAMPLE_LEVEL },
          channel,
          outlet,
        )
      : this.buildPianoVoice(
          midi,
          when,
          Number.isFinite(duration) ? duration : DEFAULT_SUSTAIN,
          gain,
          channel,
          outlet,
        );
    this.pianoVoices.set(midi, voice);
    return voice;
  }

  private buildAuthoredCompVoice(note: AuthoredCompTrackNote, when: number, duration: number): Voice {
    return note.instrument === 'piano'
      ? this.strikePiano(note.midi, when, duration, note.velocity ?? 1, 'comp')
      : this.buildVoice(note.midi, when, duration, -1, note.velocity ?? 1, 'comp');
  }

  private buildBassInstrumentVoice(
    midi: number,
    when: number,
    duration: number,
    gain: number,
    channel: Outlet,
    outlet?: AudioNode,
  ): Voice {
    // MIDI is already sounding pitch. Authored bass can use an extension or
    // five-string register below E1; the generator enforces its own range.
    if (this.bassSound === 'upright') {
      // The layer follows the note's gain: a ghost, skip or drop the soft
      // pluck, an eased beat the middle one, a full beat the hard one.
      const recorded = this.bassSampler.nearest(midi, gain);
      return recorded
        ? this.buildOneShotVoice(
            midi,
            when,
            duration,
            gain,
            recorded,
            { level: BASS_SAMPLE_LEVEL, stringIndex: 6, anchor: true },
            channel,
            outlet,
          )
        : this.buildBassVoice(midi, when, duration, gain, channel, outlet);
    }
    const set =
      this.bassSound === 'original'
        ? null
        : getInstrumentSamples(this.ensureContext(), this.bassSound);
    const sample = set ? selectInstrumentSample(set, midi) : null;
    return sample
      ? this.buildSampledInstrumentVoice(sample, when, duration, gain, 6, false, channel, outlet)
      : this.buildBassVoice(midi, when, duration, gain, channel, outlet);
  }

  /** Pluck the monophonic backing bass string (slot 6). */
  private pluckBass(
    midi: number,
    when: number,
    duration: number,
    gain = 1,
    channel: Outlet = 'direct',
    outlet?: AudioNode,
  ): Voice {
    const prev = this.stringVoices[6];
    if (prev) this.dampVoice(prev, when);
    const voice = this.buildBassInstrumentVoice(midi, when, duration, gain, channel, outlet);
    this.stringVoices[6] = voice;
    return voice;
  }

  /** A stable key identifying a chord voicing (physical layout + active frets). */
  private chordSignature(fingering: Fingering, stringSet: StringSet): string {
    // Keep the legacy string-set argument at the public boundary, but trust
    // the fingering's resolved layout for the physical strings. Drop 3's
    // "middle" layout, for example, is E-D-G-B rather than A-D-G-B.
    void stringSet;
    // A catalogue family can use different string masks within one layout.
    // Keep silent positions: 8-x-9-9-8-x and x-8-9-9-8-x are different
    // chords, and changing between them must release the old bass string.
    return `${fingering.stringLayout}:${fingering.frets.map((fret) => fret ?? 'x').join(',')}`;
  }

  /** Damp every ringing guitar string (0-5); the bass slot (6) is left alone. */
  private muteStrings(at: number): void {
    for (let s = 0; s < 6; s++) {
      const voice = this.stringVoices[s];
      if (voice) {
        this.dampVoice(voice, at);
        this.stringVoices[s] = null;
      }
    }
  }

  /**
   * Called as a chord is about to sound: if it differs from the chord currently
   * ringing, mute all the previous chord's strings (a clean chord change). The
   * same chord re-triggered keeps the per-string ring-out behaviour.
   */
  private beforeChord(fingering: Fingering, stringSet: StringSet, at: number): void {
    const sig = this.chordSignature(fingering, stringSet);
    if (this.lastChordSig !== null && this.lastChordSig !== sig) this.muteStrings(at);
    this.lastChordSig = sig;
  }

  /**
   * Strike one chord of the arrangement at `when` (internal). `gain` scales
   * loudness (comping offbeats sit under downbeats) and `releaseAfterSec`,
   * when set, damps the voices — a staccato stab.
   *
   * The chord is described by a VOICING, not a fingering, so the comp channel
   * can be a keyboard (#236): `pianoMidis` present means strike those pitches
   * on the piano voice, otherwise pluck the grip. Every arrangement path —
   * comp figures, held chords, the resume re-strike, chromatic approach stabs
   * — goes through here, so each one follows the instrument without knowing
   * the instrument exists.
   */
  private strumAt(
    voicing: ChordStrike,
    when: number,
    strum = 0.045,
    gain = 1,
    releaseAfterSec?: number,
    channel: Outlet = 'comp',
  ): void {
    if (voicing.pianoMidis?.length) {
      // A section changing the comp from guitar to piano: the guitarist stops
      // as the pianist comes in, rather than the last grip ringing on under it.
      if (channel === 'comp' && this.lastChordSig !== null) {
        this.muteStrings(when);
        this.lastChordSig = null;
      }
      // The pianist's spread is a fraction of a guitar's, so ordinary strums
      // cap at 12ms — but an EXPLICIT roll (the comp engine's expressive
      // sweep) passes through uncapped.
      const roll = voicing.pianoRollSec ?? Math.min(strum, 0.012);
      this.strikePianoChord(
        voicing.pianoMidis,
        when,
        roll,
        gain,
        releaseAfterSec,
        voicing.pianoPartial,
        channel,
      );
      return;
    }
    // And back: a comp chord rings until the hand lifts, and a guitar strum is
    // not a piano chord, so the hand lifts here or it rings through the section.
    if (channel === 'comp' && this.pianoSounding.size) this.liftPianoHand(when);
    const { fingering, stringSet } = voicing;
    this.beforeChord(fingering, stringSet, when);
    let voice = 0;
    for (const stringIndex of activeStringsForFingering(fingering)) {
      const fret = fingering.frets[stringIndex];
      if (fret === null || fret === undefined) continue;
      const at = when + voice * strum;
      this.pluckString(
        stringIndex,
        frettedMidi(stringIndex, fret),
        at,
        DEFAULT_SUSTAIN,
        gain,
        channel,
      );
      if (releaseAfterSec !== undefined) {
        const plucked = this.stringVoices[stringIndex];
        if (plucked) this.dampVoice(plucked, when + releaseAfterSec, 0.06);
      }
      voice += 1;
    }
  }

  /**
   * Strike a piano chord: every pitch of the PREVIOUS chord that this one does
   * not hold is released, which is what lifting the hand does — a pianist does
   * not leave the old chord ringing under the new one, and per-pitch damping
   * alone (strikePiano) would only catch the notes that happen to repeat.
   * Common tones are deliberately left alone so a voice-led line rings through
   * its held notes, which is the whole point of voicing it that way.
   *
   * The roll is much tighter than a guitar strum — a pianist's spread, not a
   * pick across six strings — and a chord's own repeated pitch still re-strikes
   * because strikePiano damps per pitch.
   */
  private pianoSounding = new Set<number>();
  private strikePianoChord(
    midis: number[],
    when: number,
    strum: number,
    gain: number,
    releaseAfterSec?: number,
    partial = false,
    channel: Outlet = 'comp',
  ): void {
    if (partial) {
      // A one-hand answer over the other hand's ringing tones: release
      // NOTHING — the hand that is not playing has not moved. The struck
      // pitches join the sounding set so the next full chord change still
      // lifts them correctly; a re-struck ringing pitch is damped per pitch
      // by strikePiano below, same as always.
      for (const midi of midis) this.pianoSounding.add(midi);
    } else {
      const next = new Set(midis);
      for (const midi of this.pianoSounding) {
        if (next.has(midi)) continue;
        const voice = this.pianoVoices.get(midi);
        if (voice)
          this.dampVoice(voice, when + PIANO_PEDAL.changeOverlap, PIANO_PEDAL.changeRelease);
        this.pianoVoices.delete(midi);
      }
      this.pianoSounding = next;
    }
    // The cap lives at strumAt (an explicit expressive roll passes uncapped);
    // here the stagger is used as given.
    const roll = strum;
    midis.forEach((midi, index) => {
      const at = when + index * roll;
      // A chord rings until the hand lifts (the next chord, a stab's lift,
      // Stop) — a fixed three seconds cut every long chord short.
      this.strikePiano(midi, at, Infinity, gain, channel);
      if (releaseAfterSec !== undefined) {
        // The lift is on time; the pedal makes the fade a piano's.
        const struck = this.pianoVoices.get(midi);
        if (struck) this.dampVoice(struck, when + releaseAfterSec, PIANO_PEDAL.stabRelease);
      }
    });
  }

  /** Lift the comp's piano chord as a chord change does: the pedal overlaps, then it fades. */
  private liftPianoHand(when: number): void {
    for (const midi of this.pianoSounding) {
      const voice = this.pianoVoices.get(midi);
      if (voice) this.dampVoice(voice, when + PIANO_PEDAL.changeOverlap, PIANO_PEDAL.changeRelease);
      this.pianoVoices.delete(midi);
    }
    this.pianoSounding.clear();
  }

  /** Silence every ringing piano note (the piano's `muteStrings`). */
  private mutePiano(at: number): void {
    for (const midi of this.pianoSounding) {
      const voice = this.pianoVoices.get(midi);
      if (voice) this.dampVoice(voice, at, 0.05);
      this.pianoVoices.delete(midi);
    }
    this.pianoSounding.clear();
  }

  /** Cancel the active transport and every source it owns. */
  private cancelSequence(): void {
    this.seqToken += 1;
    if (this.seqTimer !== null) {
      clearTimeout(this.seqTimer);
      this.seqTimer = null;
    }
    this.activePump = null;
    this.wantSinkRoute = false;
    this.routeDirect();
    this.liveOptions = null;
    this.liveEvents = null;
    this.arrControl = null;
    this.resumeRestrike = false;
    this.stopDrums(undefined, 0.015);
    this.emitBeat(-1);
    this.emitPass(0);
    this.emitPendingChorus(null);
    this.setSeqPlaying(false);
    this.setTransport('idle');
  }

  /**
   * Freeze the running arrangement at the next beat boundary. Ringing strings
   * are damped; the scheduler's place is kept so resume() picks up exactly
   * where it left off. The sequence still counts as "playing" while paused.
   */
  private pauseTransport(interrupted: boolean): void {
    if (this.transport !== 'playing' || !this.arrControl) return;
    if (!interrupted) this.autoPausedForInterruption = false;
    // The lookahead may already have created nodes for a future beat. Rewind
    // the scheduler cursor to the first not-yet-audible beat before silencing
    // those nodes, so resume neither skips it nor layers a duplicate.
    this.arrControl.freeze();
    if (this.seqTimer !== null) {
      clearTimeout(this.seqTimer);
      this.seqTimer = null;
    }
    if (this.ctx) {
      const now = this.ctx.currentTime;
      // Include metronome and any additional voices already placed inside the
      // lookahead window, not just the latest voice for each instrument string.
      for (const voice of [...this.voices]) this.dampVoice(voice, now);
      this.stringVoices = [null, null, null, null, null, null, null];
      this.leadVoices.clear();
      this.mutePiano(now);
    }
    this.stopDrums();
    this.lastChordSig = null;
    this.setTransport('paused');
  }

  pause(): void {
    this.pauseTransport(false);
  }

  /** Continue a paused arrangement from the beat it stopped before. */
  resumePlayback(): void {
    const control = this.arrControl;
    if (this.transport !== 'paused' || !control) return;
    if (this.contextClockStalled) {
      // The old scheduler is bound to a dead audio clock. Resetting is safer
      // than repeatedly re-entering the watchdog; the transport becomes idle
      // and the next Play starts normally on the replacement route.
      void this.resetAudio();
      return;
    }
    const continuePlayback = () => {
      if (this.transport !== 'paused' || this.arrControl !== control) return;
      this.autoPausedForInterruption = false;
      this.setTransport('playing');
      this.resumeRestrike = true;
      control.resetClock();
      control.schedule(control.nextAbs);
    };
    const state = this.ctx?.state as string | undefined;
    if (!this.ctx || state === 'running') {
      continuePlayback();
      return;
    }
    void this.resume().then((ready) => {
      if (ready && (this.ctx?.state as string | undefined) === 'running') continuePlayback();
    });
  }

  /**
   * Update the running arrangement's options live (tempo, metronome, bass line,
   * bass solo, loop). A no-op when nothing is playing — changes then apply on
   * the next Play.
   */
  /**
   * Replace live playback options mid-arrangement (#174's atomic-swap rule).
   *
   * One synchronous `Object.assign` is the whole mechanism, and it is enough:
   * the scheduler reads `liveOptions` once per beat window, so it can never
   * observe half of an update. A cue-mapping change recomputes feel, bass,
   * comp and drums together and lands them in one mutation — a mixed
   * old/new timeline is not representable.
   *
   * THE DOCUMENTED BOUNDARY is the next beat window: beats already committed
   * to the ~250ms lookahead sound with the realization they were scheduled
   * under. That is inherent to a lookahead scheduler, and it is the boundary
   * every other live control (tempo, style, mixer) already uses.
   */
  setArrangementOptions(patch: Partial<ArrangementOptions>): void {
    if (!this.liveOptions) return;
    // Options computed for a form that has not landed yet land with it: the
    // tracks are keyed to ITS beats, not the ones still being scheduled.
    if (this.arrControl?.hasPendingForm()) this.arrControl.reform(null, patch);
    else Object.assign(this.liveOptions, patch);
    // A new mapping can reference a loop that was not reachable at Play, so
    // was never preloaded. Warm it now rather than let the boundary arrive
    // with no buffer and play silence until the fetch lands. Same for a live
    // kit switch (patch.drums): the url-changed branch re-anchors phase, but
    // it needs the bytes.
    let bpm = this.liveOptions.bpm;
    let nextBpm: number | undefined;
    try {
      const resolved = this.liveOptions.bpmForPass?.(Math.max(1, this.lastPass));
      if (resolved !== undefined && Number.isFinite(resolved)) bpm = resolved;
      const next = this.liveOptions.bpmForPass?.(Math.max(1, this.lastPass) + 1);
      if (next !== undefined && Number.isFinite(next)) nextBpm = Math.max(20, next);
    } catch {
      /* the scheduler uses the same fallback */
    }
    void this.warmArrangementDrums(
      this.liveOptions,
      Math.max(20, bpm),
      nextBpm,
      drumsAtBeat(this.liveOptions, this.drumBeat),
    );
  }

  /**
   * Replace the running arrangement's EVENTS live — the voicing hot-swap
   * behind changing the comp instrument, the piano voicing family, the
   * voice-leading engine or the KEY mid-playback without restarting the
   * transport, and the reharm audition, where the chords themselves change.
   *
   * Two swaps are legal, and the difference is what each keeps intact:
   *
   *  - SAME TIMING (identical count, identical startBeat/durationBeats per
   *    index): new voicings under the same chords. Nothing index-dependent
   *    moves, so `patch` is optional.
   *  - SAME SPAN (a different list covering exactly the same stretch of the
   *    form): one chord became two, or two became one — a reharmonization.
   *    CompHit.chordIndex addresses this array POSITIONALLY, so the caller
   *    must hand over the recomputed hits (and anything else derived from
   *    the chords) in `patch`; they are applied in the same synchronous
   *    call, so no beat window can see new events beside old hits.
   *
   * What both preserve is the snapshot Play took: the pass grid, the
   * count-in shape and the form's total length. A list that covers a
   * DIFFERENT span means the form itself moved, which cannot be patched into
   * the running closure — that returns false and the caller stops cleanly.
   *
   * The boundary is setArrangementOptions' boundary: the next beat window. A
   * chord already ringing keeps its old pitches until its next attack (the
   * piano path then releases the pitches the new voicing does not hold).
   */
  setArrangementEvents(next: ArrangementEvent[], patch?: Partial<ArrangementOptions>): boolean {
    const current = this.liveEvents;
    if (!current || !this.liveOptions) return false;
    // A form waiting to land takes the new events with it.
    if (this.arrControl?.hasPendingForm()) return this.arrControl.reform(next, patch ?? {});
    if (!sameArrangementTiming(next, current)) {
      // A reharmonization: the chords moved inside a form that did not.
      if (Math.abs(arrangementSpanBeats(next) - arrangementSpanBeats(current)) > 1e-6) return false;
      if (!patch) return false;
    }
    if (patch) Object.assign(this.liveOptions, patch);
    this.liveEvents = next;
    // A live switch TO piano may find the samples cold — warm them now; the
    // additive synth answers instantly meanwhile. Play's cold-decode rule
    // ("must not change the voice mid-form") deliberately does not apply: a
    // live instrument switch IS a voice change, asked for mid-form.
    if (next.some((event) => event.pianoMidis?.length)) void this.preloadPianoSamples();
    return true;
  }

  /**
   * Replace the running arrangement's FORM live — the Ending settings (a
   * ritardando, a tag, a held last chord) rebuild the played form, and the
   * band plays on through the change (Sep 2026, user: "changing the ending
   * settings stops playback — can we keep it playing"). `patch` carries every
   * option computed for the new form: its length, pass starts, bar starts,
   * loop count, holds, ritardando and the tracks keyed to its beats.
   *
   * The new form lands on the next beat the scheduler reaches, and the cursor
   * keeps its chorus and its place in that chorus. That is only a musical
   * identity when the choruses ARE the same music in both forms, which is
   * true of an ending change (it touches the last chorus alone) and not of a
   * policy switch or an added bar — those still stop. Refused (false) when
   * nothing is playing or the anacrusis differs.
   */
  setArrangementForm(events: ArrangementEvent[], patch: Partial<ArrangementOptions>): boolean {
    const live = this.liveOptions;
    const control = this.arrControl;
    if (!live || !control) return false;
    const pickupNow = Math.max(0, live.pickupBeats ?? 0);
    const pickupNext = Math.max(0, patch.pickupBeats ?? pickupNow);
    if (Math.abs(pickupNext - pickupNow) > 1e-6) return false;
    if (!control.reform(events, patch)) return false;
    if (events.some((event) => event.pianoMidis?.length)) void this.preloadPianoSamples();
    return true;
  }

  /** Hard stop: damp every string and cancel any sequence. */
  stopAll(): void {
    this.cancelSequence();
    this.lastChordSig = null;
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const voice of [...this.voices]) this.dampVoice(voice, now);
    this.mutePiano(now);
  }

  /** Public alias for stopping playback (e.g. a Stop button). */
  stop(): void {
    this.stopAll();
  }

  /**
   * Whether a transport is running or paused — a one-shot tap then sounds
   * OVER it (Sep 2026, user: "pressing a chord in the middle of playing a
   * tune stops the playback; it would be good to play the chord over the
   * current track"). Tapping a grip on the sheet while the band plays is how
   * you check a voicing against the time; it is not a request to stop.
   */
  private transportLive(): boolean {
    return this.seqPlaying || this.transport !== 'idle';
  }

  /** A tap clears leftover one-shot state only when nothing is playing. */
  private yieldToOneShot(): void {
    if (!this.transportLive()) this.cancelSequence();
  }

  /**
   * Strum a single chord. `spread` is the seconds between strings (0 = all
   * notes together / block chord). Each string interrupts only its own note.
   * Over a live transport the grip is struck on STRINGLESS voices instead:
   * the six string slots belong to the comp while the band plays, and a tap
   * that took them would cut the comp's ringing chord (`beforeChord` mutes on
   * a signature change) — the tapped chord rings beside the band, and the
   * comp's next hit lands untouched.
   */
  async playFingering(fingering: Fingering, stringSet: StringSet, spread = 0.045): Promise<void> {
    try {
      this.yieldToOneShot();
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      await this.preloadGuitarSamples();
      if (requestToken !== this.seqToken) return;
      const at = this.ensureContext().currentTime + 0.02;
      if (this.transportLive()) {
        let voice = 0;
        for (const stringIndex of activeStringsForFingering(fingering)) {
          const fret = fingering.frets[stringIndex];
          if (fret === null || fret === undefined) continue;
          this.buildVoice(frettedMidi(stringIndex, fret), at + voice * spread, DEFAULT_SUSTAIN, -1);
          voice++;
        }
        return;
      }
      this.strumAt({ fingering, stringSet }, at, spread, 1, undefined, 'direct');
    } catch {
      /* no audio available */
    }
  }

  /** Play a single MIDI note (stringless, used for melody preview). */
  async playNote(midi: number, duration = DEFAULT_SUSTAIN): Promise<void> {
    try {
      this.yieldToOneShot();
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      await this.preloadInstrumentSamples();
      if (requestToken !== this.seqToken) return;
      const at = this.ensureContext().currentTime + 0.02;
      // Over a live transport the note stays off the band's per-pitch lead
      // voices (as previewNote does): through them it cut the head's
      // ringing note of the same pitch.
      if (this.transportLive()) this.buildLeadVoice(midi, at, duration, 1, -1);
      else this.pluckLead(midi, at, duration);
    } catch {
      /* no audio available */
    }
  }

  /**
   * Sound one pitch as EDIT FEEDBACK — note entry, arrow-key pitch nudges.
   * Unlike `playNote` this never cancels an in-flight sequence: the editor
   * previews AROUND the transport, not over it. The caller decides whether a
   * preview is appropriate right now; this method only guarantees it cannot
   * disturb playback.
   */
  async previewNote(midi: number, duration = 1.0): Promise<void> {
    try {
      const requestToken = this.seqToken;
      if (!(await this.resume())) return;
      await this.preloadInstrumentSamples();
      if (requestToken !== this.seqToken) return;
      this.buildLeadVoice(midi, this.ensureContext().currentTime + 0.02, duration, 1, -1);
    } catch {
      /* no audio available */
    }
  }

  /** The audio clock (seconds), for a lookahead scheduler. Ensures the context. */
  now(): number {
    return this.ensureContext().currentTime;
  }

  /**
   * Schedule a note to sound at an absolute audio-clock time. Sample-accurate
   * (voices are built on the audio timeline), unlike setTimeout dispatch. Used
   * by the score transport's lookahead scheduler.
   */
  scheduleNoteAt(
    midi: number,
    when: number,
    duration: number,
    options?: NoteExpressionOptions,
  ): void {
    try {
      this.scheduleExpressiveVoice(when, duration, 'melody', options, (length, gain, outlet) =>
        this.buildVoice(midi, when, length, -1, gain, 'melody', outlet),
      );
    } catch {
      /* no audio available */
    }
  }

  scheduleLeadNoteAt(
    midi: number,
    when: number,
    duration: number,
    options?: NoteExpressionOptions,
  ): void {
    try {
      this.scheduleExpressiveVoice(when, duration, 'melody', options, (length, gain, outlet) =>
        this.pluckLead(midi, when, length, gain, 'melody', outlet),
      );
    } catch {
      /* no audio available */
    }
  }

  scheduleBassNoteAt(
    midi: number,
    when: number,
    duration: number,
    options?: NoteExpressionOptions,
  ): void {
    try {
      this.scheduleExpressiveVoice(when, duration, 'bass', options, (length, gain, outlet) =>
        this.pluckBass(midi, when, length, gain, 'bass', outlet),
      );
    } catch {
      /* no audio available */
    }
  }

  schedulePianoNoteAt(
    midi: number,
    when: number,
    duration: number,
    options?: NoteExpressionOptions,
  ): void {
    try {
      this.scheduleExpressiveVoice(when, duration, 'comp', options, (length, gain, outlet) =>
        this.strikePiano(midi, when, length, gain, 'comp', outlet),
      );
    } catch {
      /* no audio available */
    }
  }

  /** Active strings of a fingering, low→high, with their MIDI pitch. */
  private orderedStrings(
    fingering: Fingering,
    stringSet: StringSet,
  ): { stringIndex: number; midi: number }[] {
    void stringSet;
    return activeStringsForFingering(fingering)
      .filter((s) => fingering.frets[s] !== null && fingering.frets[s] !== undefined)
      .map((s) => ({ stringIndex: s, midi: frettedMidi(s, fingering.frets[s]!) }))
      .sort((a, b) => a.midi - b.midi);
  }

  /**
   * Phase 1 of a press-and-hold strum: pluck the lowest string immediately and
   * let it ring (a long sustain) while the player decides how long to hold.
   */
  async strumLead(fingering: Fingering, stringSet: StringSet): Promise<void> {
    try {
      this.cancelSequence();
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      await this.preloadGuitarSamples();
      if (requestToken !== this.seqToken) return;
      const strings = this.orderedStrings(fingering, stringSet);
      if (strings.length === 0) return;
      const at = this.ensureContext().currentTime + 0.02;
      this.beforeChord(fingering, stringSet, at);
      this.pluckString(strings[0].stringIndex, strings[0].midi, at, DEFAULT_SUSTAIN + 1);
    } catch {
      /* no audio available */
    }
  }

  /**
   * Phase 2: on release, roll the remaining strings out over `totalSeconds`
   * (the time the lead note was held). A very short hold plays them together.
   */
  async strumRest(fingering: Fingering, stringSet: StringSet, totalSeconds: number): Promise<void> {
    try {
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      await this.preloadGuitarSamples();
      if (requestToken !== this.seqToken) return;
      const rest = this.orderedStrings(fingering, stringSet).slice(1);
      if (rest.length === 0) return;
      const start = this.ensureContext().currentTime + 0.02;
      const span = Math.max(0, totalSeconds);
      const BLOCK_MAX = 0.12;
      const spacing = span <= BLOCK_MAX || rest.length < 2 ? 0 : span / (rest.length - 1);
      const dur = Math.min(5, Math.max(2, spacing * 1.5 + 1.6));
      rest.forEach(({ stringIndex, midi }, i) =>
        this.pluckString(stringIndex, midi, start + i * spacing, dur),
      );
    } catch {
      /* no audio available */
    }
  }

  /**
   * Play a list of chords in time on the AudioContext clock. A short lookahead
   * keeps onsets stable through ordinary main-thread jitter, while a genuinely
   * late producer re-anchors the next chord instead of bursting through missed
   * work. Pressing Play again still owns and restarts the one shared sequence.
   *
   * The dwell before the next chord is `gaps[i]` when supplied (per-chord
   * timing — a harmonic rhythm), otherwise the uniform `gapSeconds`. With
   * `loop` the sequence wraps back to the first chord (after the last chord's
   * dwell) until stopped, instead of ending. Passing a number keeps the old
   * uniform-gap signature.
   */
  async playSequence(
    chords: SequenceChordInput[],
    options: number | { gapSeconds?: number; gaps?: number[]; loop?: boolean } = 0.7,
  ): Promise<void> {
    try {
      // Lookahead creates future voices, so a replacement must damp the old
      // owner's already-scheduled nodes as well as cancelling its wake-up.
      this.stopAll();
      this.wantSinkRoute = true;
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      if (chords.length === 0) return;
      await this.preloadGuitarSamples();
      if (requestToken !== this.seqToken) return;
      const opts = typeof options === 'number' ? { gapSeconds: options } : options;
      const gapSeconds = opts.gapSeconds ?? 0.7;
      const gaps = opts.gaps;
      const loop = opts.loop ?? false;
      const dwell = (i: number) => {
        const seconds = gaps && gaps[i] != null ? gaps[i] : gapSeconds;
        // A zero/invalid looping dwell would trap the lookahead's while loop.
        return Number.isFinite(seconds) ? Math.max(0.01, seconds) : 0.7;
      };
      const ctx = this.ensureContext();
      const token = ++this.seqToken;
      this.setSeqPlaying(true);
      let index = 0;
      let nextAudioTime = ctx.currentTime + AUDIO_START_LEAD_SECONDS;
      let finishAt: number | null = null;
      let lastAudioClock = ctx.currentTime;
      let lastAudioClockProgress = performance.now();

      const finish = () => {
        if (token !== this.seqToken) return;
        this.seqTimer = null;
        // The transport is over; the route itself goes back lazily (see
        // playArrangement's finish).
        this.wantSinkRoute = false;
        this.setSeqPlaying(false);
      };

      const queuePump = (pump: () => void) => {
        this.activePump = pump;
        this.seqTimer = setTimeout(pump, AUDIO_SCHEDULER_TICK_MS);
      };

      const pump = () => {
        if (token !== this.seqToken) return; // superseded / stopped
        this.seqTimer = null;
        const now = ctx.currentTime;
        const wallNow = performance.now();
        if (now > lastAudioClock + 0.001) {
          lastAudioClock = now;
          lastAudioClockProgress = wallNow;
        } else if ((ctx.state as string) === 'running' && wallNow - lastAudioClockProgress > 1000) {
          // Match the arrangement watchdog: a WebKit context can claim to be
          // running while its render clock is dead. End this owner honestly so
          // the next user gesture can rebuild the audio route.
          this.contextClockStalled = true;
          this.stopAll();
          return;
        }

        if (finishAt !== null) {
          if (now >= finishAt) finish();
          else queuePump(pump);
          return;
        }

        // Preserve the next musical chord after a blocked callback, but move
        // its grid origin forward. Scheduling into the past makes browsers
        // collapse every missed onset into one audible burst.
        if (nextAudioTime < now + AUDIO_MIN_LEAD_SECONDS) {
          nextAudioTime = now + AUDIO_START_LEAD_SECONDS;
        }

        const horizon = now + this.lookaheadSeconds();
        while (finishAt === null && nextAudioTime <= horizon) {
          const chord = chords[index];
          const at = nextAudioTime;
          this.strumAt({ fingering: chord.fingering, stringSet: chord.stringSet }, at);
          nextAudioTime = at + dwell(index);

          const isLast = index === chords.length - 1;
          if (!isLast) index += 1;
          else if (loop) index = 0;
          else finishAt = nextAudioTime;
        }

        queuePump(pump);
      };

      pump();
    } catch {
      /* no audio available */
    }
  }

  /** A short metronome click; accented (louder/higher) on the downbeat. */
  /**
   * One metronome click in the chosen voice (#412): an ACCENT on the one, a
   * BEAT, or a quieter SUB for a subdivision. Every voice is synthesised and
   * lands on the click's own bus.
   */
  private metronomeClick(when: number, kind: ClickKind | boolean): void {
    const click: ClickKind = typeof kind === 'boolean' ? (kind ? 'accent' : 'beat') : kind;
    const ctx = this.ensureContext();
    const level = click === 'accent' ? 1 : click === 'beat' ? 0.55 : 0.3;
    const sound = this.metronomeSettings.sound;
    const amp = ctx.createGain();
    amp.connect(this.clickOut());
    const oscillators: OscillatorNode[] = [];
    const sources: AudioBufferSourceNode[] = [];
    const nodes: AudioNode[] = [];
    const tone = (type: OscillatorType, freq: number, to: AudioNode, length: number) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = freq;
      osc.connect(to);
      osc.start(when);
      osc.stop(when + length);
      oscillators.push(osc);
    };
    const noise = (filterType: BiquadFilterType, freq: number, length: number) => {
      const src = ctx.createBufferSource();
      src.buffer = this.getBassNoise(ctx);
      const filter = ctx.createBiquadFilter();
      filter.type = filterType;
      filter.frequency.value = freq;
      src.connect(filter);
      filter.connect(amp);
      src.start(when);
      src.stop(when + length);
      sources.push(src);
      nodes.push(filter);
    };
    const envelope = (peak: number, decay: number) => {
      amp.gain.setValueAtTime(0.0001, when);
      amp.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak * level), when + 0.001);
      amp.gain.exponentialRampToValueAtTime(0.0001, when + decay);
      return decay + 0.01;
    };
    let length: number;
    if (sound === 'woodblock') {
      length = envelope(0.55, 0.045);
      tone('triangle', click === 'accent' ? 1760 : click === 'beat' ? 1320 : 1100, amp, length);
    } else if (sound === 'rim') {
      length = envelope(0.5, 0.03);
      tone('square', click === 'accent' ? 1900 : 1600, amp, length);
      noise('highpass', 2500, length);
    } else if (sound === 'cowbell') {
      length = envelope(0.3, click === 'accent' ? 0.16 : 0.1);
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = click === 'accent' ? 820 : 760;
      band.connect(amp);
      nodes.push(band);
      tone('square', 540, band, length);
      tone('square', 800, band, length);
    } else if (sound === 'hihat') {
      length = envelope(0.6, click === 'accent' ? 0.07 : 0.04);
      noise('highpass', 7000, length);
    } else {
      length = envelope(0.4, 0.05);
      tone('square', click === 'accent' ? 2000 : click === 'beat' ? 1300 : 1000, amp, length);
    }
    const voice: Voice = {
      channel: 'direct',
      startTime: when,
      amp,
      oscillators,
      sources,
      nodes,
      stringIndex: -1,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (when - ctx.currentTime + length + 0.15) * 1000),
      ),
    };
    this.voices.add(voice);
  }

  /** One coordinated kit accent, independent of the loop's arbitrary sample phase. */
  private drumStabAt(when: number, duration: number): void {
    const ctx = this.ensureContext();
    const length = Math.max(0.01, duration);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, when);
    amp.gain.linearRampToValueAtTime(0.7, when + Math.min(0.003, length * 0.1));
    amp.gain.exponentialRampToValueAtTime(0.0001, when + Math.min(0.24, length));
    amp.connect(this.busFor('drums'));

    const kick = ctx.createOscillator();
    kick.type = 'sine';
    kick.frequency.setValueAtTime(120, when);
    kick.frequency.exponentialRampToValueAtTime(48, when + Math.min(0.08, length));
    kick.connect(amp);
    kick.start(when);
    kick.stop(when + length);

    const snare = ctx.createBufferSource();
    snare.buffer = this.getBassNoise(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1200;
    const snareGain = ctx.createGain();
    snareGain.gain.setValueAtTime(0.45, when);
    snareGain.gain.exponentialRampToValueAtTime(0.0001, when + Math.min(0.08, length));
    snare.connect(filter);
    filter.connect(snareGain);
    snareGain.connect(amp);
    snare.start(when);
    snare.stop(when + Math.min(0.08, length));
    const voice: Voice = {
      channel: 'drums',
      startTime: when,
      amp,
      oscillators: [kick],
      sources: [snare],
      nodes: [filter, snareGain],
      stringIndex: -1,
      teardown: setTimeout(
        () => this.disposeVoice(voice),
        Math.max(0, (when - ctx.currentTime + length + 0.15) * 1000),
      ),
    };
    this.voices.add(voice);
    this.cutVoiceAt(voice, when + length);
  }

  /**
   * Play a chart respecting bar/beat placement at a given tempo. Chords strum on
   * their beat; an optional metronome clicks every beat and an optional bass
   * line plucks under it. The main thread only fills a short lookahead window;
   * every audible onset is anchored to the AudioContext clock.
   */
  async playArrangement(events: ArrangementEvent[], options: ArrangementOptions): Promise<void> {
    try {
      // Claim the shared transport before an asynchronous iOS unlock. Stop or a
      // newer Play increments the generation, preventing this invocation from
      // coming back from `resume()` and starting stale audio.
      this.stopAll();
      this.wantSinkRoute = true;
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      let totalBeats = Math.max(0, options.formBeats ?? 0, arrangementSpanBeats(events));
      if (totalBeats <= 0) return;
      const ctx = this.ensureContext();
      // Do not let a cold fetch/decode make the rhythm section join halfway
      // through the form. Unsupported codecs fail closed and the arrangement
      // continues without drums.
      // Every loop the performance can reach, loaded before a note sounds —
      // a kit that starts fetching at its own cue boundary plays silence
      // until it arrives.
      const loads: Promise<unknown>[] = [this.preloadArrangementDrums(options)];
      // Same rule for the piano samples: loading them mid-form would change
      // the piano's voice halfway through a pass, which reads as a glitch.
      if (
        events.some((event) => event.pianoMidis?.length) ||
        options.authoredCompNotes?.some((note) => note.instrument === 'piano') ||
        options.melodyInstrument === 'piano' ||
        (options.melody && options.melodyInstrument !== 'bass' && this.leadSound === 'piano')
      ) {
        loads.push(this.preloadPianoSamples());
      }
      if (options.melody || options.bassline) loads.push(this.preloadInstrumentSamples());
      else if (
        events.some((event) => !event.pianoMidis?.length) ||
        options.authoredCompNotes?.length
      )
        loads.push(this.preloadGuitarSamples());
      await Promise.all(loads);
      if (requestToken !== this.seqToken) return;
      this.liveOptions = { ...options, beatsPerBar: options.beatsPerBar ?? 4 };
      this.liveEvents = events;
      const token = ++this.seqToken;
      this.setSeqPlaying(true);
      this.setTransport('playing');
      this.setNowPlaying(options.nowPlaying);

      // Count-in shape is snapshotted at play start (a live change would remap
      // the ever-increasing abs counter and jump the form position).
      const countBars = Math.max(1, Math.round(options.countInBars ?? 1));
      const gapBeats = options.countInBetweenLoops ? countBars * (options.beatsPerBar ?? 4) : 0;
      // Anacrusis: the form's first `pickup` beats are melody-only. Snapshotted
      // like the count-in — the bar grid must not remap mid-playback.
      const pickup = Math.max(0, options.pickupBeats ?? 0);
      // The beat grid starts at the pickup's fraction: a half-beat anacrusis
      // puts every beat on x.5, and the swing pairs must count from there.
      const swingOrigin = pickup - Math.floor(pickup);
      const countInSnap = !!options.countIn;
      // The pass grid, as a function of the form. It is recomputed only when a
      // new form lands (setArrangementForm — an Ending change): pickup, count-in
      // and gap shape stay the Play-time snapshot either way.
      const shapeOf = (total: number, opts: ArrangementOptions) => {
        const logicalStarts = [...new Set([0, ...(opts.passStarts ?? [])])]
          .filter((beat) => Number.isFinite(beat) && beat >= 0 && beat < total)
          .sort((a, b) => a - b);
        const logicalPasses = logicalStarts.map((start, index) => ({
          start,
          end: logicalStarts[index + 1] ?? total,
          // Each preceding logical pass contributes one between-pass count-in.
          timelineStart: start + index * gapBeats,
        }));
        // A cycle includes a gap after its last logical pass so another whole-
        // form repeat can follow it. Finite playback subtracts that final gap.
        const cycleSpan = total + logicalPasses.length * gapBeats;
        // A written anacrusis is played ONCE, on the way in. Repeating it inserts
        // its length between the last bar and the next pass's downbeat — wrong
        // musically (you come off the final bar straight into bar 1) and, for a
        // FRACTIONAL pickup, fatal: the scheduler steps whole beats, so a cycle
        // of 128.5 puts every other pass half a beat off the bar grid and the
        // whole band drifts on and off the beat. Later passes therefore begin at
        // the first full bar, which also makes the repeat a whole number of bars.
        // `loopStartBeat` generalises the same exclusion to a written intro or
        // verse: the whole ENTRY plays once and the loop re-enters at the chorus
        // top. Entry material is whole bars past the pickup, so the seam stays
        // on the bar grid either way.
        const loopStart = Math.max(pickup, opts.loopStartBeat ?? 0);
        const firstLoopPass = loopStartPassIndex(logicalStarts, loopStart);
        const repeatedPasses = logicalPasses.length - firstLoopPass;
        // Source beats omit count-in gaps; timeline beats include them. A loop
        // entering a later logical pass must skip those earlier gaps too.
        const loopTimelineStart = loopStart + firstLoopPass * gapBeats;
        const repeatSpan = Math.max(1e-6, cycleSpan - loopTimelineStart);
        return {
          logicalPasses,
          cycleSpan,
          loopStart,
          firstLoopPass,
          repeatedPasses,
          loopTimelineStart,
          repeatSpan,
          barStartSet: opts.barStarts,
        };
      };
      let {
        logicalPasses,
        cycleSpan,
        loopStart,
        firstLoopPass,
        repeatedPasses,
        loopTimelineStart,
        repeatSpan,
        barStartSet,
      } = shapeOf(totalBeats, options);
      /** Timeline length of `n` passes, before the final pass's dropped gap. */
      const spanForPasses = (n: number) => cycleSpan + Math.max(0, n - 1) * repeatSpan;
      // With a pickup, the count-in bar OVERLAYS the anacrusis: clicks span a
      // whole number of bars ENDING at the first full bar's downbeat (abs =
      // pickup), so the pickup notes sound inside the counted bar.
      // The scheduler's whole-beat windows must sit on the TUNE's grid: bars
      // begin at `pickup`, so windows belong at `pickup + k`. A count-in gets
      // that for free. Without one, the LEAD-IN BAR IS MADE WHOLE (user
      // request Aug 2026, from Stella By Starlight's one-beat pickup): the
      // anacrusis pads to a full bar ending at bar 1's downbeat — a
      // predictable pause, with the metronome clicking through it when
      // enabled — instead of playback opening on an orphaned partial beat
      // with no context. The pad is melody-only, like the anacrusis it
      // completes, and it also keeps every window on the tune's grid (the
      // old partial lead window existed for exactly that).
      // An in-form beat as a timeline position in a given cycle. Entry material
      // (the anacrusis, an intro) exists only in the first cycle, so a beat
      // inside it always answers there.
      const formBeatAbs = (formBeat: number, cycle: number): number | null => {
        const lp = logicalPasses.findIndex(
          (segment) => formBeat >= segment.start - 1e-6 && formBeat < segment.end - 1e-6,
        );
        if (lp < 0) return null;
        const inCycle = logicalPasses[lp].timelineStart + (formBeat - logicalPasses[lp].start);
        const c = lp < firstLoopPass || formBeat < loopStart - 1e-6 ? 0 : Math.max(0, cycle);
        if (c === 0) return inCycle;
        return cycleSpan + (c - 1) * repeatSpan + (inCycle - loopTimelineStart);
      };
      // PLAY FROM A BAR (the sheet's start cursor): the band begins at that
      // bar instead of the top. A count-in counts whole bars of its own before
      // it, on a grid of their own (the anacrusis is not played, so nothing
      // overlays it), and the cursor then lands on the bar.
      const startAbs =
        options.startBeat !== undefined && options.startBeat > pickup + 1e-6
          ? formBeatAbs(options.startBeat, 0)
          : null;
      const countOrigin = startAbs !== null ? 0 : pickup;
      const initialAbs =
        startAbs !== null
          ? countInSnap
            ? -(options.beatsPerBar ?? 4) * countBars
            : startAbs
          : countInSnap
            ? pickup - (options.beatsPerBar ?? 4) * countBars
            : pickup > 1e-6
              ? pickup - (options.beatsPerBar ?? 4)
              : 0;
      // Where the count lands: the first beat after it is the start bar.
      let startLanding: number | null = startAbs !== null && countInSnap ? startAbs : null;
      // A chord pushed across the barline, or a note tied into it, sounds as
      // on a resume: the band joins the music already under way.
      if (startAbs !== null) this.resumeRestrike = true;

      let nextAbs = initialAbs;
      let nextAudioTime = ctx.currentTime + AUDIO_START_LEAD_SECONDS;
      let schedulingDone = false;
      let finishAt: number | null = null;
      let forceDrumReanchor = false;
      let scheduledBeats: { abs: number; at: number }[] = [];
      // A cue carries its PASS beside its beat, and both are announced when the
      // cue's audio time arrives. Announcing the pass at SCHEDULE time — up to
      // the lookahead ahead of the clock — paired the new pass with the old
      // pass's last beat in the hook for that window, which on a pre-expanded
      // form mapped past the final pass's grid and lit its LAST bar: the Fine
      // bar flashed at every pass boundary (user, Sep 2026). A beat-less cue
      // announces the upcoming pass at the start of a between-loops count-in.
      let beatCues: { beat: number | null; at: number; pass: number }[] = [];
      let lastAudioClock = ctx.currentTime;
      let lastAudioClockProgress = performance.now();

      const finish = () => {
        if (token !== this.seqToken) return;
        this.seqTimer = null;
        this.liveOptions = null;
        this.liveEvents = null;
        this.arrControl = null;
        this.stopDrums(ctx.currentTime, 0.015);
        this.emitBeat(-1);
        this.emitPass(0);
        this.emitPendingChorus(null);
        // The transport is over, so later sounds must not keep the media
        // route. The element keeps carrying the (now silent) band until the
        // next sound outside a transport routes direct (ensurePlaybackSession):
        // a tune usually ends with nobody at the screen, a set running on,
        // and a keeper back on its silent WAV there is the case where iOS
        // suspends the context behind it, before the next tune can start.
        this.wantSinkRoute = false;
        this.setSeqPlaying(false);
        this.setTransport('idle');
        // Last, and only here: the sequence played to its end. `stop()` runs
        // the same teardown without this, which is the whole distinction.
        this.completionListeners.forEach((l) => {
          try {
            l();
          } catch {
            /* ignore listener errors */
          }
        });
      };

      // Map the scheduler's monotonically increasing timeline back to an
      // authored form beat. Gaps are inserted after *logical* passes, not
      // merely after a whole pre-unrolled arrangement. This matters for a
      // detached-coda performance whose earlier and final passes share one
      // event array but have separate `passStarts`.
      // Pass 1 plays the whole cycle, entry material (anacrusis + any
      // intro) included; every later pass re-enters at the loop start (see
      // `repeatSpan`). With no entry this is exactly `abs % cycleSpan`.
      // Only meaningful for abs >= 0 (the count-in runs negative).
      const locate = (abs: number) => {
        const cycleIdx = abs < cycleSpan ? 0 : 1 + Math.floor((abs - cycleSpan) / repeatSpan);
        const local = abs < cycleSpan ? abs : loopTimelineStart + ((abs - cycleSpan) % repeatSpan);
        let logicalPass = logicalPasses.length - 1;
        let beat: number | null = null;
        let gapOffset = 0;
        for (let index = 0; index < logicalPasses.length; index += 1) {
          const segment = logicalPasses[index];
          const length = segment.end - segment.start;
          if (local < segment.timelineStart + length) {
            logicalPass = index;
            beat = segment.start + (local - segment.timelineStart);
            break;
          }
          if (local < segment.timelineStart + length + gapBeats) {
            logicalPass = index;
            gapOffset = local - (segment.timelineStart + length);
            break;
          }
        }
        const globalPass =
          cycleIdx === 0
            ? logicalPass + 1
            : logicalPasses.length +
              (cycleIdx - 1) * repeatedPasses +
              logicalPass -
              firstLoopPass +
              1;
        return { cycleIdx, logicalPass, beat, gapOffset, globalPass };
      };

      // CHORUS NAVIGATION (skipChorus). A chorus is a global pass. Its first
      // beat: pass 1 opens on bar 1's downbeat (the anacrusis leads into it,
      // so a jump back to the top lands where a band would count it off);
      // a later cycle re-enters at the loop start like any repeat.
      const passStartBeat = (pass: number) => {
        const { passIndex: lp, cycleIndex: cycleIdx } = loopingPassPosition(
          pass,
          logicalPasses.length,
          firstLoopPass,
        );
        if (cycleIdx > 0 && lp === firstLoopPass) return loopStart;
        if (lp > 0) return logicalPasses[lp].start;
        return pickup;
      };
      const passStartAbs = (pass: number) => {
        const { passIndex: lp, cycleIndex: cycleIdx } = loopingPassPosition(
          pass,
          logicalPasses.length,
          firstLoopPass,
        );
        const inCycle =
          cycleIdx > 0 && lp === firstLoopPass
            ? loopTimelineStart
            : lp > 0
              ? logicalPasses[lp].timelineStart
              : pickup;
        if (cycleIdx === 0) return inCycle;
        return cycleSpan + (cycleIdx - 1) * repeatSpan + Math.max(0, inCycle - loopTimelineStart);
      };
      const isBarStart = (abs: number) => {
        const beatsPerBar = this.liveOptions?.beatsPerBar ?? 4;
        const onGrid = (offset: number) =>
          Math.abs(offset - Math.round(offset / beatsPerBar) * beatsPerBar) < 1e-3;
        if (abs < 0) return onGrid(abs - pickup);
        const { beat, gapOffset } = locate(abs);
        if (beat === null) return onGrid(gapOffset);
        if (barStartSet && barStartSet.length > 0) {
          return barStartSet.some((start) => Math.abs(start - beat) < 1e-3);
        }
        return onGrid(beat - pickup);
      };
      // The cycle the scheduler last played (a between-loops count-in belongs
      // to the chorus it counts in). A live repeat count below it cannot end
      // the tune there: lowering Repeats, or turning ∞ off, in a later chorus
      // finishes that chorus instead of stopping dead on the next beat.
      let playingCycle = 0;
      const cyclesToPlay = (options: ArrangementOptions | null, cycle: number) =>
        options?.loop ? Infinity : Math.max(1, options?.loopCount ?? 1, cycle + 1);
      const totalPasses = () =>
        logicalPasses.length + (cyclesToPlay(this.liveOptions, playingCycle) - 1) * repeatedPasses;
      let pendingJump: number | null = null;
      const setPendingJump = (pass: number | null) => {
        pendingJump = pass;
        this.emitPendingChorus(pass);
      };
      /** A clean seam at a jump: everything still ringing stops at the barline. */
      const dampForJump = (at: number) => {
        for (const voice of [...this.voices]) {
          if (voice.channel !== 'drums') this.dampVoice(voice, at, 0.06);
        }
        this.stringVoices = [null, null, null, null, null, null, null, null];
        this.leadVoices.clear();
        this.mutePiano(at);
        this.lastChordSig = null;
      };
      /** The jump lands on a barline: the old chorus is cut there and the new
       * one starts on its own downbeat, the kit re-anchored. What should be
       * ringing there is re-struck as on a resume — the anticipated first
       * chord of a pass is struck before its downbeat, behind the cursor. */
      const landJump = (pass: number, at: number) => {
        nextAbs = passStartAbs(pass);
        setPendingJump(null);
        dampForJump(at);
        forceDrumReanchor = true;
        this.resumeRestrike = true;
      };
      // A tapped bar (seek): the band moves there on the next beat the
      // scheduler reaches, cut and re-struck like a chorus jump.
      let pendingSeek: number | null = null;
      const landSeek = (at: number) => {
        if (pendingSeek === null) return;
        nextAbs = pendingSeek;
        pendingSeek = null;
        dampForJump(at);
        forceDrumReanchor = true;
        this.resumeRestrike = true;
      };

      // A NEW FORM (setArrangementForm): the Ending settings rebuild the played
      // form — a tag adds bars, and any ending writes a looping form's passes
      // out — and the band plays on through it. It lands on the next beat the
      // scheduler reaches (never inside a between-loops count, whose gap
      // belongs to the chorus it counts in), and the cursor keeps its CHORUS
      // and its place in it: every chorus but the last is the same music in
      // either form, so nothing re-strikes, nothing damps and the kit runs on.
      let pendingForm: { events: ArrangementEvent[]; patch: Partial<ArrangementOptions> } | null =
        null;
      /** Whether a cursor has reached the form (the count-in and the anacrusis
       * lie before it, and are the same in every form). */
      const reachedForm = (abs: number) => abs >= pickup - 1e-6;
      const formReady = (abs: number) => !reachedForm(abs) || locate(abs).beat !== null;
      const landForm = () => {
        const form = pendingForm;
        const live = this.liveOptions;
        if (!form || !live) return;
        pendingForm = null;
        const intoForm = reachedForm(nextAbs);
        const at = intoForm ? locate(nextAbs) : null;
        const pass = at?.globalPass ?? 1;
        const offset = at && at.beat !== null ? at.beat - passStartBeat(pass) : 0;
        this.liveEvents = form.events;
        Object.assign(live, form.patch);
        totalBeats = Math.max(0, live.formBeats ?? 0, arrangementSpanBeats(form.events));
        ({
          logicalPasses,
          cycleSpan,
          loopStart,
          firstLoopPass,
          repeatedPasses,
          loopTimelineStart,
          repeatSpan,
          barStartSet,
        } = shapeOf(totalBeats, live));
        if (!intoForm) return;
        // Past the new form's end (a tag taken away while it played) the
        // scheduler finds no beat there and the tune ends at that barline.
        nextAbs = passStartAbs(pass) + offset;
        playingCycle = locate(nextAbs).cycleIdx;
        if (this.arrControl) this.arrControl.nextAbs = nextAbs;
      };

      const flushBeatCues = (now: number) => {
        while (beatCues.length > 0 && beatCues[0].at <= now + 0.001) {
          const cue = beatCues.shift()!;
          this.emitPass(cue.pass);
          if (cue.beat !== null) this.emitBeat(cue.beat);
        }
        scheduledBeats = scheduledBeats.filter((scheduled) => scheduled.at > now + 0.001);
      };

      // Schedule one integer beat at the absolute audio time supplied by the
      // lookahead producer. The return value advances the musical cursor; a null
      // next beat means this was the final beat of a finite playback.
      const scheduleBeat = (abs: number, at: number): { next: number | null; spb: number } => {
        const opts = this.liveOptions;
        if (!opts) return { next: null, spb: 0 };
        // Read the LIVE events, not the Play-time snapshot: a same-timing
        // voicing swap (setArrangementEvents — comp instrument, piano family,
        // engine, key) lands at the next window like every other live option.
        const events = this.liveEvents ?? [];
        const beatsPerBar = opts.beatsPerBar ?? 4;
        const tempoForPass = (pass: number) => {
          let bpm = opts.bpm;
          try {
            const resolved = opts.bpmForPass?.(Math.max(1, pass));
            if (resolved !== undefined && Number.isFinite(resolved)) bpm = resolved;
          } catch {
            // A UI-provided resolver must never take down the audio scheduler.
          }
          return Math.max(20, bpm);
        };

        if (abs < 0) {
          const spb = 60 / tempoForPass(1);
          // Accents align to the bar grid anchored at the first FULL bar's
          // downbeat (abs = pickup), so a pickup-overlaid count still counts
          // "1 2 3 4" with 1 on the count bar's own downbeat. With no count-in
          // this branch is the made-whole lead-in bar: the metronome (when
          // enabled) clicks through it so "the first bar plays and we're
          // off"; otherwise it is a silent pause the same length.
          if (countInSnap || opts.metronome) {
            this.metronomeClick(
              at,
              (((abs - countOrigin) % beatsPerBar) + beatsPerBar) % beatsPerBar === 0,
            );
          }
          // A FRACTIONAL pickup (Billie's Bounce leads in on the "and of 4")
          // leaves form beats inside this count window: the scheduler steps
          // whole beats from a fractional start, so those notes belong to no
          // later window and would be dropped. Sound the melody here — over
          // the click only; the band still waits for bar 1. A count into a
          // later bar skips the anacrusis, so it plays nothing here.
          if (startAbs === null && opts.melody && opts.melodyNotes && opts.melodyNotes.length) {
            for (const mn of opts.melodyNotes) {
              if (mn.beat < -0.001) continue;
              if (mn.beat - abs < -0.001 || mn.beat - abs >= 0.999) continue;
              // Inside the count-in the form beat is `abs` (the count runs
              // negative), which is where the note's own position sits.
              const sounding = swungPosition(
                opts,
                mn.beat,
                swingOrigin,
                'melody',
                opts.melodyNotes,
              );
              const length = swungLength(
                opts,
                mn.beat,
                mn.durationBeats,
                sounding,
                swingOrigin,
                'melody',
                opts.melodyNotes,
              );
              this.playMelodyAt(
                mn,
                at + (sounding - abs) * spb,
                spb,
                opts.melodyInstrument,
                0,
                length,
                melodyOnsetShift(opts, mn.beat, swingOrigin, spb, length, opts.melodyNotes),
              );
            }
          }
          return { next: abs + 1, spb };
        }

        const { cycleIdx, logicalPass, beat, gapOffset, globalPass } = locate(abs);
        const passes = cyclesToPlay(opts, playingCycle);
        // The count came down while the cursor stood at the top of the next
        // chorus: the one before was the last, so this beat is past the end.
        if (beat !== null && cycleIdx >= passes) return { next: null, spb: 0 };
        playingCycle =
          cycleIdx + (beat === null && logicalPass === logicalPasses.length - 1 ? 1 : 0);
        if (beat === null) {
          // Announce the upcoming pass before its first click. Its trainer tempo
          // governs the entire count-in, including the first gap interval.
          const upcomingPass = globalPass + 1;
          if (gapOffset === 0) {
            beatCues.push({ beat: null, at, pass: upcomingPass });
            this.stopDrums(at);
          }
          const spb = 60 / tempoForPass(upcomingPass);
          this.metronomeClick(at, gapOffset % beatsPerBar === 0);
          return { next: abs + 1, spb };
        }

        // The pass is announced with this beat's cue (below), at audio time;
        // the scheduler itself resolves tempo through bpmForPass, so nothing
        // here waits on a listener.
        let effectiveBpm = tempoForPass(globalPass);
        const timeStretch = opts.drumTimeStretch !== false;
        this.drumBeat = beat;
        const activeLoop = drumsAtBeat(opts, beat);
        void this.warmArrangementDrums(
          opts,
          effectiveBpm,
          opts.bpmForPass ? tempoForPass(globalPass + 1) : undefined,
          activeLoop,
        );
        if (
          this.drum &&
          activeLoop &&
          this.drum.bpm !== effectiveBpm &&
          !this.preparedDrumBuffer(activeLoop, effectiveBpm, timeStretch)
        ) {
          // A cold live tempo takes effect for the whole band on the first
          // beat with a prepared loop. Keeping the prior tempo meanwhile
          // avoids either a silent kit or a rhythm section that drifts apart.
          if (this.drumPreparationFailed(activeLoop, effectiveBpm, timeStretch)) {
            this.stopDrums(at, 0.02);
          } else {
            void this.prepareDrums(activeLoop, effectiveBpm, timeStretch);
            effectiveBpm = this.drum.bpm;
          }
        }
        // The ritardando folds into the beat itself (offbeats and note lengths
        // stretch with it), where a hold stretches only the step — one strike,
        // then the ring.
        const ritStretch = ritardandoStretch(opts.ritardando, beat);
        const spb = (60 / effectiveBpm) * ritStretch;
        // A fermata hold: time stretches through this beat — the clock dwells
        // HOLD_STRETCH x while whatever struck at the hold's start rings.
        const hold = opts.holds?.some((w) => beat >= w.start - 1e-3 && beat < w.end - 1e-3);
        const displayBeat = beat - logicalPasses[logicalPass].start;
        beatCues.push({ beat: displayBeat, at, pass: globalPass });
        // The initial count-in continues THROUGH the overlaid pickup span:
        // its final clicks land on the pickup's beats (abs 0..pickup), where
        // the pickup notes sound over click only.
        const countClickHere = countInSnap && abs < pickup - 1e-3;
        if (countClickHere) {
          this.metronomeClick(
            at,
            (((abs - pickup) % beatsPerBar) + beatsPerBar) % beatsPerBar === 0,
          );
        }
        // A gap click (#413): bars that click, then bars you keep yourself,
        // counted from the first downbeat so a loop or a pass boundary never
        // resets the cycle.
        const gapSilent =
          !!opts.clickGap &&
          !gapClickSounds(Math.floor((abs - pickup + 1e-6) / beatsPerBar), opts.clickGap);
        if (opts.metronome && !hold && !gapSilent) {
          // The click's pattern and subdivision (#412): which beats of the bar
          // sound, and what fills each beat between them.
          const { accents, subdivision } = this.metronomeSettings;
          const subs = subdivisionOffsets(subdivision);
          const clickAt = (when: number, inBar: number, beatSpan: number) => {
            const kind = clickFor(inBar, accents);
            if (kind) this.metronomeClick(when, kind);
            for (const sub of subs) {
              if (sub < beatSpan - 1e-3) this.metronomeClick(when + sub * spb, 'sub');
            }
          };
          const clickGrid = opts.metronomeBeats;
          if (clickGrid && clickGrid.length > 0) {
            for (const click of clickGrid) {
              const offset = click.beat - beat;
              if (offset < -0.001 || offset >= 0.999) continue;
              // The count-in already clicked this pickup position.
              if (countClickHere && click.beat < pickup - 1e-3) continue;
              clickAt(
                at + offset * spb,
                click.inBar ?? (click.accent ? 0 : 1),
                Math.max(0, Math.min(1, totalBeats - click.beat)),
              );
            }
          } else if (!countClickHere) {
            clickAt(
              at,
              (((displayBeat - pickup) % beatsPerBar) + beatsPerBar) % beatsPerBar,
              Math.max(0, Math.min(1, totalBeats - beat)),
            );
          }
        }
        const restrike = this.resumeRestrike;
        this.resumeRestrike = false;

        // A chord followed by a GAP in the harmony — an N.C. bar, or the
        // form ending short of its last bar — releases at its own end
        // (Sep 2026, Cold Duck Time: "it's ignoring the N.C. bars"). Nothing
        // strikes next to cut it, so a held pad and a hit ringing "to the
        // next attack" both sounded the chord straight through the written
        // break. Returns the seconds from `attackBeat` to the chord's end,
        // or undefined when another chord follows at once.
        const releaseAtGap = (event: ArrangementEvent, attackBeat: number) => {
          const end = event.startBeat + event.durationBeats;
          let next = totalBeats;
          for (const e of events) {
            if (e.startBeat > event.startBeat + 1e-6 && e.startBeat < next) next = e.startBeat;
          }
          if (next - end <= 1e-3) return undefined;
          return Math.max(0.025, (end - attackBeat) * spb);
        };

        const bandCues = opts.bandCues ?? [];
        const cueAt = (channel: BandCueInterval['channel'], target: number) =>
          bandCueAtBeat(bandCues, channel, target);
        const cueSuppresses = (channel: BandCueInterval['channel'], target: number) => {
          const cue = cueAt(channel, target);
          return cue !== undefined && cue.mode !== 'play';
        };
        const harmonyForCue = (cue: BandCueInterval) =>
          events.find(
            (event) =>
              event.startBeat <= cue.startBeat + 1e-3 &&
              event.startBeat + event.durationBeats > cue.startBeat + 1e-3,
          );
        const cueLength = (cue: BandCueInterval, attackBeat = cue.startBeat) =>
          Math.max(
            0.01,
            (Math.min(cue.hitDurationBeats ?? 1, cue.endBeat - cue.startBeat) -
              (attackBeat - cue.startBeat)) *
              spb,
          );
        const cueAttackBeat = (cue: BandCueInterval) =>
          restrike &&
          cue.mode === 'hit' &&
          beat > cue.startBeat + 1e-3 &&
          beat < Math.min(cue.endBeat, cue.startBeat + (cue.hitDurationBeats ?? 1)) - 1e-3
            ? beat
            : cue.startBeat;

        // The comp-role channel (generated guitar plus authored Guitar/Piano)
        // is muted whenever soloBass is set. Build one chronological action
        // list per scheduler window so an override boundary can sample-
        // accurately damp a generated attack just before it without cutting a
        // later fallback attack in the same window.
        if (!opts.soloBass) {
          const overrideSpans = opts.authoredCompSpans ?? [];
          const inOverride = (target: number) =>
            overrideSpans.some(
              (span) =>
                target >= span.startBeat - 1e-3 &&
                target < span.startBeat + span.durationBeats - 1e-3,
            );
          const actions: { offset: number; priority: number; run: (when: number) => void }[] = [];
          const addAction = (offset: number, priority: number, run: (when: number) => void) => {
            if (offset < -0.001 || offset >= 0.999) return;
            actions.push({ offset, priority, run });
          };

          // `undefined` means legacy Held-chord generation. An explicitly
          // empty array means there are deliberately zero generated attacks —
          // it must never fall through to Held chords (notably for rests).
          if (opts.compHits !== undefined) {
            const compHits = opts.compHits;
            /** The chord a hit strikes, or undefined when the comp does not play it. */
            const hitEvent = (hit: CompHit) => {
              if (
                inOverride(hit.beat) ||
                hit.beat < pickup - 1e-3 ||
                cueSuppresses('comp', hit.beat)
              )
                return undefined;
              const event = events[hit.chordIndex];
              if (!event) return undefined;
              // A written downbeat hit owns this chord: Push/anticipation
              // must not announce it early from the preceding bar.
              if (
                hit.beat < event.startBeat - 1e-3 &&
                cueAt('comp', event.startBeat)?.mode === 'hit'
              )
                return undefined;
              return event;
            };
            // `resumed` re-sounds a hit that a pause (or a chorus jump) damped
            // while it rang: the whole chord, plainly, since the other hand's
            // tones were damped with it.
            const strikeHit = (
              hit: CompHit,
              event: ArrangementEvent,
              when: number,
              attackBeat: number,
              resumed: boolean,
            ) => {
              const fingering = hit.fretShift
                ? shiftFrets(event.fingering, hit.fretShift)
                : event.fingering;
              const gapRelease = releaseAtGap(event, attackBeat);
              const ringRelease = Number.isFinite(hit.durationBeats)
                ? (() => {
                    // Durations describe score-beat endpoints. Delaying
                    // the attack for swing while retaining the full straight
                    // duration carried chords across rests and changes.
                    const end = hit.beat + hit.durationBeats;
                    const swungEnd =
                      hit.swing !== false
                        ? swungPosition(opts, end, swingOrigin, 'comp', compHits)
                        : end;
                    return Math.max(0.025, (swungEnd - attackBeat) * spb);
                  })()
                : undefined;
              const release =
                gapRelease === undefined
                  ? ringRelease
                  : ringRelease === undefined
                    ? gapRelease
                    : Math.min(ringRelease, gapRelease);
              if (opts.compStyle === 'charleston') {
                // Charleston uses the Held-chord downstroke. Damp all old
                // strings together so upper tails cannot read backwards.
                // Like a held chord it rings to the next strike, except
                // before an N.C. gap, where nothing strikes next.
                this.muteStrings(when);
                this.strumAt({ ...event, fingering }, when, 0.02, 1, gapRelease);
              } else {
                // Per-hand realization: a hit tagged for one hand strikes
                // just that hand's pitches as a PARTIAL strike — the other
                // hand's ringing tones are left alone (only families that
                // split into hands carry pianoHands; everything else, and
                // the guitar, strikes whole). Piano hits also ROLL like a
                // pianist — tight on stabs, a gentle spread on pads — where
                // the guitar comp keeps its simultaneous attack.
                // A lifted hit climbs onto the chord's next inversion up;
                // it strikes whole (hand is 'both' by construction), so
                // strikePianoChord's own hold rule keeps the common LH
                // tones ringing while the upper structure moves.
                const liftMidis =
                  hit.lift && event.pianoLift?.length && event.pianoMidis?.length
                    ? event.pianoLift
                    : undefined;
                const handMidis =
                  !resumed && !liftMidis && hit.hand && hit.hand !== 'both'
                    ? event.pianoHands?.[hit.hand]
                    : undefined;
                const strike = {
                  ...(handMidis?.length && event.pianoMidis?.length
                    ? { ...event, fingering, pianoMidis: handMidis, pianoPartial: true }
                    : { ...event, fingering, ...(liftMidis ? { pianoMidis: liftMidis } : {}) }),
                  // The engine's per-hit touch: stabs tight, pads gentle,
                  // the occasional slow sweep — uncapped at strumAt.
                  ...(!resumed && hit.rollSec !== undefined ? { pianoRollSec: hit.rollSec } : {}),
                };
                const spread = event.pianoMidis?.length
                  ? hit.durationBeats <= 0.6
                    ? 0.005
                    : 0.012
                  : 0;
                this.strumAt(
                  strike,
                  when,
                  spread,
                  hit.velocity * velocityDrift(opts.feel, 'comp', hit.beat),
                  release,
                );
              }
            };
            for (const hit of compHits) {
              const event = hitEvent(hit);
              if (!event) continue;
              if (hit.beat - beat < -0.001 || hit.beat - beat >= 0.999) continue;
              const offset =
                (hit.swing !== false
                  ? swungPosition(opts, hit.beat, swingOrigin, 'comp', compHits)
                  : hit.beat) - beat;
              addAction(offset, 0, (when) => strikeHit(hit, event, when, beat + offset, false));
            }
            if (restrike && !inOverride(beat) && !cueSuppresses('comp', beat)) {
              // Resume re-strikes the hit still ringing at this beat, as the
              // Held path does: the pause damped it and every earlier hit is
              // behind the cursor, so Push or Mostly held stayed silent until
              // the next change.
              let ringing: CompHit | undefined;
              let attackHere = false;
              for (const hit of compHits) {
                if (!hitEvent(hit)) continue;
                if (Math.abs(hit.beat - beat) < 1e-3) attackHere = true;
                else if (hit.beat < beat && (!ringing || hit.beat >= ringing.beat)) ringing = hit;
              }
              const event = ringing && events[ringing.chordIndex];
              if (
                ringing &&
                event &&
                !attackHere &&
                event.startBeat + event.durationBeats > beat + 1e-3 &&
                (!Number.isFinite(ringing.durationBeats) ||
                  ringing.beat + ringing.durationBeats > beat + 1e-3)
              ) {
                const hit = ringing;
                addAction(0, 0, (when) => strikeHit(hit, event, when, beat, true));
              }
            }
          } else {
            for (const event of events) {
              const sounding =
                restrike && event.startBeat < beat && event.startBeat + event.durationBeats > beat;
              const attackBeat = sounding ? beat : event.startBeat;
              if (
                inOverride(attackBeat) ||
                attackBeat < pickup - 1e-3 ||
                cueSuppresses('comp', attackBeat)
              )
                continue;
              addAction(attackBeat - beat, 0, (when) => {
                this.strumAt(event, when, 0.02, 1, releaseAtGap(event, attackBeat));
              });
            }
          }

          // Entering a written bar clamps any generated chord still ringing.
          // These spans are coalesced by the caller, so authored ties across
          // adjacent written bars are not treated as fresh hand-offs.
          for (const span of overrideSpans) {
            addAction(span.startBeat - beat, 1, (when) => this.muteStrings(when));
          }

          // If a written/rest-owned region ends while harmony is still active,
          // re-enter generated comp on that boundary. Held and Push otherwise
          // have no bar-relative hit from which to recover; this also makes the
          // seam deterministic for the other figures.
          const recoveryBeats = new Set([
            ...overrideSpans.map((span) => span.startBeat + span.durationBeats),
            ...bandCues
              .filter((cue) => cue.channel === 'comp' && cue.mode !== 'play')
              .map((cue) => cue.endBeat),
          ]);
          for (const endBeat of recoveryBeats) {
            if (
              endBeat >= totalBeats - 1e-3 ||
              inOverride(endBeat) ||
              cueSuppresses('comp', endBeat)
            )
              continue;
            const exactHit =
              opts.compHits !== undefined
                ? opts.compHits.some(
                    (hit) => Math.abs(hit.beat - endBeat) < 1e-3 && !inOverride(hit.beat),
                  )
                : events.some((event) => Math.abs(event.startBeat - endBeat) < 1e-3);
            if (exactHit) continue;
            let recovery: ArrangementEvent | undefined;
            for (const event of events) {
              if (
                event.startBeat <= endBeat + 1e-3 &&
                event.startBeat + event.durationBeats > endBeat + 1e-3
              ) {
                recovery = event;
              }
            }
            if (!recovery || endBeat < pickup - 1e-3) continue;
            addAction(endBeat - beat, 2, (when) => {
              this.muteStrings(when);
              this.strumAt(recovery!, when, 0.02, 1, releaseAtGap(recovery!, endBeat));
            });
          }

          // Written chord heads are independent voices: unlike pluckLead they
          // remain polyphonic, and unlike generated guitar strings they are not
          // accidentally damped by a later head in the same chord.
          for (const note of opts.authoredCompNotes ?? []) {
            if (note.beat < pickup - 1e-3 || cueSuppresses('comp', note.beat)) continue;
            if (note.beat - beat < -0.001 || note.beat - beat >= 0.999) continue;
            const authored = opts.authoredCompNotes!;
            const sounding = swungPosition(opts, note.beat, swingOrigin, 'comp', authored);
            const offset = sounding - beat;
            const lengthBeats = swungLength(
              opts,
              note.beat,
              note.durationBeats,
              sounding,
              swingOrigin,
              'comp',
              authored,
            );
            const duration = Math.min(8, Math.max(0.18, lengthBeats * spb * 0.98));
            addAction(offset, 3, (when) => {
              this.buildAuthoredCompVoice(note, when, duration);
            });
          }

          for (const cue of bandCues) {
            if (cue.channel !== 'comp' || cue.mode === 'play' || cue.startBeat < pickup - 1e-3)
              continue;
            const attackBeat = cueAttackBeat(cue);
            addAction(attackBeat - beat, 4, (when) => {
              this.cutChannelAt('comp', when);
              if (cue.mode !== 'hit') return;
              const duration = cueLength(cue, attackBeat);
              if (inOverride(cue.startBeat)) {
                for (const note of opts.authoredCompNotes ?? []) {
                  if (
                    note.beat > cue.startBeat + 1e-3 ||
                    note.beat + note.durationBeats <= cue.startBeat + 1e-3
                  )
                    continue;
                  this.buildAuthoredCompVoice(note, when, duration);
                }
              } else {
                const chord = harmonyForCue(cue);
                if (chord) this.strumAt({ ...chord, pianoRollSec: 0 }, when, 0);
              }
              this.cutChannelAt('comp', when + duration);
            });
          }

          actions
            .sort((a, b) => a.offset - b.offset || a.priority - b.priority)
            .forEach((action) =>
              action.run(
                at +
                  action.offset * spb +
                  partShift(
                    placementAt(opts, beat + action.offset),
                    opts.feel,
                    'comp',
                    beat + action.offset,
                  ),
              ),
            );
        }

        // Split the window at written cue boundaries so even a pickup or
        // shortened bar changes the kit on its exact musical beat.
        const drumBoundaries = [beat];
        for (const cue of bandCues) {
          if (cue.channel !== 'drums') continue;
          for (const boundary of [cue.startBeat, cue.endBeat]) {
            if (boundary > beat + 1e-6 && boundary < beat + 0.999) drumBoundaries.push(boundary);
          }
        }
        if (pickup > beat + 1e-6 && pickup < beat + 0.999) drumBoundaries.push(pickup);
        const drumStarts = [...new Set(drumBoundaries)].sort((a, b) => a - b);
        for (const [drumIndex, drumBeat] of drumStarts.entries()) {
          const drumAt = at + (drumBeat - beat) * spb;
          const drumEnd = drumStarts[drumIndex + 1] ?? beat + 1;
          const drumsNow = drumsAtBeat(opts, drumBeat);
          const cue = cueAt('drums', drumBeat);
          if (cue && cue.mode !== 'play') {
            if (this.drum) this.stopDrums(drumAt);
            if (Math.abs(cue.startBeat - drumBeat) < 1e-3) {
              this.cutChannelAt('drums', drumAt);
              if (cue.mode === 'hit' && drumsNow && drumBeat >= pickup - 1e-3) {
                this.drumStabAt(drumAt, cueLength(cue));
              }
            }
            forceDrumReanchor = false;
            continue;
          }
          // By default the kit lays out in harmony gaps. An explicit written
          // entrance overrides that fallback, e.g. before an offbeat chord, and
          // a written bass line playing under N.C. is the band, not a break.
          const harmonyGap =
            cue?.mode !== 'play' &&
            events.length > 0 &&
            drumBeat >= pickup - 1e-3 &&
            ![...events, ...(opts.writtenBassPlays ?? [])].some(
              (span) =>
                drumBeat >= span.startBeat - 1e-3 &&
                drumBeat < span.startBeat + span.durationBeats - 1e-3,
            );
          if (!drumsNow || hold || harmonyGap || drumBeat < pickup - 1e-3) {
            if (this.drum) this.stopDrums(drumAt);
            // A held last chord is caught with one hit on its downbeat, so the
            // kit ends WITH the band instead of simply dropping out (#414).
            const held = opts.holds?.find((w) => Math.abs(w.start - drumBeat) < 1e-3);
            if (drumsNow && held && held.end >= totalBeats - 1e-3 && drumBeat >= pickup - 1e-3) {
              this.drumStabAt(drumAt, (held.end - held.start) * spb * HOLD_STRETCH);
            }
            forceDrumReanchor = false;
          } else if (ritStretch > 1) {
            // THE KIT SLOWS WITH THE BAND (#414): the ritardando re-strikes the
            // loop slice by slice on the stretched grid, cut at the offbeat so
            // the ride's "and" lands on the band's "and" too.
            const offbeat = swungPosition(opts, beat + 0.5, swingOrigin);
            const pieces = [drumBeat, drumEnd];
            if (offbeat > drumBeat + 1e-3 && offbeat < drumEnd - 1e-3) pieces.splice(1, 0, offbeat);
            for (let i = 0; i + 1 < pieces.length; i++) {
              const from = pieces[i];
              const to = pieces[i + 1];
              this.drumSliceAt(
                drumsNow,
                from - pickup,
                at + (from - beat) * spb,
                effectiveBpm,
                timeStretch,
                to - from,
              );
            }
            forceDrumReanchor = true;
          } else if (forceDrumReanchor) {
            this.startDrums(drumsNow, drumBeat - pickup, drumAt, effectiveBpm, timeStretch, 0.02);
            forceDrumReanchor = false;
          } else if (!this.drum) {
            this.startDrums(drumsNow, drumBeat - pickup, drumAt, effectiveBpm, timeStretch);
          } else if (
            this.drum.url !== drumsNow.url ||
            this.drum.bpm !== effectiveBpm ||
            this.drum.timeStretch !== timeStretch
          ) {
            this.startDrums(drumsNow, drumBeat - pickup, drumAt, effectiveBpm, timeStretch, 0.02);
          }
        }

        if (opts.melody && opts.melodyNotes && opts.melodyNotes.length) {
          // Note lengths and expression points already include fermata dwell.
          // Resume must subtract that same sounding time, not merely the
          // written beat count, or it replays part of the held sustain/ramp.
          const elapsedMelodyBeats = (start: number, elapsed: number) =>
            elapsed +
            (opts.holds ?? []).reduce(
              (extra, window) =>
                extra +
                Math.max(0, Math.min(start + elapsed, window.end) - Math.max(start, window.start)) *
                  (HOLD_STRETCH - 1),
              0,
            );
          // THE UPBEAT LEADS BACK IN (user request Aug 2026): on every pass
          // but the last, the anacrusis melody sounds again over the TAIL of
          // the final bar — exactly where a player puts it — so the loop
          // re-enters with its lead-in intact while the bar grid stays whole
          // (later cycles still map to [pickup, cycleSpan), never stretching
          // time). Skipped when between-loop count-ins insert gap bars: the
          // count owns that seam.
          const cyclesTotal = opts.loop
            ? Infinity
            : Math.ceil(passes / Math.max(1, logicalPasses.length));
          // …and only when the loop actually re-enters at the pickup's own
          // seam: with an intro (loopStart past the pickup) the re-entry is
          // the CHORUS top, and the intro's pickup lick does not lead there.
          const upbeatLeadsBackIn =
            pickup > 1e-6 &&
            loopStart <= pickup + 1e-6 &&
            gapBeats === 0 &&
            logicalPass === logicalPasses.length - 1 &&
            cycleIdx + 1 < cyclesTotal;
          const overlayBase = totalBeats - pickup;
          for (const mn of opts.melodyNotes) {
            // Entry material belongs only to the way in. The form-beat
            // mapping already re-enters later cycles at `loopStart`; keep the
            // melody's own boundary explicit too so a future windowing/
            // count-in change cannot make an entry onset reachable after
            // pass 1.
            const inPickup = mn.beat < pickup - 1e-3;
            const inEntry = mn.beat < loopStart - 1e-3;
            if (upbeatLeadsBackIn && inPickup) {
              const overlayOffset = overlayBase + mn.beat - beat;
              if (restrike && overlayOffset < -0.001) {
                this.playMelodyAt(
                  mn,
                  at,
                  spb,
                  opts.melodyInstrument,
                  elapsedMelodyBeats(mn.beat, -overlayOffset),
                );
              }
              if (overlayOffset >= -0.001 && overlayOffset < 0.999) {
                // Swung where it is played, judged against the line it is
                // written in (the pickup bar).
                const melody = opts.melodyNotes;
                const sounding = swungPosition(
                  opts,
                  overlayBase + mn.beat,
                  swingOrigin,
                  'melody',
                  melody,
                  overlayBase,
                );
                const length = swungLength(
                  opts,
                  overlayBase + mn.beat,
                  mn.durationBeats,
                  sounding,
                  swingOrigin,
                  'melody',
                  melody,
                  overlayBase,
                );
                this.playMelodyAt(
                  mn,
                  at + (sounding - beat) * spb,
                  spb,
                  opts.melodyInstrument,
                  0,
                  length,
                  melodyOnsetShift(
                    opts,
                    overlayBase + mn.beat,
                    swingOrigin,
                    spb,
                    length,
                    melody,
                    overlayBase,
                  ),
                );
              }
            }
            if (cycleIdx > 0 && inEntry) continue;
            const offset = mn.beat - beat;
            if (restrike && offset < -0.001) {
              this.playMelodyAt(
                mn,
                at,
                spb,
                opts.melodyInstrument,
                elapsedMelodyBeats(mn.beat, -offset),
              );
            }
            if (offset < -0.001 || offset >= 0.999) continue;
            // The melody swings WITH the band, by the same feel as the bass
            // line, judged against its own line (swingFeel.ts).
            const melody = opts.melodyNotes;
            const sounding = swungPosition(opts, mn.beat, swingOrigin, 'melody', melody);
            const length = swungLength(
              opts,
              mn.beat,
              mn.durationBeats,
              sounding,
              swingOrigin,
              'melody',
              melody,
            );
            this.playMelodyAt(
              mn,
              at + (sounding - beat) * spb,
              spb,
              opts.melodyInstrument,
              0,
              length,
              melodyOnsetShift(opts, mn.beat, swingOrigin, spb, length, melody),
            );
          }
        }

        if (opts.bassline) {
          const activeBass =
            opts.bassVariants && opts.bassVariants.length
              ? opts.bassVariants[(globalPass - 1) % opts.bassVariants.length]
              : opts.bassNotes;
          const actions: { offset: number; run: (when: number) => void }[] = [];
          const addBass = (offset: number, run: (when: number) => void) => {
            if (offset >= -0.001 && offset < 0.999) actions.push({ offset, run });
          };
          const writtenBassSpans = opts.authoredBassSpans ?? [];
          const writtenBassAt = (target: number) => writtenBassSpans.some(
            (span) => target >= span.startBeat - 1e-3 &&
              target < span.startBeat + span.durationBeats - 1e-3,
          );
          // Ownership includes written rests. Stop a generated voice's release
          // tail at the entry's audio-clock beat, before any same-beat attack.
          // Adjacent/overlapping spans stay continuous, preserving written ties.
          const writtenEntries = new Set(writtenBassSpans.filter((span) =>
            !writtenBassSpans.some((previous) =>
              previous.startBeat < span.startBeat - 1e-3 &&
              previous.startBeat + previous.durationBeats >= span.startBeat - 1e-3,
            ),
          ).map((span) => span.startBeat));
          for (const start of writtenEntries) {
            if (start >= pickup - 1e-3)
              addBass(start - beat, (when) => this.cutChannelAt('bass', when));
          }
          if (activeBass !== undefined) {
            for (const bn of activeBass) {
              if (bn.beat - beat < -0.001 || bn.beat - beat >= 0.999) continue;
              if (bn.beat < pickup - 1e-3 || cueSuppresses('bass', bn.beat)) continue;
              const straight = bn.swing === false;
              const sounding = straight
                ? bn.beat
                : swungPosition(opts, bn.beat, swingOrigin, 'bass', activeBass);
              const offset = sounding - beat;
              const articulated =
                bn.articulationGate !== undefined && Number.isFinite(bn.articulationGate);
              const lengthBeats = straight
                ? bn.durationBeats
                : swungLength(
                    opts,
                    bn.beat,
                    bn.durationBeats,
                    sounding,
                    swingOrigin,
                    'bass',
                    activeBass,
                  );
              let dur = articulated
                ? Math.max(0.01, lengthBeats * spb * Math.max(0, bn.articulationGate!))
                : bn.durationBeats >= 1
                  ? Math.min(5, Math.max(1.2, lengthBeats * spb + 0.5))
                  : Math.max(0.18, lengthBeats * spb);
              // Generated ring-out stops before N.C.; a written duration
              // crossing it remains intact unless an explicit cue cuts it.
              const chord = events.find(
                (e) =>
                  bn.beat >= e.startBeat - 1e-3 && bn.beat < e.startBeat + e.durationBeats - 1e-3,
              );
              const gapCut = chord ? releaseAtGap(chord, bn.beat) : undefined;
              if (
                chord &&
                gapCut !== undefined &&
                bn.beat + bn.durationBeats <= chord.startBeat + chord.durationBeats + 1e-3
              ) {
                dur = Math.min(dur, gapCut);
              }
              addBass(offset, (when) => {
                this.pluckBass(
                  bn.midi,
                  when,
                  dur,
                  (bn.velocity ?? 1) * velocityDrift(opts.feel, 'bass', bn.beat),
                  'bass',
                );
                if (articulated) this.cutChannelAt('bass', when + dur);
              });
            }
          } else {
            for (const event of events) {
              if (
                event.bassMidi == null ||
                event.startBeat < pickup - 1e-3 ||
                writtenBassAt(event.startBeat) ||
                cueSuppresses('bass', event.startBeat)
              )
                continue;
              const gapCut = releaseAtGap(event, event.startBeat);
              const duration = Math.min(
                gapCut ?? Infinity,
                Math.min(5, Math.max(1.4, event.durationBeats * spb + 0.6)),
              );
              addBass(event.startBeat - beat, (when) => {
                this.pluckBass(event.bassMidi!, when, duration, 1, 'bass');
              });
            }
          }
          for (const cue of bandCues) {
            if (cue.channel !== 'bass' || cue.mode === 'play' || cue.startBeat < pickup - 1e-3)
              continue;
            const attackBeat = cueAttackBeat(cue);
            addBass(attackBeat - beat, (when) => {
              this.cutChannelAt('bass', when);
              if (cue.mode !== 'hit') return;
              const written = writtenBassAt(cue.startBeat);
              const note = activeBass?.find(
                (note) =>
                  note.beat <= cue.startBeat + 1e-3 &&
                  note.beat + note.durationBeats > cue.startBeat + 1e-3,
              );
              const midi = written ? note?.midi : (harmonyForCue(cue)?.bassMidi ?? note?.midi);
              if (midi === undefined) return;
              const duration = cueLength(cue, attackBeat);
              this.pluckBass(midi, when, duration, 1, 'bass');
              this.cutChannelAt('bass', when + duration);
            });
          }
          actions
            .sort((a, b) => a.offset - b.offset)
            .forEach((action) =>
              action.run(
                at +
                  action.offset * spb +
                  partShift(
                    placementAt(opts, beat + action.offset),
                    opts.feel,
                    'bass',
                    beat + action.offset,
                  ),
              ),
            );
        }

        const next = abs + 1;
        // The last pass carries no trailing count-in, hence the - gapBeats.
        return {
          next: next < spanForPasses(passes) - gapBeats ? next : null,
          spb: hold ? spb * HOLD_STRETCH : spb,
        };
      };

      const queuePump = (pump: () => void) => {
        this.activePump = pump;
        this.seqTimer = setTimeout(pump, AUDIO_SCHEDULER_TICK_MS);
      };

      const pump = () => {
        if (token !== this.seqToken || !this.liveOptions || !this.arrControl) return;
        this.seqTimer = null;
        const now = ctx.currentTime;
        const wallNow = performance.now();
        if (now > lastAudioClock + 0.001) {
          lastAudioClock = now;
          lastAudioClockProgress = wallNow;
        } else if ((ctx.state as string) === 'running' && wallNow - lastAudioClockProgress > 1000) {
          // Some WebKit route failures leave state="running" while the render
          // clock is frozen. This scheduler is permanently bound to that dead
          // context, so end it honestly instead of exposing a Resume button
          // that cannot preserve position. The next Play rebuilds and starts in
          // one user gesture.
          this.contextClockStalled = true;
          this.autoPausedForInterruption = false;
          this.stopAll();
          return;
        }
        flushBeatCues(now);

        if (schedulingDone) {
          if (finishAt !== null && now >= finishAt) finish();
          else queuePump(pump);
          return;
        }

        // If the producer missed its entire safety window (route change, GC,
        // throttled tab), preserve the next musical beat and give it a fresh
        // lead-in. Never emit an overdue backlog at `currentTime`.
        if (nextAudioTime < now + AUDIO_MIN_LEAD_SECONDS) {
          nextAudioTime = now + AUDIO_START_LEAD_SECONDS;
          forceDrumReanchor = true;
        }

        const horizon = now + this.lookaheadSeconds();
        while (!schedulingDone && nextAudioTime <= horizon) {
          const at = nextAudioTime;
          // The count into a start bar is over: the next beat is that bar.
          if (startLanding !== null && nextAbs >= countOrigin - 1e-6) {
            nextAbs = startLanding;
            startLanding = null;
            this.resumeRestrike = true;
          }
          if (pendingForm && formReady(nextAbs)) landForm();
          if (pendingJump !== null && isBarStart(nextAbs)) landJump(pendingJump, at);
          if (pendingSeek !== null) landSeek(at);
          const abs = nextAbs;
          const scheduled = scheduleBeat(abs, at);
          scheduledBeats.push({ abs, at });
          if (scheduled.next !== null) {
            nextAbs = scheduled.next;
            nextAudioTime = at + scheduled.spb;
          } else if (pendingJump !== null) {
            // The form's end is its last barline. A jump asked for in the
            // final bar lands there; ending the tune with it still pending
            // dropped the restart and let a set advance to the next tune.
            nextAudioTime = at + scheduled.spb;
            landJump(pendingJump, nextAudioTime);
          } else if (pendingSeek !== null) {
            // A bar tapped on the final beat: the tune goes on from there.
            nextAudioTime = at + scheduled.spb;
            landSeek(nextAudioTime);
          } else {
            nextAbs = abs + 1;
            schedulingDone = true;
            finishAt = at + scheduled.spb;
          }
          this.arrControl.nextAbs = nextAbs;
        }
        queuePump(pump);
      };

      this.arrControl = {
        schedule: (abs) => {
          // Paused after the final onset: there is no next musical beat to
          // replay. Let the pump complete the transport instead of wrapping
          // `endExclusive` into a phantom extra pass.
          if (schedulingDone && finishAt !== null && abs === nextAbs) {
            pump();
            return;
          }
          nextAbs = abs;
          this.arrControl!.nextAbs = abs;
          schedulingDone = false;
          finishAt = null;
          pump();
        },
        resetClock: () => {
          nextAudioTime = ctx.currentTime + AUDIO_START_LEAD_SECONDS;
          lastAudioClock = ctx.currentTime;
          lastAudioClockProgress = performance.now();
          forceDrumReanchor = true;
        },
        freeze: () => {
          const now = ctx.currentTime;
          flushBeatCues(now);
          const firstFuture = scheduledBeats.find((scheduled) => scheduled.at > now + 0.001);
          if (firstFuture) {
            nextAbs = firstFuture.abs;
            schedulingDone = false;
            finishAt = null;
          } else if (schedulingDone) {
            // The finite form's final beat has already sounded. Preserve that
            // terminal marker so Resume finishes rather than inventing a pass.
            finishAt = now;
          }
          beatCues = [];
          scheduledBeats = [];
          this.arrControl!.nextAbs = nextAbs;
        },
        nextAbs,
        position: () => {
          // The scheduling cursor, not the audible beat: they differ by the
          // lookahead only, and a jump is decided against what is scheduled.
          const abs = Math.max(pickup, nextAbs);
          const { beat, globalPass } = locate(abs);
          // A between-loops count-in leads into the NEXT chorus: that is the
          // pass the scheduler announces for the gap (and the chip shows), so
          // forward goes past it rather than to it.
          const pass = beat === null ? globalPass + 1 : globalPass;
          const start = passStartBeat(pass);
          const bars = barStartSet ?? [];
          const nextBar = bars.find((b) => b > start + 1e-3);
          const firstBar =
            nextBar !== undefined ? nextBar - start : (this.liveOptions?.beatsPerBar ?? 4);
          return {
            pass,
            total: totalPasses(),
            intoPass: beat === null ? 0 : Math.max(0, beat - start),
            firstBar,
            pending: pendingJump,
          };
        },
        reform: (next, patch) => {
          // The final beat is already scheduled: the tune ends as it was
          // going to, and the new form is the next Play's.
          if (schedulingDone) return true;
          pendingForm = {
            events: next ?? pendingForm?.events ?? this.liveEvents ?? [],
            patch: { ...pendingForm?.patch, ...patch },
          };
          // Paused, the cursor is not moving: land now, so Resume plays the new form.
          if (this.transport === 'paused' && formReady(nextAbs)) landForm();
          return true;
        },
        hasPendingForm: () => pendingForm !== null,
        seek: (formBeat) => {
          // The chorus being played: a between-loops count-in belongs to the
          // chorus it counts in, as for the scheduler's own playingCycle.
          let cycle = 0;
          if (nextAbs >= pickup - 1e-6) {
            const at = locate(nextAbs);
            cycle =
              at.cycleIdx +
              (at.beat === null && at.logicalPass === logicalPasses.length - 1 ? 1 : 0);
          }
          const target = formBeatAbs(formBeat, cycle);
          if (target === null) return false;
          setPendingJump(null);
          if (this.transport === 'paused' || (schedulingDone && finishAt !== null)) {
            // Nothing is being scheduled: move the cursor now, as a chorus
            // jump does. A paused band resumes from here.
            const wasDone = schedulingDone && this.transport !== 'paused';
            pendingSeek = null;
            startLanding = null;
            nextAbs = target;
            this.arrControl!.nextAbs = target;
            const { beat, logicalPass, globalPass } = locate(target);
            this.emitPass(globalPass);
            if (beat !== null) this.emitBeat(beat - logicalPasses[logicalPass].start);
            if (wasDone) {
              nextAudioTime = Math.max(finishAt!, ctx.currentTime + AUDIO_START_LEAD_SECONDS);
              forceDrumReanchor = true;
              this.resumeRestrike = true;
            }
            schedulingDone = false;
            finishAt = null;
            return true;
          }
          // Still counting in: the count goes on and lands on the bar.
          if (nextAbs < countOrigin - 1e-6) {
            startLanding = target;
            return true;
          }
          pendingSeek = target;
          return true;
        },
        jump: (pass) => {
          pendingSeek = null;
          if (pass === null) {
            setPendingJump(null);
            return;
          }
          const target = Math.max(1, Math.min(totalPasses(), Math.round(pass)));
          if (this.transport === 'paused' || (schedulingDone && finishAt !== null)) {
            // Nothing is being scheduled: move the cursor now. A paused
            // transport resumes from here; a finished last beat carries on.
            const wasDone = schedulingDone && this.transport !== 'paused';
            nextAbs = passStartAbs(target);
            this.arrControl!.nextAbs = nextAbs;
            setPendingJump(null);
            const { beat, logicalPass } = locate(nextAbs);
            this.emitPass(target);
            if (beat !== null) this.emitBeat(beat - logicalPasses[logicalPass].start);
            if (wasDone) {
              nextAudioTime = Math.max(finishAt!, ctx.currentTime + AUDIO_START_LEAD_SECONDS);
              forceDrumReanchor = true;
              this.resumeRestrike = true;
            }
            // An explicit jump means "play from here", so the terminal marker
            // goes whether the transport is finishing or paused: paused on
            // the final beat, it made Resume end the tune instead.
            schedulingDone = false;
            finishAt = null;
            return;
          }
          setPendingJump(target);
        },
      };
      this.arrControl.schedule(initialAbs);
    } catch {
      /* no audio available */
    }
  }

  /** Play a bare list of MIDI notes (stringless). `timbre` picks the voice:
   * the guitar pluck (default) or the struck piano. */
  async playMidi(
    notes: number[],
    strum = 0.0,
    timbre: 'guitar' | 'piano' = 'guitar',
  ): Promise<void> {
    try {
      this.yieldToOneShot();
      const requestToken = this.seqToken;
      if (!(await this.resume()) || requestToken !== this.seqToken) return;
      if (timbre === 'piano') void this.preloadPianoSamples();
      else await this.preloadGuitarSamples();
      if (requestToken !== this.seqToken) return;
      const start = this.ensureContext().currentTime + 0.02;
      // Over a live transport a piano tap takes voices of its own, as a
      // tapped grip does: pianoVoices is the comp's, and striking through it
      // damped the comp's ringing note of the same pitch and handed the tap
      // to the comp, which cut it at the band's next chord change.
      const live = this.transportLive();
      if (timbre === 'piano' && !live) {
        // A tapped chord is a hand on the keys: it rings until the next
        // chord lifts it, as the comp's do. A single tapped key lifts nothing
        // — placing a voicing note by note keeps the earlier notes sounding.
        this.strikePianoChord(notes, start, strum, 1, undefined, notes.length === 1, 'direct');
        return;
      }
      notes.forEach((midi, i) => {
        const at = start + i * strum;
        if (timbre !== 'piano') this.buildVoice(midi, at, DEFAULT_SUSTAIN, -1);
        else {
          const sample = this.nearestPianoSample(midi);
          if (sample)
            this.buildOneShotVoice(midi, at, DEFAULT_SUSTAIN, 1, sample, {
              level: PIANO_SAMPLE_LEVEL,
            });
          else this.buildPianoVoice(midi, at, DEFAULT_SUSTAIN);
        }
      });
    } catch {
      /* no audio available */
    }
  }

  /** Release the audio context and any pending teardown timers. */
  dispose(): void {
    this.cancelSequence();
    if (this.mediaNavigation) this.setMediaNavigation(null);
    const session = this.mediaSession();
    if (session && this.mediaSessionBound) {
      for (const action of ['play', 'pause', 'stop'] as MediaSessionAction[]) {
        try {
          session.setActionHandler(action, null);
        } catch {
          /* unsupported action */
        }
      }
      this.mediaSessionBound = false;
    }
    this.stopDrums();
    this.drumBuffers.clear();
    this.drumLoads.clear();
    this.drumStretchEpoch++;
    this.drumRenderer?.dispose();
    this.drumRenderer = null;
    this.wantedDrumStretches.clear();
    this.stretchedDrums.clear();
    this.drumStretches.clear();
    this.failedDrumStretches.clear();
    this.drumStretchBytes = 0;
    this.drumGain = null;
    this.limiter = null;
    this.output = null;
    this.buses = {};
    this.clickBus = null;
    this.dryBus = null;
    this.masterCompressor = null;
    this.masterMakeup = null;
    this.appliedCompression = 0;
    this.compressionActive = false;
    this.reverbSend = null;
    this.reverbNode = null;
    this.reverbSource = null;
    this.sinkNode = null;
    this.sinkRouted = false;
    this.sinkElement = null;
    this.wantSinkRoute = false;
    if (this.sessionKeeper) {
      this.sessionKeeper.pause();
      this.sessionKeeper.srcObject = null;
      this.sessionKeeper.src = '';
      this.sessionKeeper = null;
    }
    this.voices.forEach((v) => clearTimeout(v.teardown));
    this.voices.clear();
    this.stringVoices = [null, null, null, null, null, null, null];
    this.leadVoices.clear();
    this.pianoVoices.clear();
    this.pianoSounding.clear();
    this.lastChordSig = null;
    this.autoPausedForInterruption = false;
    this.contextClockStalled = false;
    if (this.ctx && this.contextStateListener) {
      this.ctx.removeEventListener('statechange', this.contextStateListener);
    }
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    this.contextStateListener = null;
    this.visibilityListener = null;
    try {
      void this.ctx?.close();
    } catch {
      /* ignore */
    }
    this.ctx = null;
    this.master = null;
  }
}

let shared: ChordPlayer | null = null;
export function getChordPlayer(): ChordPlayer {
  if (!shared) shared = new ChordPlayer();
  return shared;
}
